/* this works 
Description: This is the file that will handle interacting with the Fusion EMR (app.fusionwebclinic.com)
Date: 4/4/2024
Author: Jacob Michalski

Notes:
    All Injectors need the following functions:
        getPageInfo
        getPatientInfo
        getQuestions
        answerQuestions (Consider combining these and making the single a private function)
        answerQuestion
*/

export function getPageInfo() {
  // function getPageInfo() {
  /*
      Description: This function will get the page info from the note
      Return: A dictionary with the page info
          {
              "URL": Base URL for the page,
              "Note Type": "PT Daily/SOAP Note",...
          }
      */
  const baseURL = window.location.origin;
  let noteType = null;
  let noteElements = document.querySelectorAll('.page-title');
  if (noteElements.length === 1) {
    noteType = noteElements[0].textContent;
  } else if (noteElements.length > 1) {
    // if noteElements[0].textContent includes the year "2024" -> "202" use the second element
    if (noteElements[0].textContent.includes('202')) {
      // if the second element includes "Document" check document.querySelectorAll(".page-title")[0].textContent
      if (noteElements[1].textContent.includes('Document')) {
        let paneTitleElement = document.querySelectorAll('.pane-title');
        if (paneTitleElement.length > 0) {
          // split on "for" and take the first element
          noteType = paneTitleElement[0].textContent.split('for')[0].trim();
        }
      } else {
        noteType = noteElements[1].textContent;
      }
    } else {
      noteType = noteElements[0].textContent;
    }
  }

  // If 2024 in the noteType, then we are in the wrong place - Just call it a "Fusion Note"
  if (noteType == null || noteType.includes('202')) {
    noteType = 'Fusion Note';
  }

  return {
    URL: baseURL,
    'Note Type': noteType
  };
}

export function getPatientInfo() {
  // function getPatientInfo() {
  /*
      Description: This function will get the patient info from the note
      Return: A dictionary with the patient info
      {
          "EMR ID": 0x1234 or "michalskij"
          "First Name":firstName,
          "Last Name": lastName,
          "DOB": dob,
      }
      */
  // Assuming the element's text is "Halper John, he/him (EMR ID: 42332797)"
  let patientRaw = document.querySelector('.pane-title').textContent; // Gets us "Patient: First Last"

  const regexes = [
    /^Patient: ([A-Za-z]+) ([A-Za-z]+),*.*$/,
    /^(?:Patient: |.* for )([A-Za-z]+) ([A-Za-z]+).*$/
  ];
  for (let i = 0; i < regexes.length; i++) {
    const match = patientRaw.match(regexes[i]);
    if (match) {
      const lastName = match[2];
      const firstName = match[1];
      const emrID = `${lastName},${firstName}`.replace(/\s/g, '');
      const dob = '05/29/2023';
      return {
        'EMR ID': emrID,
        'First Name': firstName,
        'Last Name': lastName,
        DOB: dob
      };
    }
  }
  console.error('No match found');
  return null;
}

/* 
questions - This is where we will store the questions
          - a list of dictionaries
     [{
        "Question": "Where was the injury?", 
        "Type": "Multiple Choice", 
        "Choices": ["Back", "Neck", "Shoulder"]
     },
        ...
        ]
 
*/
var questions = [];
var questionInteractions = {};

/*
question Interactions - This is where we will store the interactions for each question
                        - a dictionary of dictionaries
         {
            "Where was the injury?": {
                "Type": "Multiple Choice",
                "Choices"{
                    "Back": () => {click the back button},
                    "Neck": () => {click the neck button},
                    "Shoulder": () => {click the shoulder button}
                }
            },
        },
        {
            "What is the injury?": {
                "Type": "Short Answer",
                "Input": (val) => {input the value into the input box}
        }
            ...
         }
*/

export async function getQuestions() {
  // function getQuestions() {
  questions = [];
  questionInteractions = {};

  // Start with the flow body - Active Tab
  let newBody = document.querySelector('.flow-body');
  if (!newBody) {
    return questions;
  }

  // If any table/section is already open in edit mode, Fusion can keep multiple sections mounted
  // and the scraper/UX will "pull over" across sections. Before scraping questions, force-save
  // any visible open editors so we only operate on the high-level tab content.
  const closeAnyOpenEditors = async () => {
    const isVisible = (el: Element | null | undefined) =>
      !!el && (el as any).offsetParent !== null;

    const getVisibleSaveButtons = (): HTMLButtonElement[] => {
      const btns = Array.from(
        document.querySelectorAll(
          '.flow-section-aside button[data-fire="~save"], ' +
            '.flow-section button[data-fire="~save"], button.variant-solid[data-fire="~save"]'
        )
      ) as HTMLButtonElement[];
      return btns.filter((b) => isVisible(b));
    };

    for (let pass = 0; pass < 5; pass++) {
      const saveButtons = getVisibleSaveButtons();
      if (saveButtons.length === 0) break;

      for (const btn of saveButtons) {
        try {
          btn.click();
        } catch {}
        await sleep(200);
      }
      await sleep(150);
    }
  };

  await closeAnyOpenEditors();

  // Process all flow sections as Flex questions
  handleFlowSectionsAsFlex(newBody);
  await handleObjectivesGoalsSection();
  // Fill the questions array with the questions
  Object.keys(questionInteractions).forEach((key) => {
    if (
      key == 'Appointment' ||
      key == 'Diagnoses' ||
      key == 'Contributors' ||
      key == 'Charges'
    ) {
      return;
    }
    questions.push(questionInteractions[key]);
  });

  // Sort questions based on the visible tab order in the flow-control header.
  // Read the tab order dynamically from the DOM so we don't hard-code titles.
  const flowTabs = Array.from(
    document.querySelectorAll('.flow-control .flow-tab')
  ) as HTMLElement[];

  const tabIndexByTitle = new Map<string, number>();
  flowTabs.forEach((tab, index) => {
    const title = tab.textContent?.trim();
    if (title) {
      tabIndexByTitle.set(title, index);
    }
  });

  const getTabIndex = (q: any): number => {
    const title = String(q?.Key || q?.Question || '').trim();
    if (!title) return Number.MAX_SAFE_INTEGER;
    const idx = tabIndexByTitle.get(title);
    return typeof idx === 'number' ? idx : Number.MAX_SAFE_INTEGER;
  };

  questions.sort((a, b) => {
    const ia = getTabIndex(a);
    const ib = getTabIndex(b);
    if (ia !== ib) return ia - ib;

    // Same or unknown tab index: preserve DOM order when possible
    const elA = a.OriginalElement as HTMLElement | undefined;
    const elB = b.OriginalElement as HTMLElement | undefined;
    if (elA && elB && elA !== elB) {
      const pos = elA.compareDocumentPosition(elB);
      if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
      if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1;
    }
    return 0;
  });

  return questions.map((q) => {
    if (!q || typeof q !== 'object') return q;
    const { OriginalElement, ...question } = q;
    return question;
  });
}

function handleFlowSectionsAsFlex(flowBody) {
  // Fusion sometimes keeps multiple steps mounted when a table/section is expanded.
  // Prefer scraping only the currently active step when Fusion marks it.
  const flowStepsAll = Array.from(
    flowBody.querySelectorAll('.flow-step')
  ) as HTMLElement[];
  const activeSteps = flowStepsAll.filter((step) => {
    const ariaHidden = step.getAttribute('aria-hidden');
    return (
      step.classList.contains('active') ||
      step.classList.contains('selected') ||
      step.classList.contains('current') ||
      ariaHidden === 'false'
    );
  });
  const flowSteps: HTMLElement[] =
    activeSteps.length > 0 ? activeSteps : flowStepsAll;
  const structure = {};

  flowSteps.forEach((flowStep) => {
    try {
      // Skip if step is not visible
      if (flowStep.classList.contains('hidden') || !flowStep.offsetParent) {
        return;
      }

      // Get step title
      const stepTitleElement = flowStep.querySelector(
        '.flow-step-title, .flow-step-header .flow-step-title'
      );
      if (!stepTitleElement) {
        return;
      }

      const stepTitle = stepTitleElement.textContent.trim();
      if (stepTitle == 'Findings') {
        handleFindingsSection(flowBody);
        return;
      }
      if (stepTitle == 'Functional Measures') {
        handleFunctionalMeasuresSection(flowBody);
        return;
      }

      if (!stepTitle) {
        return;
      }

      // Initialize step in structure
      if (!structure[stepTitle]) {
        structure[stepTitle] = {};
      }

      // Process all flow-sections within this flow-step
      const flowSections = flowStep.querySelectorAll('.flow-section');
      flowSections.forEach((flowSection) => {
        // Skip if section is hidden
        const sectionEl = flowSection as HTMLElement;
        if (sectionEl.classList.contains('hidden') || !sectionEl.offsetParent) {
          return;
        }

        // Get section title
        const sectionTitleElement = flowSection.querySelector(
          '.flow-section-title'
        );
        const sectionTitle =
          sectionTitleElement?.textContent?.trim() || stepTitle;
        if (sectionTitle === 'Goal / Objective Data (for this session)') {
          return;
        }
        // Check if section has dictionaries (which should be added at parent level with combined keys)
        const sectionContent = flowSection.querySelector(
          '.flow-section-content'
        );
        const hasDictionaries =
          sectionContent?.querySelectorAll('.dictionary').length > 0;

        if (hasDictionaries) {
          // For dictionary sections, add items directly to parent level with combined keys
          // Ensure section title key doesn't exist (remove it if it does)
          if (structure[stepTitle][sectionTitle]) {
            delete structure[stepTitle][sectionTitle];
          }
          // Use parent structure directly for dictionary items
          processSectionInputs(flowSection, structure[stepTitle], sectionTitle);
        } else {
          // Initialize section subsection in structure
          if (!structure[stepTitle][sectionTitle]) {
            structure[stepTitle][sectionTitle] = {};
          }

          // Process different input types in the section
          processSectionInputs(
            flowSection,
            structure[stepTitle][sectionTitle],
            sectionTitle
          );
        }
      });
    } catch (e) {
      console.error('Error processing flow step:', e);
    }
  });

  // Convert structure to Flex questions - one per flow-step
  Object.keys(structure).forEach((stepTitle) => {
    const stepStructure = structure[stepTitle];
    if (Object.keys(stepStructure).length > 0) {
      questionInteractions[stepTitle] = {
        Type: 'Flex',
        Key: stepTitle,
        Question: stepTitle,
        Structure: { ...stepStructure, prune_early: true },
        OriginalElement: flowBody
      };
    }
  });
}

function processSectionInputs(section, sectionStructure, sectionTitle) {
  // Check if section is in editing mode
  const isEditing = section.classList.contains('editing');
  const sectionContent = section.querySelector('.flow-section-content');
  const isInlineEdit = sectionContent?.classList.contains('inline-edit');
  const editButton = (section.querySelector(
    'button[data-fire="~edit"], button.edit-button[data-fire="~edit"]'
  ) ||
    section
      .closest('.flow-step')
      ?.querySelector(
        'button[data-fire="~edit"], button.edit-button[data-fire="~edit"]'
      )) as HTMLElement | null;

  // Re-find live section by title (Fusion re-renders on Edit, so closure ref goes stale)
  const normTitle = (s: string) =>
    (s || '').replace(/\s+/g, ' ').trim().toLowerCase();
  const findLiveSection = (): HTMLElement | null => {
    const sections = Array.from(
      document.querySelectorAll('.flow-section')
    ) as HTMLElement[];
    const targetNorm = normTitle(sectionTitle || '');
    return (
      sections.find((sec) => {
        const t = sec.querySelector('.flow-section-title')?.textContent || '';
        return sec.offsetParent && normTitle(t) === targetNorm;
      }) || null
    );
  };

  // Helper function to ensure section is in edit mode
  const ensureEditMode = async () => {
    const isHI = String(sectionTitle || '')
      .toLowerCase()
      .includes('history items');

    // For History Items: Fusion replaces the section DOM on Edit; use live refs
    let targetSection = section;
    let targetEditBtn = editButton;
    if (isHI) {
      const live = findLiveSection();
      if (live) {
        targetSection = live;
        targetEditBtn = live.querySelector(
          'button[data-fire="~edit"], button.edit-button[data-fire="~edit"]'
        ) as HTMLElement | null;
      }
    }

    const currentlyEditing = targetSection.classList.contains('editing');
    if (!currentlyEditing && targetEditBtn) {
      targetEditBtn.click();
      // Wait for edit form - poll LIVE DOM each time (section may have been replaced)
      let attempts = 0;
      while (attempts < 25) {
        await sleep(100);
        const live = isHI ? findLiveSection() : targetSection;
        if (!live) {
          attempts++;
          continue;
        }
        const saveBtn = live.querySelector(
          'button[data-fire="~save"]'
        ) as HTMLElement | null;
        const hasInputs = live.querySelector(
          'input:not(.hidden), textarea:not(.hidden), .dropdown.editable, form'
        );
        if (
          (saveBtn && saveBtn.offsetParent) ||
          hasInputs ||
          live.classList.contains('editing')
        ) {
          break;
        }
        attempts++;
      }
    }
  };

  const fillFromActiveElementFallback = (
    val: any,
    replyType: string | undefined,
    scopeEl?: HTMLElement | ParentNode | null
  ) => {
    const ae = document.activeElement as HTMLElement | null;
    if (!ae) return false;

    // If a scope is provided (row/cell), only fill when the active element is inside that scope.
    if (scopeEl) {
      const scopeNode = scopeEl as any;
      const contains =
        typeof scopeNode.contains === 'function'
          ? scopeNode.contains(ae)
          : false;
      if (!contains) return false;
    } else {
      // Fallback: only act if focus is still inside this section
      if (!section.contains(ae)) return false;
    }

    // TEXTAREA
    if (ae.tagName === 'TEXTAREA') {
      const ta = ae as HTMLTextAreaElement;
      if (ta.hasAttribute('readonly')) ta.removeAttribute('readonly');
      fillTextareaValue(ta, val, replyType);
      return true;
    }

    // TEXT INPUT
    if (ae.tagName === 'INPUT' && (ae as HTMLInputElement).type === 'text') {
      const ti = ae as HTMLInputElement;
      if (ti.hasAttribute('readonly')) ti.removeAttribute('readonly');
      fillTextInputValue(ti, val, replyType);
      return true;
    }

    // CONTENTEDITABLE
    if (
      (ae as any).isContentEditable ||
      ae.getAttribute('contenteditable') === 'true'
    ) {
      fillContentEditableValue(ae, val, replyType);
      return true;
    }

    return false;
  };

  // --- ADDED HELPERS for robust table row/cell filling ---
  const removeReadonlyTemporarily = <T extends HTMLElement>(
    el: T,
    fn: () => void
  ) => {
    const hadReadonly =
      el.hasAttribute('readonly') && el.getAttribute('readonly') !== 'false';
    if (hadReadonly) el.removeAttribute('readonly');
    try {
      fn();
    } finally {
      if (hadReadonly) el.setAttribute('readonly', 'true');
    }
  };

  const findTableRowByName = (tableEl: Element, targetName: string) => {
    const allRows = Array.from(
      tableEl.querySelectorAll('.table-row')
    ) as HTMLElement[];
    const found = allRows.find((r) => {
      const nameCell = r.querySelector(
        "[data-header='Name'], .table-cell.variant-primary, [data-header='Measure'], [data-header='Behavior'], [data-header='Activity'], [data-header='Title'], [data-header='Position'], [data-header='Movement'], [data-header='Transfer'], [data-header='Type of Activity'], [data-header='Type of Ambulation'], [data-header=' Work Performance']"
      ) as HTMLElement | null;
      const text = nameCell?.textContent?.trim();
      return !!text && text === targetName;
    });
    return found;
  };

  const findTableContainingRowName = (targetRowName: string) => {
    const container = String(sectionTitle || '')
      .toLowerCase()
      .includes('history items')
      ? findLiveSection()
      : section;
    if (!container) return undefined;
    const candidates = Array.from(
      container.querySelectorAll(
        '.table-root, .table.bulk-body, .table-form-component .table-root, .table-form-component .table.bulk-body'
      )
    ) as Element[];
    return candidates.find((tbl) => !!findTableRowByName(tbl, targetRowName));
  };

  const fillContentEditableValue = (
    el: HTMLElement | null,
    val: any,
    replyType?: string
  ) => {
    if (!el) return;
    if (isEmptyOrNull(val)) return;
    const strVal = String(val);
    const existing = (el.textContent || '').trim();
    const next =
      replyType === 'append' && existing ? `${existing}\n${strVal}` : strVal;

    el.focus();
    // Prefer execCommand for some rich editors
    try {
      document.execCommand('selectAll', false);
      document.execCommand('insertText', false, next);
    } catch (e) {
      // Fallback
      el.textContent = next;
    }

    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
  };

  // Check if section has edit button but no visible inputs yet
  // This happens with inline-edit sections that load content dynamically
  const hasVisibleInputs = section.querySelector(
    'input:not(.hidden):not([readonly]), textarea:not(.hidden):not([readonly]), .dropdown.editable, form input, form textarea'
  );
  const needsEditClick = editButton && !isEditing && !hasVisibleInputs;

  // If section needs edit click and has a table or dictionary, create placeholder structures
  if (needsEditClick) {
    // Check for tables (like Patient History, Outcome Measures, etc.)
    const tables = section.querySelectorAll(
      '.table-root, .table.bulk-body, .table-form-component'
    );
    if (tables.length > 0) {
      tables.forEach((table) => {
        const tableLabel = getFieldLabel(table, section) || sectionTitle;
        const rows = table.querySelectorAll('.table-row');

        rows.forEach((row, rowIndex) => {
          // Get row identifier - try multiple strategies
          let rowName = null;

          // Strategy 1: Look for Name/Measure/Behavior/Activity column
          const nameCell = row.querySelector(
            "[data-header='Name'], .table-cell.variant-primary, [data-header='Measure'], [data-header='Behavior'], [data-header='Activity'], [data-header='Title'], [data-header='Position'], [data-header='Movement'], [data-header='Transfer'], [data-header='Type of Activity'], [data-header='Type of Ambulation']"
          );
          if (nameCell) {
            rowName = nameCell.textContent?.trim();
          }

          // Strategy 2: If no name cell, use first non-empty cell
          if (!rowName || rowName === '' || rowName === 'Name') {
            const firstCell = row.querySelector('.table-cell:first-child');
            if (firstCell) {
              const firstCellText = firstCell.textContent?.trim();
              const isHeader =
                firstCellText === 'Name' ||
                firstCellText === 'Measure' ||
                firstCellText === 'Activity' ||
                firstCellText === '';
              if (!isHeader && firstCellText) {
                rowName = firstCellText;
              }
            }
          }

          // Strategy 3: If still no identifier, use row index (but only for data rows)
          if (!rowName || rowName === '' || rowName === 'Name') {
            // Check if this is actually a data row
            const hasInputs = row.querySelector(
              'input, textarea, .dropdown, .uf-field'
            );
            if (hasInputs) {
              rowName = `Row ${rowIndex}`;
            } else {
              return; // Skip header rows
            }
          }

          // Check what type of field this row has (Text, Score, Findings, etc.)
          const cells = row.querySelectorAll('.table-cell[data-header]');
          cells.forEach((cell) => {
            const header = cell.getAttribute('data-header');
            if (
              header &&
              header !== 'Name' &&
              header !== 'Actions' &&
              header !== 'Sort'
            ) {
              const combinedKey = `${rowName} - ${header}`;
              // if we've already added this exact field once, skip it
              if (sectionStructure[combinedKey]) return;
              // Create a placeholder that will click edit and then fill
              sectionStructure[combinedKey] = {
                Input: async (val, replyType) => {
                  if (isEmptyOrNull(val)) return;

                  // Click edit first
                  await ensureEditMode();
                  await sleep(600); // Wait for form to load

                  // There can be multiple tables; pick the one that actually contains our row name
                  const updatedTable = rowName
                    ? findTableContainingRowName(rowName)
                    : section.querySelector(
                        '.table-root, .table.bulk-body, .table-form-component .table-root, .table-form-component .table.bulk-body'
                      );
                  if (!updatedTable) return;

                  // Prefer matching by row name (Fusion often changes row order/hidden rows in edit mode)
                  const byNameRow = rowName
                    ? findTableRowByName(updatedTable, rowName)
                    : null;
                  const updatedRows =
                    updatedTable.querySelectorAll('.table-row');
                  const updatedRow =
                    byNameRow || (updatedRows[rowIndex] as HTMLElement | null);
                  if (!updatedRow) return;

                  const updatedCell = updatedRow.querySelector(
                    `td[data-header="${header}"], .table-cell[data-header="${header}"], .column-field[data-header="${header}"]`
                  ) as HTMLElement | null;
                  if (!updatedCell) return;

                  // Use row scope for editor discovery; Fusion sometimes renders editors at the row level.
                  const rowScope =
                    (updatedRow as HTMLElement) ||
                    (updatedCell.closest('.table-row') as HTMLElement | null);
                  const scope: ParentNode = (rowScope as any) || updatedCell;

                  // Some Fusion tables create the editable control only after clicking the cell
                  const activateCellIfNeeded = async () => {
                    const cellEl = updatedCell as HTMLElement;

                    const hasAnyEditor = () => {
                      return (
                        cellEl.querySelector(
                          'textarea, input[type="text"], .dropdown, [contenteditable="true"], .flow-editor-value'
                        ) ||
                        (cellEl as any).isContentEditable ||
                        cellEl.getAttribute('contenteditable') === 'true'
                      );
                    };

                    if (hasAnyEditor()) {
                      try {
                        cellEl.focus();
                      } catch (e) {}
                      return;
                    }

                    // Try multiple activation gestures (Fusion inline-edit can be finicky)
                    cellEl.scrollIntoView({ block: 'center' });
                    cellEl.dispatchEvent(
                      new MouseEvent('mouseover', { bubbles: true })
                    );
                    cellEl.dispatchEvent(
                      new MouseEvent('mouseenter', { bubbles: true })
                    );
                    cellEl.dispatchEvent(
                      new MouseEvent('mousedown', { bubbles: true })
                    );
                    cellEl.dispatchEvent(
                      new MouseEvent('mouseup', { bubbles: true })
                    );
                    cellEl.click();
                    cellEl.dispatchEvent(
                      new MouseEvent('dblclick', { bubbles: true })
                    );

                    // Focus helpers
                    if (!cellEl.hasAttribute('tabindex'))
                      cellEl.setAttribute('tabindex', '0');
                    try {
                      cellEl.focus();
                    } catch (e) {}

                    await sleep(600);

                    // Some implementations never create a nested editor; the cell itself becomes editable.
                    // If Fusion didn't do it, force it as a fallback.
                    if (!hasAnyEditor()) {
                      try {
                        cellEl.setAttribute('contenteditable', 'true');
                      } catch (e) {
                        // ignore
                      }
                      try {
                        cellEl.focus();
                      } catch (e) {}
                      await sleep(50);
                    }
                  };

                  await activateCellIfNeeded();
                  // If Fusion created an editor elsewhere and focused it, only fill it if it's within this row/cell scope.

                  // Guard: Fusion may reuse a single editor for all rows.
                  // Ensure the ACTIVE row matches the row we intend to fill.
                  const getActiveRowName = () => {
                    const activeRow = updatedTable?.querySelector(
                      '.table-row.active, .table-row.selected, .table-row.is-selected, .table-row[aria-selected="true"]'
                    ) as HTMLElement | null;
                    if (!activeRow) return null;
                    const nameCell = activeRow.querySelector(
                      '[data-header="Name"], .table-cell.variant-primary, .table-cell[data-header="Measure"], .table-cell[data-header="Activity"]'
                    ) as HTMLElement | null;
                    return nameCell?.textContent?.trim() || null;
                  };

                  const ensureTargetRowActive = async () => {
                    // Click the row + name cell + target cell to ensure Fusion switches the shared editor to this row
                    const nameCellEl = updatedRow.querySelector(
                      '[data-header="Name"], .table-cell.variant-primary, .table-cell[data-header="Measure"], .table-cell[data-header="Activity"]'
                    ) as HTMLElement | null;

                    try {
                      (updatedRow as HTMLElement).scrollIntoView({
                        block: 'center'
                      });
                    } catch (e) {}

                    // Try a few click patterns
                    (updatedRow as HTMLElement).click();
                    await sleep(120);
                    if (nameCellEl) {
                      nameCellEl.click();
                      await sleep(120);
                      nameCellEl.dispatchEvent(
                        new MouseEvent('dblclick', { bubbles: true })
                      );
                      await sleep(120);
                    }

                    (updatedCell as HTMLElement).click();
                    await sleep(150);
                    (updatedCell as HTMLElement).dispatchEvent(
                      new MouseEvent('dblclick', { bubbles: true })
                    );
                    await sleep(250);

                    // Wait briefly for Fusion to mark the active row
                    for (let i = 0; i < 10; i++) {
                      const active = getActiveRowName();
                      if (!active || !rowName || active === rowName) break;
                      (updatedCell as HTMLElement).click();
                      await sleep(120);
                    }
                  };

                  let activeRowName = getActiveRowName();

                  // Force activation of the target row/cell if needed
                  if (rowName && activeRowName && activeRowName !== rowName) {
                    await ensureTargetRowActive();
                    activeRowName = getActiveRowName();
                  } else if (!activeRowName) {
                    // If Fusion doesn't expose active state, still click around to move the shared editor
                    await ensureTargetRowActive();
                    activeRowName = getActiveRowName();
                  }

                  // If the active row now matches (or still unknown), prefer filling the GLOBAL active editor first.
                  // Fusion often uses a single shared editor outside the row DOM.
                  if (!activeRowName || !rowName || activeRowName === rowName) {
                    // First: fill whatever Fusion focused (global/shared editor)
                    if (fillFromActiveElementFallback(val, replyType)) {
                      return;
                    }
                    // Second: try row/cell-scoped fallback
                    if (
                      fillFromActiveElementFallback(
                        val,
                        replyType,
                        scope as any
                      )
                    ) {
                      return;
                    }
                  }
                  // Fusion sometimes makes the cell itself editable (no textarea/input is created)
                  if (
                    (updatedCell as any).isContentEditable ||
                    updatedCell.getAttribute('contenteditable') === 'true'
                  ) {
                    const cellEl = updatedCell as HTMLElement;
                    fillContentEditableValue(cellEl, val, replyType);

                    // Commit value: some Fusion inline editors save on blur/enter
                    cellEl.dispatchEvent(
                      new KeyboardEvent('keydown', {
                        key: 'Enter',
                        code: 'Enter',
                        bubbles: true
                      })
                    );
                    cellEl.dispatchEvent(
                      new KeyboardEvent('keyup', {
                        key: 'Enter',
                        code: 'Enter',
                        bubbles: true
                      })
                    );
                    cellEl.dispatchEvent(new Event('blur', { bubbles: true }));

                    // Also blur the active element if it changed
                    const ae = document.activeElement as HTMLElement | null;
                    if (ae && ae !== cellEl) {
                      ae.dispatchEvent(new Event('blur', { bubbles: true }));
                    }

                    return;
                  }

                  // Try to find input, textarea, dropdown, or contenteditable editor
                  // History Items uses textarea.expander-input specifically
                  const textarea = scope.querySelector(
                    'textarea.expander-input, textarea'
                  ) as HTMLTextAreaElement | null;
                  const textInput = scope.querySelector(
                    'input[type="text"]'
                  ) as HTMLInputElement | null;
                  const dropdown = scope.querySelector(
                    '.dropdown'
                  ) as HTMLElement | null;

                  // Prefer the current cell if it is editable; otherwise search within the row scope only
                  const contentEditable =
                    (updatedCell as any).isContentEditable ||
                    updatedCell.getAttribute('contenteditable') === 'true'
                      ? (updatedCell as HTMLElement)
                      : (scope.querySelector(
                          '[contenteditable="true"]'
                        ) as HTMLElement | null);

                  // Do NOT search the entire section; that causes all writes to go to the first editor
                  const flowEditor = scope.querySelector(
                    '.flow-editor-value'
                  ) as HTMLTextAreaElement | null;

                  if (flowEditor) {
                    flowEditor.focus();
                    removeReadonlyTemporarily(flowEditor, () =>
                      fillTextareaValue(flowEditor, val, replyType)
                    );
                  } else if (textarea) {
                    textarea.focus();
                    removeReadonlyTemporarily(textarea, () =>
                      fillTextareaValue(textarea, val, replyType)
                    );
                  } else if (textInput) {
                    textInput.focus();
                    removeReadonlyTemporarily(textInput, () =>
                      fillTextInputValue(textInput, val, replyType)
                    );
                  } else if (dropdown) {
                    const dropdownItems =
                      dropdown.querySelectorAll('.dropdown-item');
                    const matchingItem = Array.from(dropdownItems).find(
                      (item) =>
                        (item as HTMLElement).textContent?.trim() ===
                        String(val).trim()
                    ) as HTMLElement;
                    if (matchingItem) matchingItem.click();
                  } else if (contentEditable) {
                    fillContentEditableValue(contentEditable, val, replyType);
                  }
                },
                'Existing Value': cell.textContent?.trim() || ''
              };
            }
          });
        });
      });

      // Return early since we've created placeholder structures
      // The actual inputs will be detected after edit is clicked
      return;
    }

    // Check for dictionaries (like Appointment section)
    const dictionaries = sectionContent?.querySelectorAll('.dictionary');
    if (dictionaries && dictionaries.length > 0) {
      dictionaries.forEach((dictionary) => {
        const dictionaryItems = dictionary.querySelectorAll('.dictionary-item');
        dictionaryItems.forEach((item) => {
          const key = item.getAttribute('data-key');
          if (key) {
            const keyLabel =
              item.querySelector('.dictionary-key')?.textContent?.trim() || key;
            const existingValue =
              item.querySelector('.dictionary-value')?.textContent?.trim() ||
              '';

            // Find the actual input elements now (like handleObjectivesGoalsSection does)
            // Try to find textarea/input/dropdown within or near the dictionary item
            let textarea: HTMLTextAreaElement | null = item.querySelector(
              'textarea'
            ) as HTMLTextAreaElement;
            let input: HTMLInputElement | null = item.querySelector(
              'input[type="text"]'
            ) as HTMLInputElement;
            let dropdown: HTMLElement | null = item.querySelector(
              '.dropdown'
            ) as HTMLElement;

            // Also check in parent/sibling elements if not found directly
            if (!textarea && !input && !dropdown) {
              const parent = item.parentElement;
              if (parent) {
                textarea = parent.querySelector(
                  'textarea'
                ) as HTMLTextAreaElement;
                input = parent.querySelector(
                  'input[type="text"]'
                ) as HTMLInputElement;
                dropdown = parent.querySelector('.dropdown') as HTMLElement;
              }
            }

            // Fallback: try to find by name attribute after edit is clicked
            // Store references to use in Input function
            const itemKey = key;
            const itemKeyLabel = keyLabel;
            const dictionaryItemElement = item;

            // Combine section title with key label using dash separator
            const combinedKey = `${sectionTitle} - ${keyLabel}`;
            const fieldKey = getUniqueKey(combinedKey, sectionStructure);
            const innerField = {
              Input: async (val, replyType) => {
                if (isEmptyOrNull(val)) return;

                // Click edit first
                await ensureEditMode();
                await sleep(300);

                // Re-query the elements after edit mode (they might have been recreated)
                let htmlTextarea: HTMLTextAreaElement | null =
                  dictionaryItemElement.querySelector(
                    'textarea'
                  ) as HTMLTextAreaElement;
                let htmlInput: HTMLInputElement | null =
                  dictionaryItemElement.querySelector(
                    'input[type="text"]'
                  ) as HTMLInputElement;
                let htmlDropdown: HTMLElement | null =
                  dictionaryItemElement.querySelector(
                    '.dropdown'
                  ) as HTMLElement;

                // Also check in parent/sibling elements
                if (!htmlTextarea && !htmlInput && !htmlDropdown) {
                  const parent = dictionaryItemElement.parentElement;
                  if (parent) {
                    htmlTextarea = parent.querySelector(
                      'textarea'
                    ) as HTMLTextAreaElement;
                    htmlInput = parent.querySelector(
                      'input[type="text"]'
                    ) as HTMLInputElement;
                    htmlDropdown = parent.querySelector(
                      '.dropdown'
                    ) as HTMLElement;
                  }
                }

                // Fallback: try to find by name attribute
                if (!htmlTextarea && !htmlInput && !htmlDropdown) {
                  const form = section.querySelector('form');
                  if (form) {
                    htmlTextarea = form.querySelector(
                      `textarea[name*="${itemKey}"], textarea[name*="${itemKeyLabel}"]`
                    ) as HTMLTextAreaElement;
                    htmlInput = form.querySelector(
                      `input[name*="${itemKey}"], input[name*="${itemKeyLabel}"]`
                    ) as HTMLInputElement;
                    htmlDropdown = form.querySelector(
                      '.dropdown'
                    ) as HTMLElement;
                  }
                }

                // Fill the field (following handleObjectivesGoalsSection pattern)
                if (htmlTextarea) {
                  if (htmlTextarea.disabled) {
                    return;
                  }

                  const oldValue = htmlTextarea.value;
                  if (replyType === 'append') {
                    htmlTextarea.value =
                      `${htmlTextarea.value || ''}\n${val}`.trim();
                  } else {
                    htmlTextarea.value = val;
                  }
                  htmlTextarea.dispatchEvent(
                    new Event('input', { bubbles: true })
                  );
                  htmlTextarea.dispatchEvent(
                    new Event('change', { bubbles: true })
                  );
                } else if (htmlInput) {
                  if (htmlInput.disabled) {
                    return;
                  }

                  const oldValue = htmlInput.value;
                  if (replyType === 'append') {
                    htmlInput.value = `${htmlInput.value || ''}\n${val}`.trim();
                  } else {
                    htmlInput.value = val;
                  }
                  htmlInput.dispatchEvent(
                    new Event('input', { bubbles: true })
                  );
                  htmlInput.dispatchEvent(
                    new Event('change', { bubbles: true })
                  );
                } else if (htmlDropdown) {
                  const dropdownItems =
                    htmlDropdown.querySelectorAll('.dropdown-item');
                  const matchingItem = Array.from(dropdownItems).find(
                    (item) => (item as HTMLElement).textContent?.trim() === val
                  ) as HTMLElement;
                  if (matchingItem) matchingItem.click();
                }
              },
              'Existing Value': existingValue
            };
            sectionStructure[fieldKey] = { [fieldKey]: innerField };
          }
        });
      });

      // Return early since we've created placeholder structures
      return;
    }
  }

  // Process text areas (including flow-editor textareas and uf-field textareas)
  const textareas = section.querySelectorAll(
    'textarea.textbox:not(.hidden), textarea.flow-editor-value:not(.hidden), .uf-field textarea:not(.hidden), .userform-text-area-field textarea:not(.hidden)'
  );
  textareas.forEach((textarea) => {
    // Skip if readonly
    if (
      textarea.hasAttribute('readonly') &&
      textarea.getAttribute('readonly') !== 'false'
    ) {
      return;
    }

    // Skip if inside a table (tables are handled separately)
    if (isInsideTable(textarea)) {
      return;
    }

    let label = getFieldLabel(textarea, section);

    // Fallback for editor-style sections like Clinical Impressions
    // (Strengths, Performance Impairments, Functional Limitations, Summary, Patient Education)
    // where there is a single flow-editor textarea without a field label.
    if (!label) {
      const isFlowEditor = textarea.classList.contains('flow-editor-value');
      const flowEditorTextareas = section.querySelectorAll(
        'textarea.flow-editor-value'
      );
      if (isFlowEditor && flowEditorTextareas.length === 1 && sectionTitle) {
        label = sectionTitle;
      }
    }

    if (label) {
      // Check if field is inside a subsection (but not inside a table)
      const subsectionTitle = getSubsectionTitle(textarea);
      let finalKey = label;
      if (subsectionTitle) {
        finalKey = `${subsectionTitle} ${label}`;
      }
      const key = getUniqueKey(finalKey, sectionStructure);
      // Capture at registration time while textarea is live in the DOM
      const taNameCapture = textarea.getAttribute('name');
      const isFlowEditorTextarea = !!textarea.closest('form.flow-editor');
      sectionStructure[key] = {
        Input: async (val, replyType) => {
          if (isEmptyOrNull(val)) {
            // Skip empty answers
            return;
          }

          // For form.flow-editor sections (SOAP, evaluations) use fresh document
          // queries and bypass ensureEditMode to avoid stale closure references.
          if (isFlowEditorTextarea && taNameCapture) {
            const liveTextarea = document.querySelector(
              `form.flow-editor textarea[name="${taNameCapture}"]`
            ) as HTMLTextAreaElement | null;
            if (!liveTextarea) return;
            fillTextareaValue(liveTextarea, val, replyType);
            await sleep(50);
            const liveSubmitBtn = (
              liveTextarea.closest('form') as HTMLFormElement | null
            )?.querySelector(
              'button[type="submit"]'
            ) as HTMLButtonElement | null;
            if (liveSubmitBtn) {
              liveSubmitBtn.click();
              await sleep(500);
            }
            return;
          }

          let enteredEditMode = false;
          let textareaFilled = false;

          // Always check for edit button if not in editing mode
          if (!isEditing && editButton) {
            await ensureEditMode();
            enteredEditMode = true;
            // Re-query the textarea after edit mode is activated
            const textareaName = textarea.getAttribute('name');
            const updatedTextarea = textareaName
              ? (section.querySelector(
                  `textarea[name="${textareaName}"]`
                ) as HTMLTextAreaElement)
              : (section.querySelector(
                  'textarea.flow-editor-value, textarea.textbox:not(.hidden)'
                ) as HTMLTextAreaElement);

            if (updatedTextarea) {
              fillTextareaValue(updatedTextarea, val, replyType);
              textareaFilled = true;
              // Wait a moment for the value to be set
              await sleep(50);
            } else {
              fillTextareaValue(textarea, val, replyType);
              textareaFilled = true;
              await sleep(50);
            }
          } else {
            fillTextareaValue(textarea, val, replyType);
            textareaFilled = true;
            // Wait a moment for the value to be set
            await sleep(50);
          }

          if (!textareaFilled) {
            return;
          }

          // If we entered edit mode, we need to save
          // Wait a moment for the save button to appear (it might render after the textarea is filled)
          await sleep(100);

          // Try multiple ways to find the save button
          const findSaveButtonInSection = (
            sec: HTMLElement
          ): HTMLButtonElement | null => {
            // Try data-fire attribute first
            let btn = sec.querySelector(
              'button[data-fire="~save"]'
            ) as HTMLButtonElement;
            if (btn && btn.offsetParent !== null) return btn;

            // Try submit button inside a flow-editor form (e.g. Fusion SOAP sections)
            const flowEditorForm = sec.querySelector('form.flow-editor');
            if (flowEditorForm) {
              const submitBtn = flowEditorForm.querySelector(
                'button[type="submit"]'
              ) as HTMLButtonElement;
              if (submitBtn && submitBtn.offsetParent !== null)
                return submitBtn;
            }

            // Try by text content
            const allButtons = Array.from(
              sec.querySelectorAll('button')
            ) as HTMLButtonElement[];
            for (const b of allButtons) {
              if (b.offsetParent === null) continue;
              const text = (b.textContent || '').toLowerCase().trim();
              if (text === 'save' || text.includes('save')) {
                return b;
              }
            }

            // Try in parent sections (save button might be in a parent container)
            let parent = sec.parentElement;
            let depth = 0;
            while (parent && depth < 3) {
              btn = parent.querySelector(
                'button[data-fire="~save"]'
              ) as HTMLButtonElement;
              if (btn && btn.offsetParent !== null) return btn;
              parent = parent.parentElement;
              depth++;
            }

            return null;
          };

          // Check dynamically if section is in edit mode (has save button visible)
          let currentSaveButton = findSaveButtonInSection(section);
          const isCurrentlyInEditMode = currentSaveButton !== null;

          // If we entered edit mode but don't see save button yet, wait a bit more
          if (enteredEditMode && !currentSaveButton) {
            for (let i = 0; i < 10; i++) {
              await sleep(100);
              currentSaveButton = findSaveButtonInSection(section);
              if (currentSaveButton) break;
            }
          }

          if (enteredEditMode || isCurrentlyInEditMode) {
            if (currentSaveButton) {
              currentSaveButton.click();

              // Wait for save to complete (save button disappears or section exits edit mode)
              let saveCompleted = false;
              for (let i = 0; i < 40; i++) {
                await sleep(50);
                const checkSaveBtn = findSaveButtonInSection(section);
                const stillInEditMode = checkSaveBtn !== null;
                if (!stillInEditMode) {
                  saveCompleted = true;
                  break;
                }
              }

              if (!saveCompleted) {
                // Save did not visibly complete within timeout; allow caller to proceed
              }
            } else {
              // No save button found; section may auto-save
            }
          } else {
            // Section never entered edit mode; nothing to save
          }
        },
        'Existing Value':
          textarea.value ||
          section.querySelector('.flow-section-text')?.textContent?.trim() ||
          ''
      };
    }
  });

  // Process text inputs (regular and date)
  const textInputs = section.querySelectorAll(
    'input.textbox[type="text"]:not(.hidden), input.date-value:not(.hidden)'
  );
  textInputs.forEach((input) => {
    // Skip if inside a table (tables are handled separately)
    if (isInsideTable(input)) {
      return;
    }

    if (input.classList.contains('date-value')) {
      // Handle date inputs
      const label = getFieldLabel(input, section);
      if (label) {
        // Check if field is inside a subsection (but not inside a table)
        const subsectionTitle = getSubsectionTitle(input);
        let finalKey = label;
        if (subsectionTitle) {
          finalKey = `${subsectionTitle} ${label}`;
        }
        const key = getUniqueKey(finalKey, sectionStructure);
        sectionStructure[key] = {
          Input: async (val, replyType) => {
            if (isEmptyOrNull(val)) return;

            // Always check for edit button if not in editing mode
            if (!isEditing && editButton) {
              await ensureEditMode();
              // Re-query after edit mode
              const inputName = input.getAttribute('name');
              const updatedInput = inputName
                ? (section.querySelector(
                    `input[name="${inputName}"].date-value`
                  ) as HTMLInputElement)
                : (section.querySelector(
                    'input.date-value:not(.hidden)'
                  ) as HTMLInputElement);

              if (updatedInput) {
                updatedInput.focus();
                updatedInput.value = val;
                updatedInput.dispatchEvent(
                  new Event('input', { bubbles: true })
                );
                updatedInput.dispatchEvent(
                  new Event('change', { bubbles: true })
                );
                return;
              }
            }

            input.focus();
            input.value = val;
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.dispatchEvent(new Event('change', { bubbles: true }));
            // Trigger datepicker if needed
            const dateOpener = input
              .closest('.date')
              ?.querySelector('.date-opener button');
            if (dateOpener) {
              dateOpener.click();
            }
          },
          'Existing Value': input.value || ''
        };
      }
    } else {
      // Regular text input
      const label = getFieldLabel(input, section);
      if (label) {
        // Check if field is inside a subsection (but not inside a table)
        const subsectionTitle = getSubsectionTitle(input);
        let finalKey = label;
        if (subsectionTitle) {
          finalKey = `${subsectionTitle} ${label}`;
        }
        const key = getUniqueKey(finalKey, sectionStructure);
        sectionStructure[key] = {
          // Generic guidance for single-line inputs
          Description:
            'This is a single-line field. Keep the value concise (e.g., a short phrase or number). Use longer text areas for detailed narratives.',
          Input: async (val, replyType) => {
            if (isEmptyOrNull(val)) {
              // Skip empty answers
              return;
            }

            // Always check for edit button if not in editing mode
            if (!isEditing && editButton) {
              await ensureEditMode();
              // Re-query after edit mode
              const inputName = input.getAttribute('name');
              const updatedInput = inputName
                ? (section.querySelector(
                    `input[name="${inputName}"][type="text"]`
                  ) as HTMLInputElement)
                : (section.querySelector(
                    'input.textbox[type="text"]:not(.hidden)'
                  ) as HTMLInputElement);

              if (updatedInput) {
                updatedInput.focus();
                if (replyType === 'append') {
                  updatedInput.value = (updatedInput.value + '\n' + val).trim();
                } else {
                  updatedInput.value = val;
                }
                updatedInput.dispatchEvent(
                  new Event('input', { bubbles: true })
                );
                updatedInput.dispatchEvent(
                  new Event('change', { bubbles: true })
                );
                return;
              }
            }

            input.focus();
            if (replyType === 'append') {
              input.value = (input.value + '\n' + val).trim();
            } else {
              input.value = val;
            }
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.dispatchEvent(new Event('change', { bubbles: true }));
          },
          'Existing Value': input.value || ''
        };
      }
    }
  });

  // Process dropdowns (custom Fusion dropdowns - both editable and readonly)
  const dropdowns = section.querySelectorAll('.dropdown:not(.hidden)');
  dropdowns.forEach((dropdown) => {
    // Skip if inside a table (tables are handled separately)
    // Check multiple ways to ensure we catch all table contexts
    if (isInsideTable(dropdown)) {
      return;
    }

    // Additional explicit check: manually traverse parent chain to find table cells
    let parent = dropdown.parentElement;
    let foundTableCell = false;
    while (parent && parent !== section && parent !== document.body) {
      if (
        parent.classList.contains('table-cell') ||
        parent.classList.contains('column-field') ||
        parent.classList.contains('table-row') ||
        parent.classList.contains('userform-table') ||
        parent.classList.contains('table-form-builder-component') ||
        parent.classList.contains('table-root') ||
        parent.classList.contains('table') ||
        (parent.hasAttribute &&
          parent.hasAttribute('data-header') &&
          (parent.classList.contains('table-cell') ||
            parent.classList.contains('column-field')))
      ) {
        foundTableCell = true;
        break;
      }
      parent = parent.parentElement;
    }
    if (foundTableCell) {
      return;
    }

    const textInput = dropdown.querySelector(
      'input.dropdown-text'
    ) as HTMLInputElement;
    const isReadonly =
      textInput &&
      textInput.hasAttribute('readonly') &&
      textInput.getAttribute('readonly') !== 'false';

    // For inline-edit sections, we'll handle them when editing
    if (isReadonly && !isEditing && !isInlineEdit) {
      // Skip readonly dropdowns that aren't in inline-edit sections
      return;
    }

    const label = getFieldLabel(dropdown, section);
    if (label) {
      // Safety check: if label matches a common table header name, ALWAYS check if it's in a table
      // These are almost always table headers, not standalone fields
      const commonTableHeaders = [
        'Position',
        'Movement',
        'Transfer',
        'Description',
        'Performance',
        'Distance',
        'Description / Device',
        'Type of Ambulation',
        'Type of Activity',
        ' Work Performance'
      ];
      if (commonTableHeaders.includes(label)) {
        // Double-check if it's in a table using multiple methods
        const inTableCell = dropdown.closest('.table-cell, .column-field');
        const inTableRow = dropdown.closest('.table-row');
        const inUserformTable = dropdown.closest('.userform-table');
        if (
          inTableCell ||
          inTableRow ||
          inUserformTable ||
          isInsideTable(dropdown)
        ) {
          return; // Skip - this is definitely a table cell, not a standalone field
        }
      }

      // Check if field is inside a subsection (but not inside a table)
      const subsectionTitle = getSubsectionTitle(dropdown);
      let finalKey = label;
      if (subsectionTitle) {
        finalKey = `${subsectionTitle} ${label}`;
      }
      const key = getUniqueKey(finalKey, sectionStructure);
      const hiddenInput = dropdown.querySelector(
        'input.hidden.dropdown-value'
      ) as HTMLInputElement;
      const dropdownItems = dropdown.querySelectorAll('.dropdown-item');

      const choices = Array.from(dropdownItems)
        .map((item) => (item as HTMLElement).textContent?.trim())
        .filter((text) => text && text !== '');

      sectionStructure[key] = {
        Choices: choices.length > 0 ? choices : undefined,
        Input: async (val, replyType) => {
          if (isEmptyOrNull(val)) {
            // Skip empty answers
            return;
          }

          // If readonly or inline-edit, we need to click edit first
          if ((isReadonly || isInlineEdit) && !isEditing) {
            await ensureEditMode();
            // Re-query the dropdown after edit mode is activated
            const updatedDropdown = section.querySelector(
              '.dropdown:not(.hidden)'
            );
            if (updatedDropdown) {
              const updatedHiddenInput = updatedDropdown.querySelector(
                'input.hidden.dropdown-value'
              ) as HTMLInputElement;
              const updatedTextInput = updatedDropdown.querySelector(
                'input.dropdown-text'
              ) as HTMLInputElement;
              const updatedDropdownItems =
                updatedDropdown.querySelectorAll('.dropdown-item');

              const matchingItem = Array.from(updatedDropdownItems).find(
                (item) => (item as HTMLElement).textContent?.trim() === val
              ) as HTMLElement;

              if (matchingItem) {
                const dataValue =
                  matchingItem.getAttribute('data-value') || val;

                if (updatedHiddenInput) {
                  updatedHiddenInput.value = dataValue;
                  updatedHiddenInput.dispatchEvent(
                    new Event('change', { bubbles: true })
                  );
                }

                if (updatedTextInput) {
                  updatedTextInput.value = val;
                  updatedTextInput.dispatchEvent(
                    new Event('input', { bubbles: true })
                  );
                  updatedTextInput.dispatchEvent(
                    new Event('change', { bubbles: true })
                  );
                }

                matchingItem.click();
              }
              return;
            }
          }

          // Find matching dropdown item
          const matchingItem = Array.from(dropdownItems).find(
            (item) => (item as HTMLElement).textContent?.trim() === val
          ) as HTMLElement;

          if (matchingItem) {
            const dataValue = matchingItem.getAttribute('data-value') || val;

            // Update hidden input
            if (hiddenInput) {
              hiddenInput.value = dataValue;
              hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
            }

            // Update visible text input
            if (textInput) {
              textInput.value = val;
              textInput.dispatchEvent(new Event('input', { bubbles: true }));
              textInput.dispatchEvent(new Event('change', { bubbles: true }));
            }

            // Click the matching item to trigger any UI updates
            matchingItem.click();
          }
        },
        'Existing Value': textInput?.value || ''
      };
    }
  });

  // Process checkboxes
  const checkboxGroups = section.querySelectorAll(
    '.options:not(.hidden), .userform-checkboxes-field:not(.hidden)'
  );
  checkboxGroups.forEach((checkboxGroup) => {
    // Skip if inside a table (tables are handled separately)
    if (isInsideTable(checkboxGroup)) {
      return;
    }

    const label = getFieldLabel(checkboxGroup, section);
    if (label) {
      // Check if field is inside a subsection (but not inside a table)
      const subsectionTitle = getSubsectionTitle(checkboxGroup);
      let finalKey = label;
      if (subsectionTitle) {
        finalKey = `${subsectionTitle} ${label}`;
      }
      const key = getUniqueKey(finalKey, sectionStructure);
      const checkboxes = checkboxGroup.querySelectorAll(
        'input.options-item-value[type="checkbox"]'
      );
      const checkboxMap = {};

      checkboxes.forEach((checkbox) => {
        const checkboxItem = (checkbox as HTMLElement).closest('.options-item');
        const checkboxText = checkboxItem
          ?.querySelector('.options-item-text')
          ?.textContent?.trim();
        const customInput = checkboxItem?.querySelector(
          'input.options-item-render'
        ) as HTMLInputElement;

        if (checkboxText) {
          checkboxMap[checkboxText] = {
            checkbox: checkbox as HTMLInputElement,
            customInput: customInput
          };
        }
      });

      const choices = Object.keys(checkboxMap);
      sectionStructure[key] = {
        Choices: choices.length > 0 ? choices : undefined,
        Input: async (val, replyType) => {
          if (isEmptyOrNull(val)) return;

          // Always check for edit button if not in editing mode
          if (!isEditing && editButton) {
            await ensureEditMode();
            // Re-query checkboxes after edit mode
            const updatedCheckboxGroup = section.querySelector(
              '.options, .userform-checkboxes-field'
            );
            if (updatedCheckboxGroup) {
              const updatedCheckboxes = updatedCheckboxGroup.querySelectorAll(
                'input.options-item-value[type="checkbox"]'
              );
              const updatedCheckboxMap = {};

              updatedCheckboxes.forEach((checkbox) => {
                const checkboxItem = (checkbox as HTMLElement).closest(
                  '.options-item'
                );
                const checkboxText = checkboxItem
                  ?.querySelector('.options-item-text')
                  ?.textContent?.trim();
                if (checkboxText) {
                  updatedCheckboxMap[checkboxText] =
                    checkbox as HTMLInputElement;
                }
              });

              // Handle multiple values separated by $ (for multi-select checkboxes)
              const values =
                typeof val === 'string'
                  ? val.split('$').map((v) => v.trim())
                  : [val];

              values.forEach((value) => {
                if (updatedCheckboxMap[value]) {
                  updatedCheckboxMap[value].checked = true;
                  updatedCheckboxMap[value].dispatchEvent(
                    new Event('change', { bubbles: true })
                  );
                }
              });
              return;
            }
          }

          // Handle multiple values separated by $ (for multi-select checkboxes)
          const values =
            typeof val === 'string'
              ? val.split('$').map((v) => v.trim())
              : [val];

          values.forEach((value) => {
            // Check if it's a custom value
            if (value === 'custom' && checkboxMap['custom']) {
              checkboxMap['custom'].checkbox.checked = true;
              checkboxMap['custom'].checkbox.dispatchEvent(
                new Event('change', { bubbles: true })
              );
            } else if (checkboxMap[value]) {
              checkboxMap[value].checkbox.checked = true;
              checkboxMap[value].checkbox.dispatchEvent(
                new Event('change', { bubbles: true })
              );
            }
          });
        },
        'Existing Value': Array.from(checkboxes)
          .filter((cb) => (cb as HTMLInputElement).checked)
          .map((cb) => {
            const item = (cb as HTMLElement).closest('.options-item');
            return (
              item?.querySelector('.options-item-text')?.textContent?.trim() ||
              ''
            );
          })
          .filter(Boolean)
          .join(', ')
      };
    }
  });

  // Process tables with multiple fields (like diagnoses table, balance table)
  // Handle both editing mode tables and inline-edit tables
  // Skip tables that are inside .userform-table (those are handled separately)
  // Also check for .table-form-component which is used in History Items
  let tables = Array.from(
    section.querySelectorAll(
      '.table-root:not(.hidden), .table.bulk-body:not(.hidden), .table-form-component:not(.hidden), .table.mountable:not(.hidden)'
    )
  ) as HTMLElement[];

  // Also check for tables inside .bulk mountable (History Items uses this)
  if (tables.length === 0) {
    const bulkMountable = section.querySelector('.bulk.mountable');
    if (bulkMountable) {
      const nestedTables = Array.from(
        bulkMountable.querySelectorAll(
          '.table-root, .table.bulk-body, .table-form-component, .table.mountable'
        )
      ) as HTMLElement[];
      if (nestedTables.length > 0) {
        tables = nestedTables;
      }
    }

    // Also try finding table-form-component directly
    if (tables.length === 0) {
      const tableFormComponent = section.querySelector('.table-form-component');
      if (tableFormComponent) {
        tables = [tableFormComponent as HTMLElement];
      }
    }
  }

  tables.forEach((table, tableIndex) => {
    // Skip if table is inside a userform-table (handled by userformTables section)
    if (table.closest('.userform-table, .table-form-builder-component')) {
      return;
    }

    // Skip if table is in a read-only section (no editing class and no editable inputs and not inline-edit)
    const hasEditableInputs = table.querySelector(
      'input:not([readonly]), textarea:not([readonly]), .dropdown.editable'
    );

    if (!isEditing && !hasEditableInputs && !isInlineEdit) {
      return;
    }

    const tableLabel = getFieldLabel(table, section) || sectionTitle || 'Table';
    const rows = table.querySelectorAll('.table-row:not(.hidden)');

    rows.forEach((row, rowIndex) => {
      // Get row identifier - try multiple strategies
      let rowIdentifier = null;

      // Strategy 1: Look for Name/Measure/Behavior/Activity/Work Performance column
      // First check if it's a dropdown and get its value
      const nameCell = row.querySelector(
        "[data-header='Name'], .table-cell.variant-primary, [data-header='Measure'], [data-header='Behavior'], [data-header='Activity'], [data-header='Title'], [data-header='Position'], [data-header='Movement'], [data-header='Transfer'], [data-header='Type of Activity'], [data-header='Type of Ambulation'], [data-header=' Work Performance']"
      );
      if (nameCell) {
        // Get the header attribute to check against
        const cellHeader = nameCell.getAttribute('data-header')?.trim();

        // Check if it contains a dropdown - get the dropdown value
        const dropdown = nameCell.querySelector('.dropdown');
        if (dropdown) {
          const dropdownText = dropdown.querySelector(
            'input.dropdown-text'
          ) as HTMLInputElement;
          const dropdownValue = dropdownText?.value?.trim();
          if (
            dropdownValue &&
            dropdownValue !== '' &&
            dropdownValue !== '...'
          ) {
            // Make sure the dropdown value is not the same as the header and not a common header name
            const isHeaderValue =
              dropdownValue === 'Name' ||
              dropdownValue === 'Measure' ||
              dropdownValue === 'Activity' ||
              dropdownValue === ' Work Performance' ||
              dropdownValue === 'Title' ||
              dropdownValue === 'Position' ||
              dropdownValue === 'Movement' ||
              dropdownValue === 'Transfer' ||
              dropdownValue === 'Type of Ambulation' ||
              dropdownValue === 'Type of Activity' ||
              dropdownValue === 'Description / Device' ||
              dropdownValue === 'Performance' ||
              dropdownValue === 'Distance' ||
              dropdownValue === cellHeader;
            if (!isHeaderValue) {
              rowIdentifier = dropdownValue;
            }
          }
          // If dropdown is empty, don't use header text - let Strategy 2/3 handle it
        } else {
          // Not a dropdown, use text content (but check it's not a header)
          const cellText = nameCell.textContent?.trim();
          // Don't use header values as identifiers - check both against known headers and the cell's data-header attribute
          if (
            cellText &&
            cellText !== 'Name' &&
            cellText !== 'Measure' &&
            cellText !== 'Activity' &&
            cellText !== ' Work Performance' &&
            cellText !== 'Title' &&
            cellText !== 'Position' &&
            cellText !== 'Movement' &&
            cellText !== 'Transfer' &&
            cellText !== 'Type of Ambulation' &&
            cellText !== 'Type of Activity' &&
            cellText !== 'Description / Device' &&
            cellText !== 'Performance' &&
            cellText !== 'Distance' &&
            cellText !== cellHeader
          ) {
            // Also check against the actual header attribute
            rowIdentifier = cellText;
          }
        }
      }

      // Strategy 2: If no name cell, check first cell for dropdown or text
      // Also check first cell even if it has a data-header (in case Strategy 1 didn't match)
      // Check against common header names to avoid using them
      const isHeaderIdentifier =
        rowIdentifier === 'Name' ||
        rowIdentifier === ' Work Performance' ||
        rowIdentifier === 'Type of Ambulation' ||
        rowIdentifier === 'Type of Activity' ||
        rowIdentifier === 'Measure' ||
        rowIdentifier === 'Activity' ||
        rowIdentifier === 'Title' ||
        rowIdentifier === 'Position' ||
        rowIdentifier === 'Movement' ||
        rowIdentifier === 'Transfer' ||
        rowIdentifier === 'Description / Device' ||
        rowIdentifier === 'Performance' ||
        rowIdentifier === 'Distance';

      if (!rowIdentifier || rowIdentifier === '' || isHeaderIdentifier) {
        // First try cells without data-header
        let firstCell = row.querySelector(
          '.table-cell:not([data-header]), .column-field:not([data-header])'
        ) as HTMLElement;
        // If no match, get the first cell regardless of data-header (but not if it was already processed in Strategy 1)
        if (!firstCell) {
          const allCells = row.querySelectorAll('.table-cell, .column-field');
          firstCell = allCells[0] as HTMLElement;
        }

        if (firstCell) {
          const firstCellHeader = firstCell.getAttribute('data-header')?.trim();

          // Check if first cell has a dropdown
          const firstCellDropdown = firstCell.querySelector('.dropdown');
          if (firstCellDropdown) {
            const dropdownText = firstCellDropdown.querySelector(
              'input.dropdown-text'
            ) as HTMLInputElement;
            const dropdownValue = dropdownText?.value?.trim();
            // Only use dropdown value if it's not empty, not the header, and not a common header name
            const isHeaderValue =
              dropdownValue === 'Name' ||
              dropdownValue === 'Measure' ||
              dropdownValue === 'Activity' ||
              dropdownValue === ' Work Performance' ||
              dropdownValue === 'Title' ||
              dropdownValue === 'Position' ||
              dropdownValue === 'Movement' ||
              dropdownValue === 'Transfer' ||
              dropdownValue === 'Type of Ambulation' ||
              dropdownValue === 'Type of Activity' ||
              dropdownValue === 'Description / Device' ||
              dropdownValue === 'Performance' ||
              dropdownValue === 'Distance' ||
              dropdownValue === firstCellHeader;
            if (
              dropdownValue &&
              dropdownValue !== '' &&
              dropdownValue !== '...' &&
              !isHeaderValue
            ) {
              rowIdentifier = dropdownValue;
            }
            // If dropdown is empty, don't use textContent - skip to Strategy 3
          } else {
            // No dropdown, try text content (but not if it matches the header)
            const firstCellText = firstCell.textContent?.trim();
            // Check if it's not a header value
            const isHeader =
              firstCellText === 'Name' ||
              firstCellText === 'Measure' ||
              firstCellText === 'Activity' ||
              firstCellText === '' ||
              firstCellText === ' Work Performance' ||
              firstCellText === 'Type of Ambulation' ||
              firstCellText === 'Type of Activity' ||
              firstCellText === 'Description / Device' ||
              firstCellText === 'Performance' ||
              firstCellText === 'Distance' ||
              firstCellText === firstCellHeader; // Also check against cell's own header
            if (!isHeader && firstCellText) {
              rowIdentifier = firstCellText;
            }
          }
        }
      }

      // Strategy 3: If still no identifier, use row index (but only for data rows, not header)
      // Also check against common header names to avoid using them as identifiers
      const isHeaderName =
        rowIdentifier === 'Name' ||
        rowIdentifier === ' Work Performance' ||
        rowIdentifier === 'Type of Ambulation' ||
        rowIdentifier === 'Type of Activity' ||
        rowIdentifier === 'Measure' ||
        rowIdentifier === 'Activity' ||
        rowIdentifier === 'Title' ||
        rowIdentifier === 'Position' ||
        rowIdentifier === 'Movement' ||
        rowIdentifier === 'Transfer' ||
        rowIdentifier === 'Description / Device' ||
        rowIdentifier === 'Performance' ||
        rowIdentifier === 'Distance';

      if (!rowIdentifier || rowIdentifier === '' || isHeaderName) {
        // Check if this is actually a data row (has inputs or is not a header row)
        const hasInputs = row.querySelector(
          'input, textarea, .dropdown, .uf-field'
        );
        if (hasInputs) {
          rowIdentifier = `Row ${rowIndex + 1}`;
        } else {
          return; // Skip header rows
        }
      }

      // Final safety check: ensure rowIdentifier is valid and not a header name
      // Re-check isHeaderName with current rowIdentifier value
      const finalHeaderCheck =
        rowIdentifier === 'Name' ||
        rowIdentifier === ' Work Performance' ||
        rowIdentifier === 'Type of Ambulation' ||
        rowIdentifier === 'Type of Activity' ||
        rowIdentifier === 'Measure' ||
        rowIdentifier === 'Activity' ||
        rowIdentifier === 'Title' ||
        rowIdentifier === 'Position' ||
        rowIdentifier === 'Movement' ||
        rowIdentifier === 'Transfer' ||
        rowIdentifier === 'Description / Device' ||
        rowIdentifier === 'Performance' ||
        rowIdentifier === 'Distance';

      if (!rowIdentifier || rowIdentifier === '' || finalHeaderCheck) {
        // Check if this is actually a data row (has inputs or is not a header row)
        const hasInputs = row.querySelector(
          'input, textarea, .dropdown, .uf-field'
        );
        if (hasInputs) {
          rowIdentifier = `Row ${rowIndex + 1}`;
        } else {
          return; // Skip header rows
        }
      }

      // Check if table is inside a subsection and prepend subsection heading to row identifier
      // Try multiple ways to find the subsection - check from various elements in the hierarchy
      let subsection = null;
      let subsectionTitle = null;

      // Try from the table element itself
      subsection = table.closest('.uf-subsection');
      if (subsection) {
        const subsectionHeading = subsection.querySelector(
          '.uf-subsection-heading-title'
        );
        subsectionTitle = subsectionHeading?.textContent?.trim();
      }

      // If not found, try from the row
      if (!subsectionTitle) {
        subsection = row.closest('.uf-subsection');
        if (subsection) {
          const subsectionHeading = subsection.querySelector(
            '.uf-subsection-heading-title'
          );
          subsectionTitle = subsectionHeading?.textContent?.trim();
        }
      }

      // If still not found, try from parent elements
      if (!subsectionTitle) {
        let parent = table.parentElement;
        while (parent && parent !== document.body) {
          if (parent.classList.contains('uf-subsection')) {
            subsection = parent as HTMLElement;
            const subsectionHeading = subsection.querySelector(
              '.uf-subsection-heading-title'
            );
            subsectionTitle = subsectionHeading?.textContent?.trim();
            break;
          }
          parent = parent.parentElement;
        }
      }

      // Apply subsection prefix if found
      if (subsectionTitle) {
        rowIdentifier = `${subsectionTitle} - ${rowIdentifier}`;
      }

      // Process each cell in the row
      const cells = row.querySelectorAll(
        '.table-cell:not(.hidden), .column-field:not(.hidden)'
      );

      cells.forEach((cell) => {
        const header =
          cell.getAttribute('data-header') ||
          cell.querySelector('.table-header-content')?.textContent?.trim() ||
          '';
        if (
          !header ||
          header === 'Actions' ||
          header === 'Sort' ||
          header === 'Name'
        ) {
          return;
        }

        // Check for different input types in the cell
        const textarea = cell.querySelector(
          'textarea:not([readonly]), textarea.expander-input:not([readonly]), textarea.textbox:not([readonly])'
        ) as HTMLTextAreaElement;
        const textInput = cell.querySelector(
          'input[type="text"]:not(.hidden):not([readonly])'
        ) as HTMLInputElement | null;
        const dateInput = cell.querySelector(
          'input.date-value:not([readonly])'
        ) as HTMLInputElement | null;
        const dropdown = cell.querySelector(
          '.dropdown.editable, .dropdown:not([readonly])'
        );

        // Check for radio buttons (both actual radio inputs and checkboxes styled as radio in "options single")
        const ufField = cell.querySelector('.uf-field');
        const isRadioField = ufField?.classList.contains(
          'userform-radio-field'
        );
        const optionsSingle = ufField?.querySelector('.options.single');
        const radioInputs = cell.querySelectorAll('input[type="radio"]');
        const checkboxRadioInputs = optionsSingle
          ? cell.querySelectorAll('input[type="checkbox"].options-item-value')
          : [];
        // Initialize nested structure for this row
        const combinedKey = `${rowIdentifier} - ${header}`;
        const fieldKey = getUniqueKey(combinedKey, sectionStructure);
        if (textarea) {
          // Store references to help find this specific textarea after edit mode
          const textareaName = textarea.getAttribute('name');
          const rowDataId = row.getAttribute('data-id');
          const rowNameText = rowIdentifier; // Store the row identifier text (e.g., "Medical History")

          // Store identifiers to re-find the textarea (History Items tables re-render frequently)
          const htmlTextarea = textarea as HTMLTextAreaElement;
          const storedRowNameText = rowNameText;
          const storedRowDataId = rowDataId;
          const storedTextareaName = textareaName;
          const storedSectionTitle = sectionTitle;

          sectionStructure[fieldKey] = {
            Input: async (value, replyType) => {
              if (isEmptyOrNull(value)) {
                return;
              }

              // ALWAYS re-find the textarea by row name (like the working console script)
              // History Items tables get re-rendered, so captured references become stale
              const normalize = (s: string) =>
                (s || '').replace(/\s+/g, ' ').trim().toLowerCase();

              // Re-find the section
              const sections = Array.from(
                document.querySelectorAll('.flow-section')
              ) as HTMLElement[];

              const historySection = sections.find((sec) => {
                const title = sec.querySelector(
                  '.flow-section-title'
                )?.textContent;
                const visible = !!sec.offsetParent;
                const normalizedTitle = normalize(title || '');
                const normalizedTarget = normalize(storedSectionTitle || '');
                return visible && normalizedTitle === normalizedTarget;
              });

              if (!historySection) {
                return;
              }

              // Find the row by matching Name cell text (exactly like working console script)
              const rowsWithId = Array.from(
                historySection.querySelectorAll('.table-row[data-id]')
              ) as HTMLElement[];
              const rowsFallback = Array.from(
                historySection.querySelectorAll(
                  '.table-body .table-row, .table-row'
                )
              ) as HTMLElement[];
              const rows = rowsWithId.length > 0 ? rowsWithId : rowsFallback;

              let targetRow: HTMLElement | null = null;

              for (let i = 0; i < rows.length; i++) {
                const row = rows[i];
                const nameCell =
                  row.querySelector('.table-cell[data-header="Name"]') ||
                  row.querySelector('.table-cell.variant-primary');
                const rowName = nameCell?.textContent?.trim();
                const normalizedRowName = normalize(rowName || '');
                const normalizedTarget = normalize(storedRowNameText || '');

                if (rowName && normalizedRowName === normalizedTarget) {
                  targetRow = row;
                  break;
                }
              }

              // Fallback: try by data-id
              if (!targetRow && storedRowDataId) {
                targetRow = historySection.querySelector(
                  `.table-row[data-id="${storedRowDataId}"]`
                ) as HTMLElement;
              }

              if (!targetRow) {
                return;
              }

              // Find textarea within THIS specific row (exactly like working console script)
              const textarea1 = targetRow.querySelector(
                'textarea.expander-input[name^="items["]'
              ) as HTMLTextAreaElement;
              const textarea2 = targetRow.querySelector(
                'textarea.expander-input'
              ) as HTMLTextAreaElement;
              const textarea3 = targetRow.querySelector(
                'textarea'
              ) as HTMLTextAreaElement;

              const targetTextarea = textarea1 || textarea2 || textarea3;

              if (!targetTextarea) {
                return;
              }

              if (targetTextarea.disabled) {
                return;
              }

              targetTextarea.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
              });
              await sleep(100); // Small delay for scroll

              // Focus the textarea
              targetTextarea.focus();

              // Fill the textarea (following handleObjectivesGoalsSection pattern)
              const oldValue = targetTextarea.value;
              if (replyType === 'append') {
                targetTextarea.value =
                  `${targetTextarea.value || ''}\n${value}`.trim();
              } else {
                targetTextarea.value = value;
              }

              targetTextarea.dispatchEvent(
                new Event('input', { bubbles: true })
              );
              targetTextarea.dispatchEvent(
                new Event('change', { bubbles: true })
              );

              targetTextarea.blur();
            },
            'Existing Value': htmlTextarea.value || ''
          };

          // ALSO add to questionInteractions as individual question (so it can be answered directly, not just as Flex)
          questionInteractions[fieldKey] = {
            Type: 'Long Answer',
            Key: fieldKey,
            Question: fieldKey,
            Input: sectionStructure[fieldKey].Input,
            'Existing Value': sectionStructure[fieldKey]['Existing Value']
          };
        } else if (dateInput) {
          sectionStructure[fieldKey] = {
            Input: async (val, replyType) => {
              if (isEmptyOrNull(val)) return;

              // If inline-edit, ensure we're in edit mode
              if (isInlineEdit && !isEditing) {
                await ensureEditMode();
                // Re-query after edit mode
                const updatedRow = table.querySelector(
                  `.table-row[data-id="${row.getAttribute('data-id')}"], .table-row:nth-child(${rowIndex + 1})`
                );
                if (updatedRow) {
                  const updatedDateInput = updatedRow.querySelector(
                    `td[data-header="${header}"] input.date-value, .column-field[data-header="${header}"] input.date-value`
                  ) as HTMLInputElement;
                  if (updatedDateInput) {
                    fillTextInputValue(updatedDateInput, val, replyType);
                    return;
                  }
                }
              }

              fillTextInputValue(dateInput, val, replyType);
            },
            'Existing Value': dateInput.value || ''
          };
        } else if (dropdown) {
          const hiddenInput = dropdown.querySelector(
            'input.hidden.dropdown-value'
          ) as HTMLInputElement;
          const textInput = dropdown.querySelector(
            'input.dropdown-text'
          ) as HTMLInputElement;
          const dropdownItems = dropdown.querySelectorAll('.dropdown-item');

          const choices = Array.from(dropdownItems)
            .map((item) => (item as HTMLElement).textContent?.trim())
            .filter((text) => text && text !== '');

          sectionStructure[fieldKey] = {
            Choices: choices.length > 0 ? choices : undefined,
            Input: async (val, replyType) => {
              if (isEmptyOrNull(val)) return;

              // If inline-edit, ensure we're in edit mode
              if (isInlineEdit && !isEditing) {
                await ensureEditMode();
                // Re-query after edit mode
                const updatedRow = table.querySelector(
                  `.table-row[data-id="${row.getAttribute('data-id')}"], .table-row:nth-child(${rowIndex + 1})`
                );
                if (updatedRow) {
                  const updatedDropdown = updatedRow.querySelector(
                    `td[data-header="${header}"] .dropdown, .column-field[data-header="${header}"] .dropdown`
                  );
                  if (updatedDropdown) {
                    const updatedHiddenInput = updatedDropdown.querySelector(
                      'input.hidden.dropdown-value'
                    ) as HTMLInputElement;
                    const updatedTextInput = updatedDropdown.querySelector(
                      'input.dropdown-text'
                    ) as HTMLInputElement;
                    const updatedDropdownItems =
                      updatedDropdown.querySelectorAll('.dropdown-item');

                    const matchingItem = Array.from(updatedDropdownItems).find(
                      (item) =>
                        (item as HTMLElement).textContent?.trim() === val
                    ) as HTMLElement;

                    if (matchingItem) {
                      const dataValue =
                        matchingItem.getAttribute('data-value') || val;

                      if (updatedHiddenInput) {
                        fillTextInputValue(
                          updatedHiddenInput,
                          dataValue,
                          replyType
                        );
                      }

                      if (updatedTextInput) {
                        fillTextInputValue(updatedTextInput, val, replyType);
                      }

                      matchingItem.click();
                    }
                    return;
                  }
                }
              }

              const matchingItem = Array.from(dropdownItems).find(
                (item) => (item as HTMLElement).textContent?.trim() === val
              ) as HTMLElement;

              if (matchingItem) {
                const dataValue =
                  matchingItem.getAttribute('data-value') || val;

                if (hiddenInput) {
                  fillTextInputValue(hiddenInput, dataValue, replyType);
                }

                if (textInput) {
                  fillTextInputValue(textInput, val, replyType);
                }

                matchingItem.click();
              }
            },
            'Existing Value': textInput?.value || ''
          };
        } else if (textInput) {
          sectionStructure[fieldKey] = {
            Input: async (val, replyType) => {
              if (isEmptyOrNull(val)) return;

              // If inline-edit, ensure we're in edit mode
              if (isInlineEdit && !isEditing) {
                await ensureEditMode();
                // Re-query after edit mode
                const updatedRow = table.querySelector(
                  `.table-row[data-id="${row.getAttribute('data-id')}"], .table-row:nth-child(${rowIndex + 1})`
                );
                if (updatedRow) {
                  const updatedTextInput = updatedRow.querySelector(
                    `td[data-header="${header}"] input[type="text"], .column-field[data-header="${header}"] input[type="text"]`
                  ) as HTMLInputElement;
                  if (updatedTextInput) {
                    fillTextInputValue(updatedTextInput, val, replyType);
                    return;
                  }
                }
              }

              fillTextInputValue(textInput, val, replyType);
            },
            'Existing Value': textInput.value || ''
          };
        } else if (
          isRadioField &&
          (radioInputs.length > 0 || checkboxRadioInputs.length > 0)
        ) {
          // Use checkbox inputs if they exist (styled as radio), otherwise use actual radio inputs
          const inputsToUse =
            checkboxRadioInputs.length > 0 ? checkboxRadioInputs : radioInputs;

          const choices = Array.from(inputsToUse)
            .map((input) => {
              const label = (input as HTMLElement)
                .closest('.uf-field-value, .options-item')
                ?.querySelector('.options-item-text')
                ?.textContent?.trim();
              return label || (input as HTMLInputElement).value;
            })
            .filter(Boolean);

          sectionStructure[fieldKey] = {
            Choices: choices.length > 0 ? choices : undefined,
            Input: async (val, replyType) => {
              if (isEmptyOrNull(val)) return;

              // If inline-edit, ensure we're in edit mode
              if (isInlineEdit && !isEditing) {
                await ensureEditMode();
                await sleep(200);
                // Re-query after edit mode
                const updatedRow = table.querySelector(
                  `.table-row[data-id="${row.getAttribute('data-id')}"], .table-row:nth-child(${rowIndex + 1})`
                );
                if (updatedRow) {
                  const updatedCell = updatedRow.querySelector(
                    `td[data-header="${header}"], .column-field[data-header="${header}"]`
                  );
                  const updatedUfField = updatedCell?.querySelector(
                    '.uf-field.userform-radio-field'
                  );
                  if (updatedUfField) {
                    const updatedOptionsSingle =
                      updatedUfField.querySelector('.options.single');
                    const updatedInputs = updatedOptionsSingle
                      ? updatedUfField.querySelectorAll(
                          'input[type="checkbox"].options-item-value'
                        )
                      : updatedUfField.querySelectorAll('input[type="radio"]');

                    const matchingInput = Array.from(updatedInputs).find(
                      (input) => {
                        const label = (input as HTMLElement)
                          .closest('.uf-field-value, .options-item')
                          ?.querySelector('.options-item-text')
                          ?.textContent?.trim();
                        return (
                          label === val ||
                          (input as HTMLInputElement).value === val
                        );
                      }
                    ) as HTMLInputElement;

                    if (matchingInput) {
                      // For radio buttons, uncheck all others first
                      if (updatedOptionsSingle) {
                        Array.from(updatedInputs).forEach((input) => {
                          (input as HTMLInputElement).checked = false;
                        });
                      }
                      matchingInput.checked = true;
                      matchingInput.dispatchEvent(
                        new Event('change', { bubbles: true })
                      );
                    }
                    return;
                  }
                }
              }

              const matchingInput = Array.from(inputsToUse).find((input) => {
                const label = (input as HTMLElement)
                  .closest('.uf-field-value, .options-item')
                  ?.querySelector('.options-item-text')
                  ?.textContent?.trim();
                return (
                  label === val || (input as HTMLInputElement).value === val
                );
              }) as HTMLInputElement;

              if (matchingInput) {
                // For radio buttons, uncheck all others first
                if (optionsSingle) {
                  Array.from(inputsToUse).forEach((input) => {
                    (input as HTMLInputElement).checked = false;
                  });
                }
                matchingInput.checked = true;
                matchingInput.dispatchEvent(
                  new Event('change', { bubbles: true })
                );
              }
            },
            'Existing Value':
              Array.from(inputsToUse)
                .filter((r) => (r as HTMLInputElement).checked)
                .map((r) => {
                  const label = (r as HTMLElement)
                    .closest('.uf-field-value, .options-item')
                    ?.querySelector('.options-item-text')
                    ?.textContent?.trim();
                  return label || (r as HTMLInputElement).value;
                })
                .filter(Boolean)[0] || ''
          };
        }
      });
    });
  });

  // Process multifield inputs (text + dropdown combination, or multiple text inputs like time in/out)
  const multifields = section.querySelectorAll('.multifield:not(.hidden)');
  multifields.forEach((multifield) => {
    const label = getFieldLabel(multifield, section);
    if (label) {
      const textInputs = multifield.querySelectorAll(
        'input[type="text"]:not(.hidden)'
      );
      const dropdown = multifield.querySelector('.dropdown');

      // Handle time in/out (two text inputs)
      if (textInputs.length === 2 && !dropdown) {
        const timeInInput = textInputs[0] as HTMLInputElement;
        const timeOutInput = textInputs[1] as HTMLInputElement;

        // Create separate fields for time in and time out
        const timeInKey = getUniqueKey(`${label} - In`, sectionStructure);
        sectionStructure[timeInKey] = {
          Input: (val, replyType) => {
            if (isEmptyOrNull(val)) return;
            timeInInput.focus();
            timeInInput.value = val;
            timeInInput.dispatchEvent(new Event('input', { bubbles: true }));
            timeInInput.dispatchEvent(new Event('change', { bubbles: true }));
          },
          'Existing Value': timeInInput.value || ''
        };

        const timeOutKey = getUniqueKey(`${label} - Out`, sectionStructure);
        sectionStructure[timeOutKey] = {
          Input: (val, replyType) => {
            if (isEmptyOrNull(val)) return;
            timeOutInput.focus();
            timeOutInput.value = val;
            timeOutInput.dispatchEvent(new Event('input', { bubbles: true }));
            timeOutInput.dispatchEvent(new Event('change', { bubbles: true }));
          },
          'Existing Value': timeOutInput.value || ''
        };
      }
      // Handle text + dropdown (like therapy duration)
      else if (textInputs.length === 1 && dropdown) {
        const textInput = textInputs[0] as HTMLInputElement;
        const key = getUniqueKey(label, sectionStructure);
        const hiddenInput = dropdown.querySelector(
          'input.hidden.dropdown-value'
        ) as HTMLInputElement;
        const textInputDropdown = dropdown.querySelector(
          'input.dropdown-text'
        ) as HTMLInputElement;
        const dropdownItems = dropdown.querySelectorAll('.dropdown-item');

        const choices = Array.from(dropdownItems)
          .map((item) => (item as HTMLElement).textContent?.trim())
          .filter((text) => text && text !== '');

        sectionStructure[key] = {
          Description: `Enter the numeric value and unit (e.g., "5 Week(s)"). Available units: ${choices.join(', ')}`,
          Choices: choices.length > 0 ? choices : undefined,
          Input: (val, replyType) => {
            if (isEmptyOrNull(val)) return;

            // Try to parse "5 Week(s)" format
            const match = String(val).match(/^(\d+)\s*(.+)$/);
            if (match) {
              const number = match[1];
              const unit = match[2].trim();

              textInput.value = number;
              textInput.dispatchEvent(new Event('input', { bubbles: true }));
              textInput.dispatchEvent(new Event('change', { bubbles: true }));

              // Find and select matching unit
              const matchingItem = Array.from(dropdownItems).find(
                (item) => (item as HTMLElement).textContent?.trim() === unit
              ) as HTMLElement;

              if (matchingItem) {
                const dataValue =
                  matchingItem.getAttribute('data-value') || unit;
                if (hiddenInput) {
                  hiddenInput.value = dataValue;
                  hiddenInput.dispatchEvent(
                    new Event('change', { bubbles: true })
                  );
                }
                if (textInputDropdown) {
                  textInputDropdown.value = unit;
                  textInputDropdown.dispatchEvent(
                    new Event('input', { bubbles: true })
                  );
                  textInputDropdown.dispatchEvent(
                    new Event('change', { bubbles: true })
                  );
                }
                matchingItem.click();
              }
            } else {
              // Just set the text value
              textInput.value = String(val);
              textInput.dispatchEvent(new Event('input', { bubbles: true }));
              textInput.dispatchEvent(new Event('change', { bubbles: true }));
            }
          },
          'Existing Value':
            `${textInput.value || ''} ${textInputDropdown?.value || ''}`.trim()
        };
      }
    }
  });

  // Note: Dictionary sections are now handled above in the needsEditClick check

  // Process empty uf-field sections (sections with labels but no visible inputs yet)
  // These need edit clicks to show the actual inputs
  if (needsEditClick && !hasVisibleInputs) {
    const ufFields = section.querySelectorAll('.uf-field, .form-field');
    ufFields.forEach((ufField) => {
      const label = ufField
        .querySelector('.uf-field-label, .form-label-text')
        ?.textContent?.trim();
      if (label) {
        // Check if this field type might have inputs after edit
        const fieldType = ufField.classList.contains('userform-text-area-field')
          ? 'textarea'
          : ufField.classList.contains('userform-text-field')
            ? 'text'
            : ufField.classList.contains('userform-dropdown-field')
              ? 'dropdown'
              : ufField.classList.contains('userform-checkboxes-field')
                ? 'checkbox'
                : ufField.classList.contains('userform-radio-field')
                  ? 'radio'
                  : 'unknown';

        if (fieldType !== 'unknown') {
          const key = getUniqueKey(label, sectionStructure);
          sectionStructure[key] = {
            Input: async (val, replyType) => {
              if (isEmptyOrNull(val)) return;

              // Click edit first
              await ensureEditMode();
              await sleep(300);

              // Re-query to find the actual input
              const updatedUfField = section.querySelector(
                `.uf-field:has(.uf-field-label:contains("${label}")), .form-field:has(.form-label-text:contains("${label}"))`
              );
              if (!updatedUfField) {
                // Try finding by label text
                const allUfFields = section.querySelectorAll(
                  '.uf-field, .form-field'
                );
                const matchingField = Array.from(allUfFields).find((field) => {
                  const fieldLabel = (field as HTMLElement)
                    .querySelector('.uf-field-label, .form-label-text')
                    ?.textContent?.trim();
                  return fieldLabel === label;
                }) as HTMLElement;

                if (matchingField) {
                  const textarea = matchingField.querySelector(
                    'textarea'
                  ) as HTMLTextAreaElement;
                  const textInput = matchingField.querySelector(
                    'input[type="text"]'
                  ) as HTMLInputElement;
                  const dropdown = matchingField.querySelector(
                    '.dropdown'
                  ) as HTMLElement;

                  if (textarea) {
                    fillTextareaValue(textarea, val, replyType);
                  } else if (textInput) {
                    fillTextInputValue(textInput, val, replyType);
                  } else if (dropdown) {
                    const dropdownItems =
                      dropdown.querySelectorAll('.dropdown-item');
                    const matchingItem = Array.from(dropdownItems).find(
                      (item) =>
                        (item as HTMLElement).textContent?.trim() === val
                    ) as HTMLElement;
                    if (matchingItem) matchingItem.click();
                  }
                }
              }
            },
            'Existing Value': ''
          };
        }
      }
    });
  }

  // Process userform tables (tables within eval-sections)
  const userformTables = section.querySelectorAll(
    '.userform-table:not(.hidden), .table-form-builder-component:not(.hidden)'
  );
  userformTables.forEach((userformTable) => {
    const table = userformTable.querySelector('.table-root, .table.bulk-body');
    if (!table) return;

    // Skip if table has no editable inputs (will be processed after edit is clicked)
    const hasEditableInputs = table.querySelector(
      'input:not([readonly]):not(.hidden), textarea:not([readonly]):not(.hidden), .dropdown.editable:not(.hidden)'
    );
    if (!hasEditableInputs && !isInlineEdit) {
      return;
    }

    const tableLabel = getFieldLabel(userformTable, section) || 'Table';
    const rows = table.querySelectorAll('.table-row:not(.hidden)');

    rows.forEach((row, rowIndex) => {
      // Skip header rows
      if (
        row.classList.contains('table-header') ||
        row.querySelector('.column-header')
      ) {
        return;
      }

      // Skip rows that have no editable inputs (will be processed after edit is clicked)
      const hasEditableInputsInRow = row.querySelector(
        'input:not([readonly]):not(.hidden), textarea:not([readonly]):not(.hidden), .dropdown.editable:not(.hidden)'
      );
      if (!hasEditableInputsInRow && !isInlineEdit) {
        return;
      }

      // Get row identifier - try multiple strategies
      let rowIdentifier = null;

      // Strategy 1: Look for Name/Measure/Behavior/Activity/Work Performance column
      // First check if it's a dropdown and get its value
      const nameCell = row.querySelector(
        "[data-header='Name'], .table-cell.variant-primary, [data-header='Measure'], [data-header='Behavior'], [data-header='Activity'], [data-header='Title'], [data-header='Position'], [data-header='Movement'], [data-header='Transfer'], [data-header='Type of Activity'], [data-header='Type of Ambulation'], [data-header=' Work Performance']"
      );
      if (nameCell) {
        // Get the header attribute to check against
        const cellHeader = nameCell.getAttribute('data-header')?.trim();

        // Check if it contains a dropdown - get the dropdown value
        const dropdown = nameCell.querySelector('.dropdown');
        if (dropdown) {
          const dropdownText = dropdown.querySelector(
            'input.dropdown-text'
          ) as HTMLInputElement;
          const dropdownValue = dropdownText?.value?.trim();
          if (
            dropdownValue &&
            dropdownValue !== '' &&
            dropdownValue !== '...'
          ) {
            // Make sure the dropdown value is not the same as the header and not a common header name
            const isHeaderValue =
              dropdownValue === 'Name' ||
              dropdownValue === 'Measure' ||
              dropdownValue === 'Activity' ||
              dropdownValue === ' Work Performance' ||
              dropdownValue === 'Title' ||
              dropdownValue === 'Position' ||
              dropdownValue === 'Movement' ||
              dropdownValue === 'Transfer' ||
              dropdownValue === 'Type of Ambulation' ||
              dropdownValue === 'Type of Activity' ||
              dropdownValue === 'Description / Device' ||
              dropdownValue === 'Performance' ||
              dropdownValue === 'Distance' ||
              dropdownValue === cellHeader;
            if (!isHeaderValue) {
              rowIdentifier = dropdownValue;
            }
          }
          // If dropdown is empty, don't use header text - let Strategy 2/3 handle it
        } else {
          // Not a dropdown, use text content (but check it's not a header)
          const cellText = nameCell.textContent?.trim();
          // Don't use header values as identifiers - check both against known headers and the cell's data-header attribute
          if (
            cellText &&
            cellText !== 'Name' &&
            cellText !== 'Measure' &&
            cellText !== 'Activity' &&
            cellText !== ' Work Performance' &&
            cellText !== 'Title' &&
            cellText !== 'Position' &&
            cellText !== 'Movement' &&
            cellText !== 'Transfer' &&
            cellText !== 'Type of Ambulation' &&
            cellText !== 'Type of Activity' &&
            cellText !== 'Description / Device' &&
            cellText !== 'Performance' &&
            cellText !== 'Distance' &&
            cellText !== cellHeader
          ) {
            // Also check against the actual header attribute
            rowIdentifier = cellText;
          }
        }
      }

      // Strategy 2: If no name cell, check first cell for dropdown or text
      // Also check first cell even if it has a data-header (in case Strategy 1 didn't match)
      // Check against common header names to avoid using them
      const isHeaderIdentifier =
        rowIdentifier === 'Name' ||
        rowIdentifier === ' Work Performance' ||
        rowIdentifier === 'Type of Ambulation' ||
        rowIdentifier === 'Type of Activity' ||
        rowIdentifier === 'Measure' ||
        rowIdentifier === 'Activity' ||
        rowIdentifier === 'Title' ||
        rowIdentifier === 'Position' ||
        rowIdentifier === 'Movement' ||
        rowIdentifier === 'Transfer' ||
        rowIdentifier === 'Description / Device' ||
        rowIdentifier === 'Performance' ||
        rowIdentifier === 'Distance';

      if (!rowIdentifier || rowIdentifier === '' || isHeaderIdentifier) {
        // First try cells without data-header
        let firstCell = row.querySelector(
          '.table-cell:not([data-header]), .column-field:not([data-header])'
        ) as HTMLElement;
        // If no match, get the first cell regardless of data-header (but not if it was already processed in Strategy 1)
        if (!firstCell) {
          const allCells = row.querySelectorAll('.table-cell, .column-field');
          firstCell = allCells[0] as HTMLElement;
        }

        if (firstCell) {
          const firstCellHeader = firstCell.getAttribute('data-header')?.trim();

          // Check if first cell has a dropdown
          const firstCellDropdown = firstCell.querySelector('.dropdown');
          if (firstCellDropdown) {
            const dropdownText = firstCellDropdown.querySelector(
              'input.dropdown-text'
            ) as HTMLInputElement;
            const dropdownValue = dropdownText?.value?.trim();
            // Only use dropdown value if it's not empty, not the header, and not a common header name
            const isHeaderValue =
              dropdownValue === 'Name' ||
              dropdownValue === 'Measure' ||
              dropdownValue === 'Activity' ||
              dropdownValue === ' Work Performance' ||
              dropdownValue === 'Title' ||
              dropdownValue === 'Position' ||
              dropdownValue === 'Movement' ||
              dropdownValue === 'Transfer' ||
              dropdownValue === 'Type of Ambulation' ||
              dropdownValue === 'Type of Activity' ||
              dropdownValue === 'Description / Device' ||
              dropdownValue === 'Performance' ||
              dropdownValue === 'Distance' ||
              dropdownValue === firstCellHeader;
            if (
              dropdownValue &&
              dropdownValue !== '' &&
              dropdownValue !== '...' &&
              !isHeaderValue
            ) {
              rowIdentifier = dropdownValue;
            }
            // If dropdown is empty, don't use textContent - skip to Strategy 3
          } else {
            // No dropdown, try text content (but not if it matches the header)
            const firstCellText = firstCell.textContent?.trim();
            // Check if it's not a header value
            const isHeader =
              firstCellText === 'Name' ||
              firstCellText === 'Measure' ||
              firstCellText === 'Activity' ||
              firstCellText === '' ||
              firstCellText === ' Work Performance' ||
              firstCellText === 'Type of Ambulation' ||
              firstCellText === 'Type of Activity' ||
              firstCellText === 'Description / Device' ||
              firstCellText === 'Performance' ||
              firstCellText === 'Distance' ||
              firstCellText === firstCellHeader; // Also check against cell's own header
            if (!isHeader && firstCellText) {
              rowIdentifier = firstCellText;
            }
          }
        }
      }

      // Strategy 3: If still no identifier, use row index (but only for data rows, not header)
      // Also check against common header names to avoid using them as identifiers
      const isHeaderName =
        rowIdentifier === 'Name' ||
        rowIdentifier === ' Work Performance' ||
        rowIdentifier === 'Type of Ambulation' ||
        rowIdentifier === 'Type of Activity' ||
        rowIdentifier === 'Measure' ||
        rowIdentifier === 'Activity' ||
        rowIdentifier === 'Title' ||
        rowIdentifier === 'Position' ||
        rowIdentifier === 'Movement' ||
        rowIdentifier === 'Transfer' ||
        rowIdentifier === 'Description / Device' ||
        rowIdentifier === 'Performance' ||
        rowIdentifier === 'Distance';

      if (!rowIdentifier || rowIdentifier === '' || isHeaderName) {
        // Check if this is actually a data row (has inputs or is not a header row)
        const hasInputs = row.querySelector(
          'input, textarea, .dropdown, .uf-field'
        );
        if (hasInputs) {
          rowIdentifier = `Row ${rowIndex + 1}`;
        } else {
          return; // Skip header rows
        }
      }

      // Final safety check: ensure rowIdentifier is valid and not a header name
      // Re-check isHeaderName with current rowIdentifier value
      const finalHeaderCheck =
        rowIdentifier === 'Name' ||
        rowIdentifier === ' Work Performance' ||
        rowIdentifier === 'Type of Ambulation' ||
        rowIdentifier === 'Type of Activity' ||
        rowIdentifier === 'Measure' ||
        rowIdentifier === 'Activity' ||
        rowIdentifier === 'Title' ||
        rowIdentifier === 'Position' ||
        rowIdentifier === 'Movement' ||
        rowIdentifier === 'Transfer' ||
        rowIdentifier === 'Description / Device' ||
        rowIdentifier === 'Performance' ||
        rowIdentifier === 'Distance';

      if (!rowIdentifier || rowIdentifier === '' || finalHeaderCheck) {
        // Check if this is actually a data row (has inputs or is not a header row)
        const hasInputs = row.querySelector(
          'input, textarea, .dropdown, .uf-field'
        );
        if (hasInputs) {
          rowIdentifier = `Row ${rowIndex + 1}`;
        } else {
          return; // Skip header rows
        }
      }

      // Check if table is inside a subsection and prepend subsection heading to row identifier
      // Try multiple ways to find the subsection - check from various elements in the hierarchy
      let subsection = null;
      let subsectionTitle = null;

      // Try from userformTable (the .userform-table element)
      subsection = userformTable.closest('.uf-subsection');
      if (subsection) {
        const subsectionHeading = subsection.querySelector(
          '.uf-subsection-heading-title'
        );
        subsectionTitle = subsectionHeading?.textContent?.trim();
      }

      // If not found, try from the table element itself
      if (!subsectionTitle) {
        subsection = table.closest('.uf-subsection');
        if (subsection) {
          const subsectionHeading = subsection.querySelector(
            '.uf-subsection-heading-title'
          );
          subsectionTitle = subsectionHeading?.textContent?.trim();
        }
      }

      // If still not found, try from the row
      if (!subsectionTitle) {
        subsection = row.closest('.uf-subsection');
        if (subsection) {
          const subsectionHeading = subsection.querySelector(
            '.uf-subsection-heading-title'
          );
          subsectionTitle = subsectionHeading?.textContent?.trim();
        }
      }

      // If still not found, try from parent elements (more explicit traversal)
      if (!subsectionTitle) {
        let parent = userformTable.parentElement;
        let depth = 0;
        while (parent && parent !== document.body && depth < 10) {
          if (parent.classList && parent.classList.contains('uf-subsection')) {
            subsection = parent as HTMLElement;
            const subsectionHeading = subsection.querySelector(
              '.uf-subsection-heading-title'
            );
            subsectionTitle = subsectionHeading?.textContent?.trim();
            if (subsectionTitle) break;
          }
          // Also check if parent contains uf-subsection-heading-title directly
          const headingInParent = parent.querySelector?.(
            '.uf-subsection-heading-title'
          );
          if (headingInParent) {
            subsectionTitle = headingInParent.textContent?.trim();
            if (subsectionTitle) break;
          }
          parent = parent.parentElement;
          depth++;
        }
      }

      // Final fallback: search the entire section for subsections and match by proximity
      if (!subsectionTitle) {
        const allSubsections = section.querySelectorAll('.uf-subsection');
        for (let i = 0; i < allSubsections.length; i++) {
          const sub = allSubsections[i] as HTMLElement;
          if (
            sub.contains(userformTable) ||
            sub.contains(table) ||
            sub.contains(row)
          ) {
            const subsectionHeading = sub.querySelector(
              '.uf-subsection-heading-title'
            );
            subsectionTitle = subsectionHeading?.textContent?.trim();
            if (subsectionTitle) break;
          }
        }
      }

      // Apply subsection prefix if found
      if (subsectionTitle) {
        rowIdentifier = `${subsectionTitle} - ${rowIdentifier}`;
      }

      // Initialize nested structure for this row
      if (!sectionStructure[rowIdentifier]) {
        sectionStructure[rowIdentifier] = {};
      }

      const cells = row.querySelectorAll(
        '.table-cell:not(.hidden), .column-field:not(.hidden)'
      );

      cells.forEach((cell) => {
        const header =
          cell.getAttribute('data-header') ||
          cell.querySelector('.table-header-content')?.textContent?.trim() ||
          '';
        if (
          !header ||
          header === 'Actions' ||
          header === 'Sort' ||
          header === 'Name'
        ) {
          return;
        }

        // Skip cells that have no editable inputs
        const hasEditableInputsInCell = cell.querySelector(
          'input:not([readonly]):not(.hidden), textarea:not([readonly]):not(.hidden), .dropdown.editable:not(.hidden)'
        );
        if (!hasEditableInputsInCell && !isInlineEdit) {
          return; // Skip cells without editable inputs
        }

        // Check for different input types in the cell
        const ufField = cell.querySelector('.uf-field, .form-field');
        if (!ufField) {
          // If no uf-field but we're in inline-edit, the field might load after edit is clicked
          if (isInlineEdit && !isEditing) {
            sectionStructure[rowIdentifier][header] = {
              Input: async (val, replyType) => {
                if (isEmptyOrNull(val)) return;
                await ensureEditMode();
                await sleep(200);
                // Re-query after edit mode
                const updatedRow = table.querySelector(
                  `.table-row:nth-child(${rowIndex + 1})`
                );
                if (updatedRow) {
                  const updatedCell = updatedRow.querySelector(
                    `td[data-header="${header}"], .column-field[data-header="${header}"]`
                  );
                  const updatedUfField = updatedCell?.querySelector(
                    '.uf-field, .form-field'
                  );
                  if (updatedUfField) {
                    // Process the field type
                    const dropdown = updatedUfField.querySelector('.dropdown');
                    const textInput = updatedUfField.querySelector(
                      'input[type="text"]'
                    ) as HTMLInputElement;
                    const textarea = updatedUfField.querySelector(
                      'textarea'
                    ) as HTMLTextAreaElement;

                    if (dropdown) {
                      const dropdownItems =
                        dropdown.querySelectorAll('.dropdown-item');
                      const matchingItem = Array.from(dropdownItems).find(
                        (item) =>
                          (item as HTMLElement).textContent?.trim() === val
                      ) as HTMLElement;
                      if (matchingItem) matchingItem.click();
                    } else if (textarea) {
                      textarea.value = val;
                      textarea.dispatchEvent(
                        new Event('input', { bubbles: true })
                      );
                      textarea.dispatchEvent(
                        new Event('change', { bubbles: true })
                      );
                    } else if (textInput) {
                      textInput.value = val;
                      textInput.dispatchEvent(
                        new Event('input', { bubbles: true })
                      );
                      textInput.dispatchEvent(
                        new Event('change', { bubbles: true })
                      );
                    }
                  }
                }
              },
              'Existing Value': ''
            };
          }
          return;
        }

        // Check for dropdown field
        const dropdown = ufField.querySelector('.dropdown');
        if (dropdown) {
          const hiddenInput = dropdown.querySelector(
            'input.hidden.dropdown-value'
          ) as HTMLInputElement;
          const textInput = dropdown.querySelector(
            'input.dropdown-text'
          ) as HTMLInputElement;
          const dropdownItems = dropdown.querySelectorAll('.dropdown-item');

          const choices = Array.from(dropdownItems)
            .map((item) => (item as HTMLElement).textContent?.trim())
            .filter((text) => text && text !== '');

          sectionStructure[rowIdentifier][header] = {
            Choices: choices.length > 0 ? choices : undefined,
            Input: async (val, replyType) => {
              if (isEmptyOrNull(val)) return;

              // If inline-edit, ensure we're in edit mode
              if (isInlineEdit && !isEditing) {
                await ensureEditMode();
                await sleep(200);
                // Re-query after edit mode
                const updatedRow = table.querySelector(
                  `.table-row:nth-child(${rowIndex + 1})`
                );
                if (updatedRow) {
                  const updatedCell = updatedRow.querySelector(
                    `td[data-header="${header}"], .column-field[data-header="${header}"]`
                  );
                  const updatedDropdown =
                    updatedCell?.querySelector('.dropdown');
                  if (updatedDropdown) {
                    const updatedDropdownItems =
                      updatedDropdown.querySelectorAll('.dropdown-item');
                    const matchingItem = Array.from(updatedDropdownItems).find(
                      (item) =>
                        (item as HTMLElement).textContent?.trim() === val
                    ) as HTMLElement;
                    if (matchingItem) matchingItem.click();
                    return;
                  }
                }
              }

              const matchingItem = Array.from(dropdownItems).find(
                (item) => (item as HTMLElement).textContent?.trim() === val
              ) as HTMLElement;

              if (matchingItem) {
                const dataValue =
                  matchingItem.getAttribute('data-value') || val;

                if (hiddenInput) {
                  hiddenInput.value = dataValue;
                  hiddenInput.dispatchEvent(
                    new Event('change', { bubbles: true })
                  );
                }

                if (textInput) {
                  textInput.value = val;
                  textInput.dispatchEvent(
                    new Event('input', { bubbles: true })
                  );
                  textInput.dispatchEvent(
                    new Event('change', { bubbles: true })
                  );
                }

                matchingItem.click();
              }
            },
            'Existing Value': textInput?.value || ''
          };
          return;
        }

        // Check for text field
        const textInput = ufField.querySelector(
          'input[type="text"]:not(.hidden)'
        ) as HTMLInputElement;
        if (textInput) {
          sectionStructure[rowIdentifier][header] = {
            Input: async (val, replyType) => {
              if (isEmptyOrNull(val)) return;

              // If inline-edit, ensure we're in edit mode
              if (isInlineEdit && !isEditing) {
                await ensureEditMode();
                await sleep(200);
                // Re-query after edit mode
                const updatedRow = table.querySelector(
                  `.table-row:nth-child(${rowIndex + 1})`
                );
                if (updatedRow) {
                  const updatedCell = updatedRow.querySelector(
                    `td[data-header="${header}"], .column-field[data-header="${header}"]`
                  );
                  const updatedTextInput = updatedCell?.querySelector(
                    'input[type="text"]'
                  ) as HTMLInputElement;
                  if (updatedTextInput) {
                    updatedTextInput.focus();
                    updatedTextInput.value = val;
                    updatedTextInput.dispatchEvent(
                      new Event('input', { bubbles: true })
                    );
                    updatedTextInput.dispatchEvent(
                      new Event('change', { bubbles: true })
                    );
                    return;
                  }
                }
              }

              textInput.focus();
              textInput.value = val;
              textInput.dispatchEvent(new Event('input', { bubbles: true }));
              textInput.dispatchEvent(new Event('change', { bubbles: true }));
            },
            'Existing Value': textInput.value || ''
          };
          return;
        }

        // Check for textarea
        const textarea = ufField.querySelector(
          'textarea'
        ) as HTMLTextAreaElement;
        if (textarea) {
          sectionStructure[rowIdentifier][header] = {
            Input: async (val, replyType) => {
              if (isEmptyOrNull(val)) return;

              // If inline-edit, ensure we're in edit mode
              if (isInlineEdit && !isEditing) {
                await ensureEditMode();
                await sleep(200);
                // Re-query after edit mode
                const updatedRow = table.querySelector(
                  `.table-row:nth-child(${rowIndex + 1})`
                );
                if (updatedRow) {
                  const updatedCell = updatedRow.querySelector(
                    `td[data-header="${header}"], .column-field[data-header="${header}"]`
                  );
                  const updatedTextarea = updatedCell?.querySelector(
                    'textarea'
                  ) as HTMLTextAreaElement;
                  if (updatedTextarea) {
                    fillTextareaValue(updatedTextarea, val, replyType);
                    return;
                  }
                }
              }

              fillTextareaValue(textarea, val, replyType);
            },
            'Existing Value': textarea.value || ''
          };
          return;
        }

        // Check for radio buttons (both actual radio inputs and checkboxes styled as radio in "options single")
        const isRadioField = ufField.classList.contains('userform-radio-field');
        const optionsSingle = ufField.querySelector('.options.single');
        const radioInputs = ufField.querySelectorAll('input[type="radio"]');
        const checkboxRadioInputs = optionsSingle
          ? ufField.querySelectorAll(
              'input[type="checkbox"].options-item-value'
            )
          : [];

        if (
          isRadioField &&
          (radioInputs.length > 0 || checkboxRadioInputs.length > 0)
        ) {
          // Use checkbox inputs if they exist (styled as radio), otherwise use actual radio inputs
          const inputsToUse =
            checkboxRadioInputs.length > 0 ? checkboxRadioInputs : radioInputs;

          const choices = Array.from(inputsToUse)
            .map((input) => {
              const label = (input as HTMLElement)
                .closest('.uf-field-value, .options-item')
                ?.querySelector('.options-item-text')
                ?.textContent?.trim();
              return label || (input as HTMLInputElement).value;
            })
            .filter(Boolean);

          sectionStructure[rowIdentifier][header] = {
            Choices: choices.length > 0 ? choices : undefined,
            Input: async (val, replyType) => {
              if (isEmptyOrNull(val)) return;

              // If inline-edit, ensure we're in edit mode
              if (isInlineEdit && !isEditing) {
                await ensureEditMode();
                await sleep(200);
                // Re-query after edit mode
                const updatedRow = table.querySelector(
                  `.table-row:nth-child(${rowIndex + 1})`
                );
                if (updatedRow) {
                  const updatedCell = updatedRow.querySelector(
                    `td[data-header="${header}"], .column-field[data-header="${header}"]`
                  );
                  const updatedUfField = updatedCell?.querySelector(
                    '.uf-field.userform-radio-field'
                  );
                  if (updatedUfField) {
                    const updatedOptionsSingle =
                      updatedUfField.querySelector('.options.single');
                    const updatedInputs = updatedOptionsSingle
                      ? updatedUfField.querySelectorAll(
                          'input[type="checkbox"].options-item-value'
                        )
                      : updatedUfField.querySelectorAll('input[type="radio"]');

                    const matchingInput = Array.from(updatedInputs).find(
                      (input) => {
                        const label = (input as HTMLElement)
                          .closest('.uf-field-value, .options-item')
                          ?.querySelector('.options-item-text')
                          ?.textContent?.trim();
                        return (
                          label === val ||
                          (input as HTMLInputElement).value === val
                        );
                      }
                    ) as HTMLInputElement;

                    if (matchingInput) {
                      // For radio buttons, uncheck all others first
                      if (updatedOptionsSingle) {
                        Array.from(updatedInputs).forEach((input) => {
                          (input as HTMLInputElement).checked = false;
                        });
                      }
                      matchingInput.checked = true;
                      matchingInput.dispatchEvent(
                        new Event('change', { bubbles: true })
                      );
                    }
                    return;
                  }
                }
              }

              const matchingInput = Array.from(inputsToUse).find((input) => {
                const label = (input as HTMLElement)
                  .closest('.uf-field-value, .options-item')
                  ?.querySelector('.options-item-text')
                  ?.textContent?.trim();
                return (
                  label === val || (input as HTMLInputElement).value === val
                );
              }) as HTMLInputElement;

              if (matchingInput) {
                // For radio buttons, uncheck all others first
                if (optionsSingle) {
                  Array.from(inputsToUse).forEach((input) => {
                    (input as HTMLInputElement).checked = false;
                  });
                }
                matchingInput.checked = true;
                matchingInput.dispatchEvent(
                  new Event('change', { bubbles: true })
                );
              }
            },
            'Existing Value':
              Array.from(inputsToUse)
                .filter((r) => (r as HTMLInputElement).checked)
                .map((r) => {
                  const label = (r as HTMLElement)
                    .closest('.uf-field-value, .options-item')
                    ?.querySelector('.options-item-text')
                    ?.textContent?.trim();
                  return label || (r as HTMLInputElement).value;
                })
                .filter(Boolean)[0] || ''
          };
        }
      });
    });
  });
}

async function handleObjectivesGoalsSection() {
  const objectivesSectionTitle = 'Goal / Objective Data (for this session)';

  // Find the section by title
  const sections = Array.from(
    document.querySelectorAll('.flow-section')
  ) as HTMLElement[];
  const objectivesSection = sections.find((sec) => {
    const title = sec.querySelector('.flow-section-title')?.textContent?.trim();
    return title === objectivesSectionTitle;
  });

  if (!objectivesSection) {
    return;
  }

  // Ensure we're looking at the active editing section (Fusion can re-render a new instance)
  const activeObjectivesSection =
    (await ensureSectionInEditModeAndGetActive(
      objectivesSection,
      objectivesSectionTitle
    )) || objectivesSection;

  // Find form/table body (may live directly in section or inside the form)
  const objectivesForm =
    (activeObjectivesSection.querySelector(
      '.objectives-form'
    ) as HTMLElement | null) ||
    (objectivesSection.querySelector('.objectives-form') as HTMLElement | null);
  let tableBody =
    (activeObjectivesSection.querySelector(
      '.table-body'
    ) as HTMLElement | null) ||
    (objectivesForm
      ? (objectivesForm.querySelector('.table-body') as HTMLElement | null)
      : null);

  if (!tableBody) {
    return;
  }

  const originalElement = objectivesForm || activeObjectivesSection;
  const rows = Array.from(tableBody.querySelectorAll('.table-row'));

  const structure: any = {};
  let currentCategory: string | null = null;
  let hasProcessedAnyRow = false;

  const escapeAttrValue = (v: string) => {
    // @ts-ignore
    const esc = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape : null;
    if (esc) return esc(v);
    return v.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  };

  const findSaveButtonInSection = (
    sec: HTMLElement
  ): HTMLButtonElement | null => {
    const byDataFire = sec.querySelector(
      'button[data-fire="~save"]'
    ) as HTMLButtonElement | null;
    if (byDataFire) return byDataFire;
    const allButtons = Array.from(
      sec.querySelectorAll('button')
    ) as HTMLButtonElement[];
    return (
      allButtons.find((b) =>
        (b.textContent || '').toLowerCase().includes('save')
      ) || null
    );
  };

  const getActiveObjectivesSection = async (): Promise<HTMLElement | null> => {
    try {
      // Re-find section by title each time (DOM may change after save/render)
      const sections = Array.from(
        document.querySelectorAll('.flow-section')
      ) as HTMLElement[];
      const currentSection = sections.find((sec) => {
        const title = sec
          .querySelector('.flow-section-title')
          ?.textContent?.trim();
        return title === objectivesSectionTitle;
      });
      if (!currentSection) return null;
      return await ensureSectionInEditModeAndGetActive(
        currentSection,
        objectivesSectionTitle
      );
    } catch (e) {
      return objectivesSection;
    }
  };

  rows.forEach((row) => {
    // Skip graph rows
    if (row.querySelector('.renderable[data-context*="graph"]')) {
      return;
    }

    const isLongTerm = row.classList.contains('long-term');
    const isShortTerm = row.classList.contains('short-term');
    const isHeadingRow = row.classList.contains('heading-row');

    // Get the objective ID from the input name
    const valueInput = row.querySelector(
      "input[name^='objectives['], input.hidden.dropdown-value"
    ) as HTMLInputElement | null;
    const commentTextarea = row.querySelector(
      "textarea[name^='objectives[']"
    ) as HTMLTextAreaElement | null;

    const descriptionCell = row.querySelector("[data-header='Description']");
    let goalDescription = '';

    if (isLongTerm && isHeadingRow) {
      if (descriptionCell) {
        const dynamicItems = descriptionCell.querySelectorAll('.dynamic-item');
        if (dynamicItems.length >= 2) {
          currentCategory = dynamicItems[0].textContent.trim();
          const desc = dynamicItems[1].textContent.trim();
          goalDescription = desc.replace(/^Long-Term:\s*/i, '').trim();
          if (!structure[currentCategory]) {
            structure[currentCategory] = {};
          }
        }
      }
    } else if (isShortTerm) {
      if (descriptionCell) {
        const dynamicItems = descriptionCell.querySelectorAll('.dynamic-item');
        if (dynamicItems.length > 0) {
          const desc = dynamicItems[dynamicItems.length - 1].textContent.trim();
          goalDescription = desc.replace(/^Short-Term:\s*/i, '').trim();
        }
      }
    }

    if (!currentCategory) return;

    if ((valueInput || commentTextarea) && goalDescription) {
      hasProcessedAnyRow = true;

      if (valueInput) {
        const htmlInput = valueInput as HTMLInputElement;
        const inputName = htmlInput.getAttribute('name') || '';
        const valueInputType =
          htmlInput.type || htmlInput.tagName.toLowerCase();
        const fieldKey = `${goalDescription} - Value`;

        if (
          htmlInput.classList.contains('dropdown-value') ||
          htmlInput.classList.contains('hidden')
        ) {
          const dropdown = row.querySelector('.dropdown') as HTMLElement | null;
          if (dropdown) {
            const dropdownItems = dropdown.querySelectorAll('.dropdown-item');
            const options = Array.from(dropdownItems)
              .map((item) => item.textContent.trim())
              .filter((text) => text && text !== '');

            structure[currentCategory][fieldKey] = {
              Choices: options,
              Input: async (value, replyType) => {
                if (isEmptyOrNull(value)) return;
                const activeSection = await getActiveObjectivesSection();
                const activeInput =
                  inputName && activeSection
                    ? (activeSection.querySelector(
                        `input[name="${escapeAttrValue(inputName)}"]`
                      ) as HTMLInputElement | null)
                    : htmlInput;
                if (!activeInput) return;

                const activeRow = activeInput.closest(
                  '.table-row'
                ) as HTMLElement | null;
                const activeDropdown =
                  (activeRow?.querySelector(
                    '.dropdown'
                  ) as HTMLElement | null) || dropdown;
                if (!activeDropdown) return;

                const activeItems =
                  activeDropdown.querySelectorAll('.dropdown-item');
                const matchingItem = Array.from(activeItems).find(
                  (item) => item.textContent.trim() === String(value).trim()
                ) as HTMLElement | undefined;
                if (!matchingItem) return;

                const dataValue = matchingItem.getAttribute('data-value');
                // Open dropdown and click the matching item (Fusion often updates internal state on click)
                const dropdownTextEl = activeDropdown.querySelector(
                  '.dropdown-text'
                ) as HTMLInputElement | null;
                const dropdownHandleEl = activeDropdown.querySelector(
                  '.dropdown-handle'
                ) as HTMLElement | null;
                (
                  dropdownTextEl ||
                  dropdownHandleEl ||
                  activeDropdown
                ).dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
                (
                  dropdownTextEl ||
                  dropdownHandleEl ||
                  activeDropdown
                ).dispatchEvent(new MouseEvent('click', { bubbles: true }));

                matchingItem.dispatchEvent(
                  new MouseEvent('mousedown', { bubbles: true })
                );
                matchingItem.dispatchEvent(
                  new MouseEvent('click', { bubbles: true })
                );

                // Ensure hidden input reflects the selected data-value
                activeInput.value = dataValue || '';
                activeInput.dispatchEvent(
                  new Event('input', { bubbles: true })
                );
                activeInput.dispatchEvent(
                  new Event('change', { bubbles: true })
                );

                if (dropdownTextEl) {
                  dropdownTextEl.value = String(value);
                  dropdownTextEl.dispatchEvent(
                    new Event('input', { bubbles: true })
                  );
                  dropdownTextEl.dispatchEvent(
                    new Event('change', { bubbles: true })
                  );
                }
              }
            };
          }
        } else if (valueInputType === 'text' || valueInputType === 'input') {
          const valueCell = row.querySelector("[data-header='Value']");
          let unit = '';
          if (valueCell) {
            const unitElement = valueCell.querySelector('.dynamic-item');
            if (unitElement) {
              unit = unitElement.textContent.trim();
            }
          }

          structure[currentCategory][fieldKey] = {
            ...(unit && {
              Description: `Enter only the numeric value; the unit (${unit}) will be applied automatically.`
            }),
            Input: async (value, replyType) => {
              if (isEmptyOrNull(value)) return;
              const activeSection = await getActiveObjectivesSection();
              const activeInput =
                inputName && activeSection
                  ? (activeSection.querySelector(
                      `input[name="${escapeAttrValue(inputName)}"]`
                    ) as HTMLInputElement | null)
                  : htmlInput;
              if (!activeInput || (activeInput as any).disabled) return;

              if (replyType === 'append') {
                activeInput.value =
                  `${activeInput.value || ''}\n${value}`.trim();
              } else {
                activeInput.value = String(value);
              }
              activeInput.dispatchEvent(new Event('input', { bubbles: true }));
              activeInput.dispatchEvent(new Event('change', { bubbles: true }));
            }
          };
        }
      }

      if (commentTextarea) {
        const htmlTextarea = commentTextarea as HTMLTextAreaElement;
        const textareaName = htmlTextarea.getAttribute('name') || '';
        const fieldKey = `${goalDescription} - Comment`;

        structure[currentCategory][fieldKey] = {
          Input: async (value, replyType) => {
            if (isEmptyOrNull(value)) return;
            const activeSection = await getActiveObjectivesSection();
            const activeTextarea =
              textareaName && activeSection
                ? (activeSection.querySelector(
                    `textarea[name="${escapeAttrValue(textareaName)}"]`
                  ) as HTMLTextAreaElement | null)
                : htmlTextarea;
            if (!activeTextarea || (activeTextarea as any).disabled) return;

            if (replyType === 'append') {
              activeTextarea.value =
                `${activeTextarea.value || ''}\n${value}`.trim();
            } else {
              activeTextarea.value = String(value);
            }
            activeTextarea.dispatchEvent(new Event('input', { bubbles: true }));
            activeTextarea.dispatchEvent(
              new Event('change', { bubbles: true })
            );
          }
        };
      }
    }
  });

  const hasAnyFields = Object.values(structure).some(
    (cat: any) => cat && typeof cat === 'object' && Object.keys(cat).length > 0
  );

  if (hasProcessedAnyRow && hasAnyFields) {
    questionInteractions['Goal / Objective Data (for this session)'] = {
      Type: 'Flex',
      Key: objectivesSectionTitle,
      Question: objectivesSectionTitle,
      Structure: structure,
      OriginalElement: originalElement
    };
  }

  // Close the section after scan so we operate on high-level tab content
  await closeGoalObjectiveDataSection();
}

/** Close Goal / Objective Data section if it's in edit mode (click Save to exit). */
async function closeGoalObjectiveDataSection() {
  const title = 'Goal / Objective Data (for this session)';
  const sections = Array.from(
    document.querySelectorAll('.flow-section')
  ) as HTMLElement[];
  const section = sections.find((sec) => {
    const t = sec.querySelector('.flow-section-title')?.textContent?.trim();
    return t === title && sec.offsetParent !== null;
  });
  if (!section) return;

  const isEditing =
    section.classList.contains('editing') ||
    section.classList.contains('eval-section-edit');
  if (!isEditing) return;

  const saveBtn = section.querySelector(
    'button[data-fire="~save"]'
  ) as HTMLButtonElement | null;
  if (saveBtn && saveBtn.offsetParent) {
    saveBtn.click();
    await sleep(200);
  }
}

// Shared helpers for step/section handling
function findFlowStepByTitle(
  flowBody: HTMLElement,
  title: string
): HTMLElement | null {
  const flowSteps = Array.from(
    flowBody.querySelectorAll('.flow-step')
  ) as HTMLElement[];

  return (
    flowSteps.find((flowStep) => {
      const stepTitleElement = flowStep.querySelector(
        '.flow-step-title, .flow-step-header .flow-step-title'
      );
      const stepTitle = stepTitleElement?.textContent?.trim();
      return stepTitle === title;
    }) || null
  );
}

async function ensureSectionInEditModeAndGetActive(
  section: HTMLElement,
  sectionTitle: string
): Promise<HTMLElement | null> {
  let activeSection: HTMLElement | null = section;

  const alreadyEditing =
    section.classList.contains('editing') ||
    section.classList.contains('eval-section-edit');
  const editButton = section.querySelector(
    'button[data-fire="~edit"], button.edit-button[data-fire="~edit"]'
  ) as HTMLElement | null;

  if (!alreadyEditing && editButton) {
    editButton.click();

    // Wait for section to enter edit mode and inputs to appear
    let attempts = 0;
    while (attempts < 20) {
      await sleep(100);

      // After clicking edit, Fusion may render a new .flow-section.editing
      // instance; prefer that as the active section.
      const possibleSections = Array.from(
        document.querySelectorAll(
          '.flow-section.editing, .flow-section.eval-section-edit'
        )
      ) as HTMLElement[];
      const matching = possibleSections.find((sec) => {
        const t = sec.querySelector(
          '.flow-section-title'
        ) as HTMLElement | null;
        return t?.textContent?.trim() === sectionTitle;
      });
      if (matching) {
        activeSection = matching;
      }

      const hasInputs = activeSection?.querySelector('textarea, input, form');
      if (
        hasInputs ||
        activeSection?.classList.contains('editing') ||
        activeSection?.classList.contains('eval-section-edit')
      ) {
        break;
      }
      attempts++;
    }
  }

  return activeSection;
}

async function fillLabeledTextareaAndSave(
  section: HTMLElement | null,
  targetLabel: string,
  value: any,
  replyType?: string
) {
  if (!section) return;
  if (isEmptyOrNull(value)) return;

  const updatedField = Array.from(
    section.querySelectorAll(
      '.form-field.uf-field.userform-text-area-field, .userform-text-area-field'
    )
  ).find((field) => {
    const labelEl = field.querySelector(
      '.uf-field-label, .form-label-text'
    ) as HTMLElement | null;
    const labelText = labelEl?.textContent?.trim();
    return labelText === targetLabel;
  }) as HTMLElement | undefined;

  if (!updatedField) return;

  const textarea = updatedField.querySelector(
    'textarea'
  ) as HTMLTextAreaElement | null;
  if (!textarea) return;

  fillTextareaValue(textarea, value, replyType);

  // Click Save to commit, if a save button exists in this section
  const saveButton =
    (section.querySelector(
      'button[data-fire="~save"]'
    ) as HTMLButtonElement | null) ||
    (
      Array.from(section.querySelectorAll('button')) as HTMLButtonElement[]
    ).find((b) => (b.textContent || '').toLowerCase().includes('save')) ||
    null;

  if (saveButton) {
    saveButton.click();
    let attempts = 0;
    while (attempts < 20) {
      await sleep(100);
      const stillEditing =
        section.classList.contains('editing') ||
        section.classList.contains('eval-section-edit');
      if (!stillEditing) break;
      attempts++;
    }
  }
}

function handleFindingsSection(flowBody: HTMLElement) {
  const findingsStep = findFlowStepByTitle(flowBody, 'Findings');

  if (!findingsStep) {
    return;
  }

  // This structure will be used as the Flex "Structure" for the Findings step.
  // We keep a single top-level key "Findings", and inside that we store one
  // entry per section, e.g. "Balance - Additional Comments".
  const structure: any = {
    Findings: {}
  };

  const flowSections = findingsStep.querySelectorAll(
    '.flow-section'
  ) as NodeListOf<HTMLElement>;

  flowSections.forEach((section) => {
    try {
      // Skip hidden sections
      if (section.classList.contains('hidden') || !section.offsetParent) {
        return;
      }

      const sectionTitleElement = section.querySelector('.flow-section-title');
      const sectionTitle =
        sectionTitleElement?.textContent?.trim() || 'Findings';

      // Find the "Additional Comments" textarea container in this section
      const additionalCommentsField = Array.from(
        section.querySelectorAll(
          '.form-field.uf-field.userform-text-area-field, .userform-text-area-field'
        )
      ).find((field) => {
        const labelEl = field.querySelector(
          '.uf-field-label, .form-label-text'
        ) as HTMLElement | null;
        const labelText = labelEl?.textContent?.trim();
        return labelText === 'Additional Comments';
      }) as HTMLElement | undefined;

      if (!additionalCommentsField) {
        return;
      }

      const fieldKey = `${sectionTitle} - Additional Comments`;

      // Avoid duplicate registration inside the Findings structure
      if (structure.Findings[fieldKey]) {
        return;
      }

      // Under the "Findings" group, add this section's Additional Comments
      structure.Findings[fieldKey] = {
        Input: async (val: any, replyType?: string) => {
          const activeSection = await ensureSectionInEditModeAndGetActive(
            section,
            sectionTitle
          );
          await fillLabeledTextareaAndSave(
            activeSection,
            'Additional Comments',
            val,
            replyType
          );
        },
        'Existing Value': ''
      };
    } catch (e) {
      console.error('Error in handleFindingsSection section processing:', e);
    }
  });

  // Only create the Flex question if we found any Additional Comments fields
  if (Object.keys(structure.Findings).length > 0) {
    questionInteractions['Findings'] = {
      Type: 'Flex',
      Key: 'Findings',
      Question: 'Findings',
      Structure: structure,
      OriginalElement: findingsStep
    };
  }
}

function handleFunctionalMeasuresSection(flowBody: HTMLElement) {
  const fmStep = findFlowStepByTitle(flowBody, 'Functional Measures');

  if (!fmStep) {
    return;
  }

  const structure: any = {
    'Functional Measures': {}
  };

  const flowSections = fmStep.querySelectorAll(
    '.flow-section'
  ) as NodeListOf<HTMLElement>;

  flowSections.forEach((section) => {
    try {
      if (section.classList.contains('hidden') || !section.offsetParent) {
        return;
      }

      const sectionTitleElement = section.querySelector('.flow-section-title');
      const sectionTitle =
        sectionTitleElement?.textContent?.trim() || 'Functional Measures';

      // Find all textarea fields in this section whose label is a
      // "comment/notes" style field – we only want these summary fields,
      // not the table cells.
      const commentFields = Array.from(
        section.querySelectorAll(
          '.form-field.uf-field.userform-text-area-field, .userform-text-area-field'
        )
      ).filter((field) => {
        const labelEl = field.querySelector(
          '.uf-field-label, .form-label-text'
        ) as HTMLElement | null;
        const labelText = labelEl?.textContent?.trim() || '';
        const lower = labelText.toLowerCase();

        // Match labels like:
        // - Notes / Observations
        // - Comments
        // - Stationary Comments, Movement Comments, Transfer Comments, etc.
        return (
          lower.includes('comment') ||
          lower.includes('notes / observations') ||
          lower.startsWith('notes /')
        );
      }) as HTMLElement[];

      if (commentFields.length === 0) {
        return;
      }

      commentFields.forEach((field) => {
        const labelEl = field.querySelector(
          '.uf-field-label, .form-label-text'
        ) as HTMLElement | null;
        const fieldLabel = labelEl?.textContent?.trim() || 'Comments';
        const fieldKey = `${sectionTitle} - ${fieldLabel}`;

        if (structure['Functional Measures'][fieldKey]) {
          return;
        }

        structure['Functional Measures'][fieldKey] = {
          Input: async (val: any, replyType?: string) => {
            const activeSection = await ensureSectionInEditModeAndGetActive(
              section,
              sectionTitle
            );
            await fillLabeledTextareaAndSave(
              activeSection,
              fieldLabel,
              val,
              replyType
            );
          },
          'Existing Value': ''
        };
      });
    } catch (e) {
      console.error(
        'Error in handleFunctionalMeasuresSection section processing:',
        e
      );
    }
  });

  if (Object.keys(structure['Functional Measures']).length > 0) {
    questionInteractions['Functional Measures'] = {
      Type: 'Flex',
      Key: 'Functional Measures',
      Question: 'Functional Measures',
      Structure: structure,
      OriginalElement: fmStep
    };
  }
}
// Follow handleObjectivesGoalsSection textarea filling pattern
const fillTextareaValue = (
  ta: HTMLTextAreaElement | null,
  val: any,
  replyType?: string
) => {
  if (!ta) return;
  if ((ta as any).disabled) return;
  if (isEmptyOrNull(val)) return;

  const strVal = String(val);
  if (replyType === 'append') {
    ta.value = `${ta.value || ''}\n${strVal}`.trim();
  } else {
    ta.value = strVal;
  }

  ta.dispatchEvent(new Event('input', { bubbles: true }));
  ta.dispatchEvent(new Event('change', { bubbles: true }));
};

const fillTextInputValue = (
  input: HTMLInputElement | null,
  val: any,
  replyType?: string
) => {
  if (!input) return;
  if (input.disabled) return;
  if (isEmptyOrNull(val)) return;

  input.value = String(val);
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
};

/** Fill a section's textarea by section title (e.g. Plan of Treatment). Used when field is not in scraped structure. */
async function fillSectionTextareaByTitle(
  sectionTitle: string,
  value: string,
  replyType = 'overwrite'
) {
  const norm = (s: string) =>
    (s || '').replace(/\s+/g, ' ').trim().toLowerCase();
  const sections = Array.from(
    document.querySelectorAll('.flow-section')
  ) as HTMLElement[];
  const section = sections.find((sec) => {
    const title =
      sec.querySelector('.flow-section-title')?.textContent?.trim() || '';
    return sec.offsetParent !== null && norm(title) === norm(sectionTitle);
  });
  if (!section) return;

  const isEditing = section.classList.contains('editing');
  const editBtn = section.querySelector(
    'button[data-fire="~edit"], button.edit-button'
  ) as HTMLButtonElement;
  if (!isEditing && editBtn) {
    editBtn.click();
    await sleep(400);
  }

  const textarea = section.querySelector(
    'textarea.flow-editor-value, textarea.textbox, .uf-field textarea, textarea'
  ) as HTMLTextAreaElement;
  if (textarea && !textarea.disabled) {
    fillTextareaValue(textarea, value, replyType);
    const saveBtn = section.querySelector(
      'button[data-fire="~save"]'
    ) as HTMLButtonElement;
    if (saveBtn && saveBtn.offsetParent) {
      saveBtn.click();
      await sleep(200);
    }
  }
}

function getFieldLabel(element, section) {
  // Try to find label in various ways
  let label = null;

  // Check for uf-field-label
  const fieldLabel = element
    .closest('.form-field, .uf-field')
    ?.querySelector('.uf-field-label, .form-label-text');
  if (fieldLabel) {
    label = fieldLabel.textContent?.trim();
    if (label) return label;
  }

  // Check for data-header attribute
  const header = element.closest('[data-header]')?.getAttribute('data-header');
  if (header) {
    label = header;
    if (label) return label;
  }

  // Check parent for label
  const parent = element.parentElement;
  if (parent) {
    const parentLabel = parent.querySelector(
      '.uf-field-label, .form-label-text, [data-header]'
    );
    if (parentLabel) {
      label =
        parentLabel.textContent?.trim() ||
        parentLabel.getAttribute('data-header');
      if (label) return label;
    }
  }

  // Fallback to section title
  const sectionTitle = section.querySelector(
    '.flow-section-title, .flow-step-title'
  );
  if (sectionTitle) {
    return sectionTitle.textContent?.trim();
  }

  return 'Field';
}

function getUniqueKey(baseKey, existingStructure) {
  if (!existingStructure[baseKey]) {
    return baseKey;
  }

  let counter = 1;
  let uniqueKey = `${baseKey} (${counter})`;
  while (existingStructure[uniqueKey]) {
    counter++;
    uniqueKey = `${baseKey} (${counter})`;
  }
  return uniqueKey;
}

function isInsideTable(element) {
  // Comprehensive check to see if element is inside any table structure
  return !!element.closest(
    '.table-cell, .column-field, .table-row, .userform-table, .table-form-builder-component, .table-root, .table.bulk-body, .table-container, .table-body, .table-header, .table-form-builder, .table'
  );
}

function getSubsectionTitle(element) {
  // Check if element is inside a subsection
  const subsection = element.closest('.uf-subsection');
  if (subsection) {
    const subsectionHeading = subsection.querySelector(
      '.uf-subsection-heading-title'
    );
    const subsectionTitle = subsectionHeading?.textContent?.trim();
    return subsectionTitle || null;
  }
  return null;
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isEmptyOrNull(value) {
  return (
    value === null || value === undefined || value === '' || value === 'None'
  );
}

/**
 * Generic helper for sections that have a "Name" column + textarea column table:
 *  - Click the appropriate "Edit ..." button once
 *  - Wait for the target flow section title (e.g. "History Items")
 *  - Fill all rows by matching the Name cell to keys in rowAnswers
 *  - Click Save once at the end
 */
// Configuration constants for table filling
const TABLE_FILL_CONFIG = {
  // Timeouts and delays (in milliseconds) - Optimized for speed
  delays: {
    fieldFill: 5,
    fieldFillShort: 0,
    sectionWait: 50,
    saveWait: 100,
    maxWaitIterations: 20,
    scrollDelay: 0
  },

  // Row identifier header names (in order of priority)
  rowIdentifierHeaders: [
    'Name',
    'Measure',
    'Description',
    'Activity',
    'Behavior',
    'Title',
    'Position'
  ],

  // Textarea/notes column header names (in order of priority)
  textareaHeaders: [
    'Findings',
    'Text',
    'Comments',
    'Notes / Observations',
    'Notes',
    'Observations'
  ],

  // Score column header
  scoreHeader: 'Score',

  // CSS selectors (in order of priority)
  selectors: {
    rows: ['.table-row[data-id]', '.table-body .table-row', '.table-row'],
    textareas: [
      'textarea.expander-input[name^="items["]',
      '.userform-text-area-field textarea',
      'textarea'
    ],
    textareaCells: ['.table-cell.editor-cell', '.table-cell'],
    inputFields: ['input.textbox', 'input[type="text"]', 'textarea'],
    sections: '.flow-section',
    sectionTitle: '.flow-section-title',
    saveButton: 'button[data-fire="~save"]',
    editButton: 'button[data-fire="~edit"]',
    tableCells: '.table-cell',
    textareaField: '.userform-text-area-field',
    expanderPreview: '.expander-text',
    headerContent: '.table-header-content'
  },

  // Question type matchers
  questionTypes: {
    goals: ['goal', 'goals', 'included goals & notes'],
    includedGoals: ['included goals & notes', 'included goals'],
    tests: ['tests & measures', 'tests and measures'],
    findings: ['findings'],
    functionalMeasures: ['functional measures', 'functional measure'],
    homeActivities: ['assigned home activities', 'home activities'],
    therapyActivities: ['therapy activities', 'therapy activities / exercises']
  },

  // Button text matchers
  buttonText: {
    edit: ['edit', 'enter'],
    save: ['save'],
    enterTestData: ['enter test data']
  }
};

const matchesAny = (text: string, patterns: string[]): boolean => {
  return patterns.some((pattern) => text.includes(pattern));
};

/** Activities table (Home Activities, Therapy Activities): direct fill - Name, Reps, Sets, Comments. */
async function fillActivitiesTableDirect(
  questionTitle: string,
  sectionTitle: string,
  columnAnswers: Record<string, Record<string, string>>,
  sleepMs: (ms: number) => Promise<unknown>,
  norm: (s: string | null | undefined) => string
): Promise<string | null> {
  const sectionNorm = norm(sectionTitle);
  const isHomeActivities = matchesAny(
    sectionNorm,
    TABLE_FILL_CONFIG.questionTypes.homeActivities
  );
  const isTherapyActivities = matchesAny(
    sectionNorm,
    TABLE_FILL_CONFIG.questionTypes.therapyActivities
  );

  // Ensure the flow tab is selected so the section is visible.
  // Assigned Home Activities lives under Plan; Therapy Activities may be under Plan, Treatment, Intervention, etc.
  const tabTitle =
    matchesAny(
      norm(questionTitle),
      TABLE_FILL_CONFIG.questionTypes.homeActivities
    ) ||
    matchesAny(
      norm(sectionTitle),
      TABLE_FILL_CONFIG.questionTypes.homeActivities
    )
      ? 'Plan'
      : questionTitle;
  await ensureFlowTabSelected(tabTitle);
  await sleepMs(150);

  const findSection = (): HTMLElement | null => {
    const sections = Array.from(
      document.querySelectorAll('.flow-section')
    ) as HTMLElement[];
    let found = sections.find((sec) => {
      const title = sec.querySelector('.flow-section-title')?.textContent || '';
      const t = norm(title);
      if (!sec.offsetParent) return false;
      if (isHomeActivities) {
        return t === 'assigned home activities' || t === 'home activities';
      }
      if (isTherapyActivities) {
        return (
          t === 'therapy activities' ||
          t === 'therapy activities / exercises' ||
          (t.includes('therapy') && t.includes('activities'))
        );
      }
      return t === sectionNorm;
    });
    if (!found && (isHomeActivities || isTherapyActivities)) {
      found =
        sections.find((sec) => {
          if (!sec.offsetParent) return false;
          const hasActivitiesTable =
            sec.querySelector('[data-header="Reps"]') &&
            sec.querySelector('[data-header="Sets"]') &&
            sec.querySelector('[data-header="Comments"]');
          const title =
            sec.querySelector('.flow-section-title')?.textContent || '';
          const t = norm(title);
          if (isTherapyActivities) {
            return (
              hasActivitiesTable &&
              t.includes('therapy') &&
              t.includes('activities')
            );
          }
          return hasActivitiesTable;
        }) || null;
    }
    return found || null;
  };

  let target = findSection();
  if (!target && isTherapyActivities) {
    // Therapy Activities may be under a different tab (Treatment, Intervention, etc.)
    const tabs = Array.from(
      document.querySelectorAll('.flow-control .flow-tab')
    ) as HTMLElement[];
    const tabNamesToTry = [
      'treatment',
      'intervention',
      'activities',
      'clinical',
      'objective',
      'assessment'
    ];
    for (const tabName of tabNamesToTry) {
      const tab = tabs.find((t) => {
        const text = (t.textContent || '').trim().toLowerCase();
        return text.includes(tabName) || tabName.includes(text);
      });
      if (tab && tab.offsetParent !== null) {
        const isActive =
          tab.classList.contains('active') ||
          tab.classList.contains('selected') ||
          tab.getAttribute('aria-selected') === 'true';
        if (!isActive) {
          tab.click();
          await sleepMs(300);
        }
        target = findSection();
        if (target) break;
      }
    }
  }
  if (!target) {
    // Retry: section may still be mounting after tab switch
    for (let i = 0; i < 5; i++) {
      await sleepMs(100);
      target = findSection();
      if (target) break;
    }
  }
  if (!target) return 'Target section not found';

  try {
    target.scrollIntoView({ block: 'center', behavior: 'auto' });
  } catch {}
  await sleepMs(50);

  const editBtn = target.querySelector(
    'button[data-fire="~edit"], button.edit-button'
  ) as HTMLButtonElement;
  const isEditing =
    target.classList.contains('editing') ||
    target.classList.contains('editing-bulk');

  if (!isEditing && editBtn) {
    editBtn.click();
    await sleepMs(600);
    target = findSection();
    if (!target) return 'Target section not found after edit';
  }

  // Get data rows (exclude header) - table-body/bulk-body for post-edit structure
  const tableBody =
    target.querySelector('.table-body') || target.querySelector('.bulk-body');
  const rows = (
    tableBody
      ? tableBody.querySelectorAll('.table-row')
      : target.querySelectorAll('.table-body .table-row, .table-row')
  ) as NodeListOf<HTMLElement>;
  if (!rows.length) return 'No rows found';

  let filled = 0;
  for (const row of Array.from(rows)) {
    const name =
      row.querySelector('[data-header="Name"]')?.textContent?.trim() || '';
    const rowNorm = norm(name);
    const matchKey = Object.keys(columnAnswers).find(
      (k) => norm(k) === rowNorm || rowNorm.includes(norm(k))
    );
    if (!matchKey) continue;

    const rowData = columnAnswers[matchKey];
    for (const [colLabel, val] of Object.entries(rowData)) {
      if (isEmptyOrNull(val)) continue;
      const colNorm = norm(colLabel);
      let el: HTMLInputElement | HTMLTextAreaElement | null = null;
      if (colNorm === 'reps') {
        el = row.querySelector(
          '[data-header="Reps"] input'
        ) as HTMLInputElement | null;
      } else if (colNorm === 'sets') {
        el = row.querySelector(
          '[data-header="Sets"] input'
        ) as HTMLInputElement | null;
      } else if (colNorm === 'comments') {
        el =
          (row.querySelector(
            '[data-header="Comments"] textarea'
          ) as HTMLTextAreaElement | null) ||
          (row.querySelector(
            '[data-header="Comments"] .expander-input'
          ) as HTMLTextAreaElement | null);
        if (!el) {
          const expander = row.querySelector(
            '[data-header="Comments"] .expander-text, [data-header="Comments"] .expander'
          ) as HTMLElement;
          expander?.click();
          await sleepMs(80);
          el =
            (row.querySelector(
              '[data-header="Comments"] textarea'
            ) as HTMLTextAreaElement | null) ||
            (row.querySelector(
              '[data-header="Comments"] .expander-input'
            ) as HTMLTextAreaElement | null);
        }
      }
      if (el) {
        el.value = String(val);
        el.dispatchEvent(new Event('input', { bubbles: true }));
        el.dispatchEvent(new Event('change', { bubbles: true }));
        filled++;
      }
    }
  }

  const saveBtn = target.querySelector(
    'button[data-fire="~save"]'
  ) as HTMLButtonElement;
  if (saveBtn && saveBtn.offsetParent) {
    saveBtn.click();
    await sleepMs(200);
  }

  return filled > 0 ? null : 'No fields filled';
}

/** Included Goals & Notes (evaluation): Description, Visibility, Notes table. */
async function fillIncludedGoalsTableDirect(
  questionTitle: string,
  sectionTitle: string,
  columnAnswers: Record<string, Record<string, string>>,
  sleepMs: (ms: number) => Promise<unknown>,
  norm: (s: string | null | undefined) => string
): Promise<string | null> {
  await ensureFlowTabSelected(questionTitle);
  await sleepMs(150);

  const findSection = (): HTMLElement | null => {
    const sections = Array.from(
      document.querySelectorAll('.flow-section')
    ) as HTMLElement[];
    return (
      sections.find((sec) => {
        const title =
          sec.querySelector('.flow-section-title')?.textContent || '';
        const t = norm(title);
        if (!sec.offsetParent) return false;
        return (
          t === 'included goals & notes' ||
          t.includes('included goals') ||
          (t.includes('goals') && t.includes('notes'))
        );
      }) || null
    );
  };

  let target = findSection();
  if (!target) {
    for (let i = 0; i < 5; i++) {
      await sleepMs(100);
      target = findSection();
      if (target) break;
    }
  }
  if (!target) return 'Target section not found';

  try {
    target.scrollIntoView({ block: 'center', behavior: 'auto' });
  } catch {}
  await sleepMs(50);

  const editBtn = target.querySelector(
    'button[data-fire="~edit"], button.edit-button'
  ) as HTMLButtonElement;
  const isEditing =
    target.classList.contains('editing') ||
    target.classList.contains('editing-bulk');

  if (!isEditing && editBtn) {
    editBtn.click();
    await sleepMs(600);
    target = findSection();
    if (!target) return 'Target section not found after edit';
  }

  const tableBody =
    target.querySelector('.table-body') || target.querySelector('.bulk-body');
  const rows = (
    tableBody
      ? tableBody.querySelectorAll('.table-row')
      : target.querySelectorAll('.table-body .table-row, .table-row')
  ) as NodeListOf<HTMLElement>;
  if (!rows.length) return 'No rows found';

  const getRowDescription = (row: HTMLElement): string => {
    const descCell = row.querySelector('[data-header="Description"]');
    if (!descCell) return '';
    const dynamicItems = descCell.querySelectorAll('.dynamic-item');
    if (dynamicItems.length >= 2) {
      return (dynamicItems[1] as HTMLElement).textContent?.trim() || '';
    }
    if (dynamicItems.length === 1) {
      return (dynamicItems[0] as HTMLElement).textContent?.trim() || '';
    }
    return descCell.textContent?.trim() || '';
  };

  let filled = 0;
  for (const row of Array.from(rows)) {
    const rowDesc = getRowDescription(row);
    const rowNorm = norm(rowDesc);
    if (!rowNorm) continue;

    const matchKey = Object.keys(columnAnswers).find((k) => {
      const kNorm = norm(k);
      return (
        kNorm === rowNorm || rowNorm.includes(kNorm) || kNorm.includes(rowNorm)
      );
    });
    if (!matchKey) continue;

    const rowData = columnAnswers[matchKey];
    for (const [colLabel, val] of Object.entries(rowData)) {
      if (isEmptyOrNull(val)) continue;
      const colNorm = norm(colLabel);
      let el: HTMLInputElement | HTMLTextAreaElement | null = null;
      if (colNorm === 'description') {
        el =
          (row.querySelector(
            '[data-header="Description"] textarea'
          ) as HTMLTextAreaElement | null) ||
          (row.querySelector(
            '[data-header="Description"] input'
          ) as HTMLInputElement | null);
      } else if (colNorm === 'visibility') {
        const visCell = row.querySelector('[data-header="Visibility"]');
        const dropdown = visCell?.querySelector('.dropdown');
        if (dropdown) {
          const items = dropdown.querySelectorAll('.dropdown-item');
          const matching = Array.from(items).find(
            (item) => norm(item.textContent || '') === norm(val)
          ) as HTMLElement | undefined;
          if (matching) {
            (
              dropdown.querySelector('.dropdown-text') as HTMLInputElement
            )?.focus();
            matching.click();
            filled++;
          }
        } else {
          el = row.querySelector(
            '[data-header="Visibility"] input'
          ) as HTMLInputElement | null;
        }
      } else if (colNorm === 'notes') {
        el =
          (row.querySelector(
            '[data-header="Notes"] textarea'
          ) as HTMLTextAreaElement | null) ||
          (row.querySelector(
            '[data-header="Notes"] .expander-input'
          ) as HTMLTextAreaElement | null);
        if (!el) {
          const expander = row.querySelector(
            '[data-header="Notes"] .expander-text, [data-header="Notes"] .expander'
          ) as HTMLElement;
          expander?.click();
          await sleepMs(80);
          el =
            (row.querySelector(
              '[data-header="Notes"] textarea'
            ) as HTMLTextAreaElement | null) ||
            (row.querySelector(
              '[data-header="Notes"] .expander-input'
            ) as HTMLTextAreaElement | null);
        }
      }
      if (el) {
        el.value = String(val);
        el.dispatchEvent(new Event('input', { bubbles: true }));
        el.dispatchEvent(new Event('change', { bubbles: true }));
        filled++;
      }
    }
  }

  const saveBtn = target.querySelector(
    'button[data-fire="~save"]'
  ) as HTMLButtonElement;
  if (saveBtn && saveBtn.offsetParent) {
    saveBtn.click();
    await sleepMs(200);
  }

  return filled > 0 ? null : 'No fields filled';
}

async function fillNamedTextTableOnce(options: {
  questionTitle: string;
  sectionTitle: string;
  textAnswers?: Record<string, string>;
  scoreAnswers?: Record<string, string>;
  // For Tests & Measures-style grids: per row name, a map of column header -> value
  columnAnswers?: Record<string, Record<string, string>>;
  // Standalone comment fields outside the table structure
  standaloneComments?: Record<string, string>;
}) {
  // Use section-level mutex to prevent concurrent edits of the same section
  const sectionKey = `${options.questionTitle}::${options.sectionTitle}`;
  const sectionMutex = getSectionMutex(sectionKey);

  const result = await sectionMutex.runExclusive(async () => {
    return await fillNamedTextTableOnceInternal(options);
  });

  // Additional delay after mutex release to ensure DOM is completely stable for next operation
  // This prevents race conditions when processing multiple sections or questions
  await new Promise((resolve) => setTimeout(resolve, 100));

  return result;
}

async function fillNamedTextTableOnceInternal(options: {
  questionTitle: string;
  sectionTitle: string;
  textAnswers?: Record<string, string>;
  scoreAnswers?: Record<string, string>;
  // For Tests & Measures-style grids: per row name, a map of column header -> value
  columnAnswers?: Record<string, Record<string, string>>;
  // Standalone comment fields outside the table structure
  standaloneComments?: Record<string, string>;
}) {
  const { questionTitle, sectionTitle } = options;
  const textAnswers: Record<string, string> = options.textAnswers || {};
  const scoreAnswers: Record<string, string> = options.scoreAnswers || {};
  const columnAnswers: Record<
    string,
    Record<string, string>
  > = options.columnAnswers || {};

  // Early return if no answers provided
  if (
    Object.keys(textAnswers).length === 0 &&
    Object.keys(scoreAnswers).length === 0 &&
    Object.keys(columnAnswers).length === 0
  ) {
    return null;
  }

  // Utility functions
  const sleepMs = (ms: number) => new Promise((r) => setTimeout(r, ms));
  const norm = (s: string | null | undefined) =>
    (s || '').replace(/\s+/g, ' ').trim().toLowerCase();

  // Activities tables (Home Activities, Therapy Activities): direct fill - Name, Reps, Sets, Comments
  if (
    Object.keys(columnAnswers).length > 0 &&
    (matchesAny(
      norm(sectionTitle),
      TABLE_FILL_CONFIG.questionTypes.homeActivities
    ) ||
      matchesAny(
        norm(sectionTitle),
        TABLE_FILL_CONFIG.questionTypes.therapyActivities
      ))
  ) {
    const result = await fillActivitiesTableDirect(
      questionTitle,
      sectionTitle,
      columnAnswers,
      sleepMs,
      norm
    );
    return result;
  }

  // Included Goals & Notes (evaluation): Description, Visibility, Notes table
  if (
    Object.keys(columnAnswers).length > 0 &&
    matchesAny(
      norm(sectionTitle),
      TABLE_FILL_CONFIG.questionTypes.includedGoals
    )
  ) {
    const result = await fillIncludedGoalsTableDirect(
      questionTitle,
      sectionTitle,
      columnAnswers,
      sleepMs,
      norm
    );
    return result;
  }

  const setNativeValue = (
    el: HTMLTextAreaElement | HTMLInputElement,
    value: any
  ) => {
    if (!el) return;
    const v = value == null ? '' : String(value);
    const proto =
      el.tagName === 'TEXTAREA'
        ? (HTMLTextAreaElement.prototype as any)
        : (HTMLInputElement.prototype as any);
    const desc = Object.getOwnPropertyDescriptor(proto, 'value');
    desc?.set?.call(el, v);
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const findElementBySelectors = <T extends Element>(
    container: HTMLElement | Document,
    selectors: string[],
    cast: (el: Element) => T = (el) => el as T
  ): T | null => {
    for (const selector of selectors) {
      const element = container.querySelector(selector);
      if (element) return cast(element);
    }
    return null;
  };

  const findElementsBySelectors = <T extends Element>(
    container: HTMLElement | Document,
    selectors: string[],
    cast: (el: Element) => T = (el) => el as T
  ): T[] => {
    for (const selector of selectors) {
      const elements = Array.from(container.querySelectorAll(selector)) as T[];
      if (elements.length > 0) return elements;
    }
    return [];
  };

  const findSaveButton = (section: HTMLElement): HTMLButtonElement | null => {
    const saveBtn = findElementBySelectors(
      section,
      [TABLE_FILL_CONFIG.selectors.saveButton],
      (el) => el as HTMLButtonElement
    );

    if (saveBtn) return saveBtn;

    // Fallback: find by text
    const allButtons = Array.from(
      section.querySelectorAll('button')
    ) as HTMLButtonElement[];
    return (
      allButtons.find((b) => {
        const text = norm(b.textContent || '');
        return (
          b.offsetParent !== null &&
          matchesAny(text, TABLE_FILL_CONFIG.buttonText.save)
        );
      }) || null
    );
  };

  const findRows = (section: HTMLElement): HTMLElement[] => {
    return findElementsBySelectors(section, TABLE_FILL_CONFIG.selectors.rows);
  };

  const findTextareas = (section: HTMLElement): HTMLTextAreaElement[] => {
    return findElementsBySelectors(
      section,
      TABLE_FILL_CONFIG.selectors.textareas,
      (el) => el as HTMLTextAreaElement
    );
  };

  const findSectionByTitle = (
    title: string,
    selector: string = TABLE_FILL_CONFIG.selectors.sections
  ): HTMLElement | null => {
    const sections = Array.from(
      document.querySelectorAll(selector)
    ) as HTMLElement[];
    return (
      sections.find((sec) => {
        const secTitle =
          sec.querySelector(TABLE_FILL_CONFIG.selectors.sectionTitle)
            ?.textContent || '';
        return sec.offsetParent !== null && norm(secTitle) === norm(title);
      }) || null
    );
  };

  const findEditButtonInSection = (
    section: HTMLElement
  ): HTMLButtonElement | null => {
    return findElementBySelectors(
      section,
      [TABLE_FILL_CONFIG.selectors.editButton],
      (el) => el as HTMLButtonElement
    );
  };

  const findEditButtonByText = (
    buttons: HTMLButtonElement[],
    textMatcher: (text: string) => boolean
  ): HTMLButtonElement | null => {
    return (
      buttons.find((b) => {
        const text = norm(b.textContent || '');
        return b.offsetParent !== null && textMatcher(text);
      }) || null
    );
  };

  const clickEditForQuestion = (): boolean => {
    const qNorm = norm(questionTitle);
    const allButtons = Array.from(
      document.querySelectorAll('button')
    ) as HTMLButtonElement[];

    // Strategy 1: Find button containing question title
    let edit = findEditButtonByText(allButtons, (text) => {
      return (
        matchesAny(text, TABLE_FILL_CONFIG.buttonText.edit) &&
        text.includes(qNorm)
      );
    });

    // Strategy 2: Special handling for Goals / Included Goals & Notes
    if (!edit && matchesAny(qNorm, TABLE_FILL_CONFIG.questionTypes.goals)) {
      const goalsSection = findSectionByTitle(
        sectionTitle || 'Included Goals & Notes'
      );
      edit = goalsSection ? findEditButtonInSection(goalsSection) : null;
      if (!edit) {
        edit = findEditButtonByText(allButtons, (text) => {
          const t = norm(text);
          return (
            (t.includes('edit') || t.includes('enter')) &&
            (t.includes('goal') ||
              t.includes('notes') ||
              t.includes('visibility'))
          );
        });
      }
    }

    // Strategy 3: Special handling for Tests & Measures
    if (!edit && matchesAny(qNorm, TABLE_FILL_CONFIG.questionTypes.tests)) {
      const testSection = findSectionByTitle(
        sectionTitle,
        `${TABLE_FILL_CONFIG.selectors.sections}.test`
      );
      edit = testSection ? findEditButtonInSection(testSection) : null;

      if (!edit) {
        edit = findEditButtonByText(allButtons, (text) =>
          matchesAny(text, TABLE_FILL_CONFIG.buttonText.enterTestData)
        );
      }
    }

    // Strategy 4: Special handling for Findings
    if (!edit && matchesAny(qNorm, TABLE_FILL_CONFIG.questionTypes.findings)) {
      const findingsSection = findSectionByTitle(sectionTitle);
      edit = findingsSection ? findEditButtonInSection(findingsSection) : null;

      if (!edit) {
        // Fallback: find any edit button in any visible section
        const allSections = Array.from(
          document.querySelectorAll(TABLE_FILL_CONFIG.selectors.sections)
        ) as HTMLElement[];
        for (const sec of allSections) {
          if (!sec.offsetParent) continue;
          const btn = findEditButtonInSection(sec);
          if (btn && btn.offsetParent) {
            edit = btn;
            break;
          }
        }
      }
    }

    // Strategy 5: Special handling for Functional Measures (parent step with child sections)
    if (
      !edit &&
      matchesAny(qNorm, TABLE_FILL_CONFIG.questionTypes.functionalMeasures)
    ) {
      // Functional Measures is a parent step, each child section (like "Feeding", "Household") has its own edit button
      const functionalSection = findSectionByTitle(sectionTitle);
      edit = functionalSection
        ? findEditButtonInSection(functionalSection)
        : null;

      if (!edit) {
        // Fallback: find edit button in any visible section within the Functional Measures step
        const functionalStep = Array.from(
          document.querySelectorAll('.flow-step')
        ).find((step) => {
          const stepTitle =
            step.querySelector('.flow-step-title')?.textContent || '';
          return (
            norm(stepTitle).includes('functional') &&
            norm(stepTitle).includes('measure')
          );
        }) as HTMLElement;

        if (functionalStep) {
          const allSections = Array.from(
            functionalStep.querySelectorAll(
              TABLE_FILL_CONFIG.selectors.sections
            )
          ) as HTMLElement[];
          for (const sec of allSections) {
            if (!sec.offsetParent) continue;
            const secTitle =
              sec.querySelector(TABLE_FILL_CONFIG.selectors.sectionTitle)
                ?.textContent || '';
            // Try to match by section title first
            if (sectionTitle && norm(secTitle) === norm(sectionTitle)) {
              const btn = findEditButtonInSection(sec);
              if (btn && btn.offsetParent) {
                edit = btn;
                break;
              }
            }
          }

          // If still no match, use first available edit button
          if (!edit) {
            for (const sec of allSections) {
              if (!sec.offsetParent) continue;
              const btn = findEditButtonInSection(sec);
              if (btn && btn.offsetParent) {
                edit = btn;
                break;
              }
            }
          }
        }
      }
    }

    // Strategy 6: Assigned Home Activities / Therapy Activities - section has "Edit Activities" button
    const sectionNorm = norm(sectionTitle || '');
    if (
      !edit &&
      (matchesAny(
        sectionNorm,
        TABLE_FILL_CONFIG.questionTypes.homeActivities
      ) ||
        matchesAny(
          sectionNorm,
          TABLE_FILL_CONFIG.questionTypes.therapyActivities
        ))
    ) {
      const activitiesSection = findSectionByTitle(sectionTitle);
      edit = activitiesSection
        ? findEditButtonInSection(activitiesSection)
        : null;
      if (!edit) {
        edit = findEditButtonByText(
          allButtons,
          (text) => text.includes('edit') && text.includes('activities')
        );
      }
    }

    if (edit) {
      edit.click();
      return true;
    }
    return false;
  };

  const isSectionInEditMode = (section: HTMLElement): boolean => {
    // Check if section is currently in edit mode (has save button visible)
    const saveBtn = section.querySelector(
      TABLE_FILL_CONFIG.selectors.saveButton
    ) as HTMLElement;
    return !!(saveBtn && saveBtn.offsetParent !== null);
  };

  const findTargetSection = (checkEditMode = false): HTMLElement | null => {
    const sections = Array.from(
      document.querySelectorAll(TABLE_FILL_CONFIG.selectors.sections)
    ) as HTMLElement[];
    let found = sections.find((sec) => {
      const title =
        sec.querySelector(TABLE_FILL_CONFIG.selectors.sectionTitle)
          ?.textContent || '';
      const isVisible = sec.offsetParent !== null;
      let titleMatches =
        norm(title) === norm(sectionTitle) ||
        norm(title) === norm(questionTitle);

      // Assigned Home Activities: section title changes to "Home Activities" after edit click.
      // Must NOT match "Take-Home Activities" - use exact "home activities" or "assigned home activities".
      if (
        !titleMatches &&
        matchesAny(
          norm(sectionTitle),
          TABLE_FILL_CONFIG.questionTypes.homeActivities
        )
      ) {
        const t = norm(title);
        titleMatches =
          t === 'assigned home activities' || t === 'home activities';
      }

      // Therapy Activities / Exercises
      if (
        !titleMatches &&
        matchesAny(
          norm(sectionTitle),
          TABLE_FILL_CONFIG.questionTypes.therapyActivities
        )
      ) {
        const t = norm(title);
        titleMatches =
          t === 'therapy activities' ||
          t === 'therapy activities / exercises' ||
          (t.includes('therapy') && t.includes('activities'));
      }

      // Included Goals & Notes (evaluation)
      if (
        !titleMatches &&
        matchesAny(
          norm(sectionTitle),
          TABLE_FILL_CONFIG.questionTypes.includedGoals
        )
      ) {
        const t = norm(title);
        titleMatches =
          t === 'included goals & notes' ||
          t.includes('included goals') ||
          (t.includes('goals') && t.includes('notes'));
      }

      return isVisible && titleMatches;
    });

    // Fallback: Activities tables - find section with Reps/Sets/Comments table
    if (
      !found &&
      (matchesAny(
        norm(sectionTitle),
        TABLE_FILL_CONFIG.questionTypes.homeActivities
      ) ||
        matchesAny(
          norm(sectionTitle),
          TABLE_FILL_CONFIG.questionTypes.therapyActivities
        ))
    ) {
      const sectionNormForFallback = norm(sectionTitle);
      const isHome = matchesAny(
        sectionNormForFallback,
        TABLE_FILL_CONFIG.questionTypes.homeActivities
      );
      const isTherapy = matchesAny(
        sectionNormForFallback,
        TABLE_FILL_CONFIG.questionTypes.therapyActivities
      );
      found =
        sections.find((sec) => {
          if (!sec.offsetParent) return false;
          const hasActivitiesTable =
            sec.querySelector('[data-header="Reps"]') &&
            sec.querySelector('[data-header="Sets"]') &&
            sec.querySelector('[data-header="Comments"]');
          const isEditing =
            sec.classList.contains('editing') ||
            sec.classList.contains('editing-bulk');
          if (!hasActivitiesTable || !(isEditing || !checkEditMode))
            return false;
          const t = norm(
            sec.querySelector('.flow-section-title')?.textContent || ''
          );
          if (isHome)
            return t === 'assigned home activities' || t === 'home activities';
          if (isTherapy)
            return (
              t === 'therapy activities' ||
              t === 'therapy activities / exercises' ||
              (t.includes('therapy') && t.includes('activities'))
            );
          return true;
        }) || null;
    }

    return found || null;
  };

  // Helper functions for row processing
  const getRowIdentifier = (row: HTMLElement, rowIndex: number): string => {
    // Try each header name in priority order
    for (const headerName of TABLE_FILL_CONFIG.rowIdentifierHeaders) {
      const cell = row.querySelector(
        `.table-cell[data-header="${headerName}"]`
      ) as HTMLElement;
      if (cell) {
        // Try to get text from .uf-field-value first (single value, not concatenated)
        const fieldValue = cell.querySelector('.uf-field-value') as HTMLElement;
        if (fieldValue) {
          const name = fieldValue.textContent?.trim();
          // Only use if it's a reasonable single value (not concatenated multiple values)
          // Check if it looks like a single value (not too long, doesn't contain multiple words from different fields)
          if (name && name.length < 100 && !name.match(/[A-Z][a-z]+[A-Z]/)) {
            return name;
          }
        }
        // Fallback to cell text, but check for concatenation
        const cellText = cell.textContent?.trim();
        if (
          cellText &&
          cellText.length < 100 &&
          !cellText.match(/[A-Z][a-z]+[A-Z]/)
        ) {
          return cellText;
        }
      }
    }

    // Try variant-primary as fallback
    const primaryCell = row.querySelector(
      '.table-cell.variant-primary'
    ) as HTMLElement;
    if (primaryCell) {
      // Try to get text from .uf-field-value first
      const fieldValue = primaryCell.querySelector(
        '.uf-field-value'
      ) as HTMLElement;
      if (fieldValue) {
        const primaryName = fieldValue.textContent?.trim();
        if (
          primaryName &&
          primaryName.length < 100 &&
          !primaryName.match(/[A-Z][a-z]+[A-Z]/)
        ) {
          return primaryName;
        }
      }
      const primaryText = primaryCell.textContent?.trim();
      if (
        primaryText &&
        primaryText.length < 100 &&
        !primaryText.match(/[A-Z][a-z]+[A-Z]/)
      ) {
        return primaryText;
      }
    }

    // For tables without explicit row names (like Functional Measures tables),
    // use row index as identifier since answer keys use "Row 1", "Row 2", etc.
    // This is the most reliable approach for these types of tables
    return `Row ${rowIndex + 1}`;
  };

  const findTextareaCell = (row: HTMLElement): HTMLElement | null => {
    // Try each textarea header in priority order
    for (const headerName of TABLE_FILL_CONFIG.textareaHeaders) {
      const cell = row.querySelector(
        `.table-cell.editor-cell[data-header="${headerName}"]`
      ) as HTMLElement;
      if (cell) return cell;

      // Also try without editor-cell class
      const cellAlt = row.querySelector(
        `.table-cell[data-header="${headerName}"]`
      ) as HTMLElement;
      if (cellAlt) return cellAlt;
    }

    // Find cell containing textarea field
    const allCells = Array.from(
      row.querySelectorAll(TABLE_FILL_CONFIG.selectors.tableCells)
    ) as HTMLElement[];
    const textareaCell = allCells.find(
      (cell) =>
        cell.querySelector(TABLE_FILL_CONFIG.selectors.textareaField) !== null
    );
    if (textareaCell) return textareaCell;

    // Final fallback: try generic cell selectors
    return findElementBySelectors(
      row,
      TABLE_FILL_CONFIG.selectors.textareaCells
    );
  };

  const findTextarea = (container: HTMLElement): HTMLTextAreaElement | null => {
    return findElementBySelectors(
      container,
      TABLE_FILL_CONFIG.selectors.textareas,
      (el) => el as HTMLTextAreaElement
    );
  };

  const findInputField = (
    container: HTMLElement
  ): HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null => {
    // Try standard input fields first
    const standardInput = findElementBySelectors(
      container,
      TABLE_FILL_CONFIG.selectors.inputFields,
      (el) => el as HTMLInputElement | HTMLTextAreaElement
    );
    if (standardInput) return standardInput;

    // Try dropdown fields (select elements)
    const select = container.querySelector('select') as HTMLSelectElement;
    if (select && select.offsetParent !== null) return select;

    // Try finding input/textarea within form-field containers
    const formField = container.querySelector('.form-field, .uf-field');
    if (formField) {
      const input = formField.querySelector('input, textarea, select') as
        | HTMLInputElement
        | HTMLTextAreaElement
        | HTMLSelectElement;
      if (input && input.offsetParent !== null) return input;
    }

    // Try finding in userform fields
    const userformField = container.querySelector(
      '.userform-dropdown-field, .userform-text-field, .userform-text-area-field'
    );
    if (userformField) {
      const input = userformField.querySelector('input, textarea, select') as
        | HTMLInputElement
        | HTMLTextAreaElement
        | HTMLSelectElement;
      if (input && input.offsetParent !== null) return input;
    }

    // Try finding any input/textarea/select in the container
    const anyInput = container.querySelector(
      'input:not(.hidden), textarea:not(.hidden), select:not(.hidden)'
    ) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
    if (anyInput && anyInput.offsetParent !== null) return anyInput;

    return null;
  };

  const fillField = async (
    element: HTMLTextAreaElement | HTMLInputElement,
    value: string,
    delay: number = TABLE_FILL_CONFIG.delays.fieldFill
  ): Promise<void> => {
    // Scroll without blocking (non-critical)
    try {
      element.scrollIntoView({ block: 'center', behavior: 'instant' });
    } catch {}

    // Only wait if delay is specified and > 0
    if (delay > 0) {
      await sleepMs(delay);
    }

    element.focus();
    setNativeValue(element, value);
    element.blur();
  };

  // Inline-edit tables often don't render inputs until a cell is activated.
  // To prevent flaky "missed" fills (Patient History, Outcome Measures),
  // we activate the target cell before attempting to write.
  const fillFromActiveElementInScope = (
    scopeEl: HTMLElement,
    value: string
  ): boolean => {
    const ae = document.activeElement as HTMLElement | null;
    if (!ae) return false;
    if (!scopeEl.contains(ae)) return false;

    if (ae.tagName === 'TEXTAREA') {
      setNativeValue(ae as HTMLTextAreaElement, value);
      return true;
    }

    if (ae.tagName === 'INPUT') {
      const input = ae as HTMLInputElement;
      if (input.type === 'text' || input.type === 'number') {
        setNativeValue(input, value);
        return true;
      }
    }

    if (
      (ae as any).isContentEditable ||
      ae.getAttribute('contenteditable') === 'true'
    ) {
      ae.textContent = value;
      ae.dispatchEvent(new Event('input', { bubbles: true }));
      ae.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    return false;
  };

  const activateCell = async (cell: HTMLElement): Promise<void> => {
    try {
      cell.scrollIntoView({ block: 'center', behavior: 'instant' as any });
    } catch {}
    try {
      cell.click();
    } catch {}
    await sleepMs(60);
    try {
      cell.dispatchEvent(new MouseEvent('dblclick', { bubbles: true }));
    } catch {}
    await sleepMs(120);
  };

  const tryFillCell = async (
    cell: HTMLElement,
    rawVal: any
  ): Promise<boolean> => {
    const value = String(rawVal ?? '');
    if (isEmptyOrNull(value)) return false;

    let inputEl = findInputField(cell);
    if (!inputEl) {
      await activateCell(cell);
      inputEl = findInputField(cell);
    }

    // Expander cells (e.g. Comments in Assigned Home Activities): textarea may be hidden until expander is clicked
    if (!inputEl) {
      const expander = cell.querySelector('.expander') as HTMLElement;
      const expanderText = cell.querySelector('.expander-text') as HTMLElement;
      if (expander || expanderText) {
        (expanderText || expander)?.click();
        await sleepMs(80);
        inputEl = findInputField(cell);
        if (!inputEl) {
          const ta = cell.querySelector(
            'textarea.expander-input, textarea.textbox'
          ) as HTMLTextAreaElement;
          if (ta) inputEl = ta;
        }
      }
    }

    if (inputEl) {
      if (inputEl.tagName === 'SELECT') {
        const select = inputEl as HTMLSelectElement;
        const option = Array.from(select.options).find(
          (opt) => norm(opt.text || opt.value) === norm(value)
        );
        if (!option) return false;
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }

      await fillField(
        inputEl as HTMLInputElement | HTMLTextAreaElement,
        value,
        TABLE_FILL_CONFIG.delays.fieldFillShort
      );
      return true;
    }

    if (fillFromActiveElementInScope(cell, value)) {
      return true;
    }

    const ta = findTextarea(cell);
    if (ta) {
      await fillField(ta, value, TABLE_FILL_CONFIG.delays.fieldFillShort);
      return true;
    }

    return false;
  };

  // Step 1: Find target section (may already be in edit mode - that's OK, we'll use it)
  let targetSection = findTargetSection(false); // Don't skip sections in edit mode
  if (!targetSection) {
    // Wait for section to appear
    for (let i = 0; i < TABLE_FILL_CONFIG.delays.maxWaitIterations; i++) {
      await sleepMs(i < 3 ? 10 : TABLE_FILL_CONFIG.delays.sectionWait);
      targetSection = findTargetSection(false);
      if (targetSection) break;
    }
  }

  if (!targetSection) {
    return 'Target section not found';
  }

  // Step 2: Click Edit button (only if not already in edit mode)
  const isAlreadyInEditMode = isSectionInEditMode(targetSection);
  if (!isAlreadyInEditMode) {
    if (!clickEditForQuestion()) {
      return 'Edit button not found';
    }

    // Wait for section to enter edit mode
    let enteredEditMode = false;
    for (let i = 0; i < TABLE_FILL_CONFIG.delays.maxWaitIterations; i++) {
      await sleepMs(i < 3 ? 10 : TABLE_FILL_CONFIG.delays.sectionWait);
      // Re-query section to avoid stale references
      targetSection = findTargetSection(false);
      if (targetSection && isSectionInEditMode(targetSection)) {
        enteredEditMode = true;
        break;
      }
    }

    if (!enteredEditMode) {
      // Give it a bit more time
      await sleepMs(200);
      targetSection = findTargetSection(false);
      if (!targetSection || !isSectionInEditMode(targetSection)) {
        return 'Section failed to enter edit mode';
      }
    }
  }

  // Step 3: Wait for section to be ready (optimized with immediate check and exponential backoff)
  let saveBtn: HTMLButtonElement | null = null;
  let isReady = false;

  // Check immediately first (no delay)
  targetSection = findTargetSection();
  if (targetSection) {
    saveBtn = findSaveButton(targetSection);
    const rows = findRows(targetSection);
    const textareas = findTextareas(targetSection);
    isReady = !!(saveBtn && (rows.length > 0 || textareas.length > 0));
  }

  // If not ready, use exponential backoff: check quickly first, then slow down
  if (!isReady) {
    for (let i = 0; i < TABLE_FILL_CONFIG.delays.maxWaitIterations; i++) {
      targetSection = findTargetSection();
      if (targetSection) {
        saveBtn = findSaveButton(targetSection);
        const rows = findRows(targetSection);
        const textareas = findTextareas(targetSection);

        if (saveBtn && (rows.length > 0 || textareas.length > 0)) {
          isReady = true;
          break;
        }
      }

      // Exponential backoff: 10ms for first 3 checks, then 50ms, then 100ms
      const waitTime =
        i < 3
          ? 10
          : i < 10
            ? TABLE_FILL_CONFIG.delays.sectionWait
            : TABLE_FILL_CONFIG.delays.sectionWait * 2;
      await sleepMs(waitTime);
    }
  }

  // Re-find section to ensure we have the latest DOM reference (avoid stale references)
  targetSection = findTargetSection();
  if (!targetSection) {
    return 'Target section not found';
  }

  // Verify section is actually in edit mode before proceeding
  if (!isSectionInEditMode(targetSection)) {
    await sleepMs(100);
    targetSection = findTargetSection();
    if (!targetSection || !isSectionInEditMode(targetSection)) {
      return 'Section failed to enter edit mode';
    }
  }

  saveBtn = findSaveButton(targetSection);
  if (!saveBtn) {
    return 'Save button not found';
  }

  const rows = findRows(targetSection);

  // Step 3: Fill rows (or simple textarea fields if no rows)
  const isTestsQuestion = matchesAny(
    norm(questionTitle),
    TABLE_FILL_CONFIG.questionTypes.tests
  );
  let filled = 0;

  // Handle simple textarea fields (no table rows, just direct textarea fields like "Feeding" with "Notes / Observations")
  if (
    rows.length === 0 &&
    Object.keys(textAnswers).length > 0 &&
    Object.keys(columnAnswers).length === 0
  ) {
    for (const [fieldKey, fieldValue] of Object.entries(textAnswers)) {
      if (isEmptyOrNull(fieldValue)) continue;

      // Find textarea by label text
      const labels = Array.from(
        targetSection.querySelectorAll('.uf-field-label, .form-field-label')
      ) as HTMLElement[];

      const matchingLabel = labels.find((label) => {
        const labelText = label.textContent?.trim() || '';
        const labelNorm = labelText.toLowerCase().trim();
        const keyNorm = fieldKey.toLowerCase().trim();
        // Match if label contains the key or vice versa
        return (
          labelNorm === keyNorm ||
          labelNorm.includes(keyNorm) ||
          keyNorm.includes(labelNorm) ||
          (labelNorm.includes('notes') && keyNorm.includes('notes')) ||
          (labelNorm.includes('observations') &&
            keyNorm.includes('observations'))
        );
      });

      if (matchingLabel) {
        // Find the textarea in the same form-field container
        const formField = matchingLabel.closest(
          '.form-field, .uf-field'
        ) as HTMLElement;
        if (formField) {
          const textarea = formField.querySelector(
            'textarea'
          ) as HTMLTextAreaElement;
          if (textarea) {
            await fillField(
              textarea,
              fieldValue,
              TABLE_FILL_CONFIG.delays.fieldFill
            );
            filled++;
            await sleepMs(10);
          }
        }
      }
    }
  }

  // Only process rows if we have rows (skip if we already handled simple textarea fields)
  if (rows.length > 0) {
    for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
      // Re-query rows to avoid stale DOM references (race condition protection)
      const currentRowsList = findRows(targetSection);
      if (rowIndex >= currentRowsList.length) {
        continue;
      }
      const row = currentRowsList[rowIndex];
      const rowIdentifier = getRowIdentifier(row, rowIndex);
      const rowNorm = norm(rowIdentifier);

      // Find matching answer keys - try exact match first, then partial match
      let textKey = Object.keys(textAnswers).find((k) => norm(k) === rowNorm);
      let scoreKey = Object.keys(scoreAnswers).find((k) => norm(k) === rowNorm);
      let columnKey = Object.keys(columnAnswers).find(
        (k) => norm(k) === rowNorm
      );

      // If no exact match, try partial matching (e.g., "Row 1" matches "Row 1 - Comments")
      if (!textKey && !scoreKey && !columnKey) {
        // Try to find keys that start with the row identifier
        const matchingTextKey = Object.keys(textAnswers).find((k) =>
          norm(k).startsWith(rowNorm)
        );
        const matchingScoreKey = Object.keys(scoreAnswers).find((k) =>
          norm(k).startsWith(rowNorm)
        );
        const matchingColumnKey = Object.keys(columnAnswers).find((k) =>
          norm(k).startsWith(rowNorm)
        );

        if (matchingTextKey) {
          textKey = matchingTextKey;
        }
        if (matchingScoreKey) {
          scoreKey = matchingScoreKey;
        }
        if (matchingColumnKey) {
          columnKey = matchingColumnKey;
        }
      }

      const rowColumnAnswers = columnKey ? columnAnswers[columnKey] : undefined;

      if (!textKey && !scoreKey && !rowColumnAnswers) {
        continue;
      }

      // Fill column fields FIRST (for all question types, not just Tests & Measures)
      // This ensures we fill all columns properly, including Findings sections
      if (rowColumnAnswers) {
        const cells = Array.from(
          row.querySelectorAll(TABLE_FILL_CONFIG.selectors.tableCells)
        ) as HTMLElement[];

        for (const [colLabel, rawVal] of Object.entries(rowColumnAnswers)) {
          if (isEmptyOrNull(rawVal)) continue;

          const targetCell = cells.find((cell) => {
            const headerAttr = cell.getAttribute('data-header') || '';
            const headerText =
              (
                cell.querySelector(
                  TABLE_FILL_CONFIG.selectors.headerContent
                ) as HTMLElement
              )?.textContent?.trim() || headerAttr.trim();
            // Normalize both for comparison (handle whitespace variations)
            const headerNorm = norm(headerText);
            const colNorm = norm(colLabel.trim());
            const matches = headerNorm !== '' && headerNorm === colNorm;
            return matches;
          });

          if (!targetCell) {
            continue;
          }

          const ok = await tryFillCell(targetCell, rawVal);
          if (ok) {
            filled++;
            await sleepMs(10);
          }
        }
      }

      // Fill textarea field (only if not already filled via columnAnswers)
      if (textKey && !rowColumnAnswers) {
        const value = textAnswers[textKey];
        const textareaCell = findTextareaCell(row);
        const textarea =
          textareaCell && findTextarea(textareaCell)
            ? (findTextarea(textareaCell) as HTMLTextAreaElement)
            : findTextarea(row);

        let wrote = false;
        if (textarea) {
          await fillField(textarea, value);
          wrote = true;
        } else if (textareaCell) {
          wrote = await tryFillCell(textareaCell, value);
        }

        if (wrote) {
          const preview = (textareaCell || row).querySelector(
            TABLE_FILL_CONFIG.selectors.expanderPreview
          ) as HTMLElement;
          if (preview) preview.textContent = String(value);
          filled++;
        }
      }

      // Fill score field
      if (scoreKey) {
        const scoreValue = scoreAnswers[scoreKey];
        const scoreCell = row.querySelector(
          `.table-cell.editor-cell[data-header="${TABLE_FILL_CONFIG.scoreHeader}"]`
        ) as HTMLElement;
        let wroteScore = false;
        if (scoreCell) {
          wroteScore = await tryFillCell(scoreCell, scoreValue);
        } else {
          const any = findInputField(row) as HTMLInputElement | null;
          if (any) {
            await fillField(
              any,
              scoreValue,
              TABLE_FILL_CONFIG.delays.fieldFillShort
            );
            wroteScore = true;
          }
        }

        if (wroteScore) {
          filled++;
        }
      }
    }
  } // End of row processing (only if rows.length > 0)

  // Step 4: Fill standalone comment fields (outside table structure)
  const { standaloneComments = {} } = options;
  if (Object.keys(standaloneComments).length > 0) {
    for (const [commentKey, commentValue] of Object.entries(
      standaloneComments
    )) {
      if (isEmptyOrNull(commentValue)) continue;

      // Find comment field by label text
      const commentFields = Array.from(
        targetSection.querySelectorAll('.uf-field-label, .form-field-label')
      ) as HTMLElement[];

      const commentField = commentFields.find((label) => {
        const labelText = label.textContent?.trim() || '';
        const labelNorm = labelText.toLowerCase().trim();
        const keyNorm = commentKey.toLowerCase().trim();
        // Match if label contains the key or vice versa, or both contain "comment"
        return (
          labelNorm.includes(keyNorm) ||
          keyNorm.includes(labelNorm) ||
          (labelNorm.includes('comment') && keyNorm.includes('comment'))
        );
      });

      if (commentField) {
        // Find the textarea in the same form-field container
        const formField = commentField.closest(
          '.form-field, .uf-field'
        ) as HTMLElement;
        if (formField) {
          const textarea = formField.querySelector(
            'textarea'
          ) as HTMLTextAreaElement;
          if (textarea) {
            const commentValueStr = String(commentValue);
            await fillField(
              textarea,
              commentValueStr,
              TABLE_FILL_CONFIG.delays.fieldFill
            );
            filled++;
            await sleepMs(10);
          }
        }
      }
    }
  }

  // Step 5: Save changes and wait for completion (with race condition protection)
  // Re-query save button and check if we're in edit mode (avoid stale references)
  const currentSaveBtn = findSaveButton(targetSection);
  const isInEditMode = targetSection
    ? isSectionInEditMode(targetSection)
    : false;

  // Save if: we filled something OR we're in edit mode (need to exit edit mode)
  if ((filled > 0 || isInEditMode) && currentSaveBtn) {
    // Verify save button is still visible and clickable before clicking
    if (currentSaveBtn.offsetParent === null) {
      // Try to find it again
      const retrySaveBtn = findSaveButton(targetSection);
      if (retrySaveBtn && retrySaveBtn.offsetParent !== null) {
        retrySaveBtn.click();
      } else {
        return 'Save button not clickable';
      }
    } else {
      currentSaveBtn.click();
    }

    // Wait for save to complete - check if save button disappears or section closes
    const saveButtonSelector = TABLE_FILL_CONFIG.selectors.saveButton;
    const maxSaveWait = 2000; // Max 2 seconds for save
    const checkInterval = 50;
    let saveCompleted = false;

    for (let i = 0; i < maxSaveWait / checkInterval; i++) {
      await sleepMs(checkInterval);

      // Re-find section to avoid stale references
      const currentSection = findTargetSection();
      if (!currentSection) {
        saveCompleted = true;
        break;
      }

      // Check if save button is gone (save completed)
      const stillInEditMode = isSectionInEditMode(currentSection);
      if (!stillInEditMode) {
        saveCompleted = true;
        break;
      }

      // Also check if section is no longer visible in edit mode
      const editButton = currentSection.querySelector(
        TABLE_FILL_CONFIG.selectors.editButton
      ) as HTMLElement;
      if (
        editButton &&
        editButton.offsetParent !== null &&
        !isSectionInEditMode(currentSection)
      ) {
        // Section returned to normal view
        saveCompleted = true;
        break;
      }
    }

    // Additional delay to ensure DOM is fully stable for next question (race condition protection)
    await sleepMs(TABLE_FILL_CONFIG.delays.saveWait);

    // Final verification: ensure section is no longer in edit mode (with retries)
    let finalSection = findTargetSection();
    let retryCount = 0;
    const maxRetries = 10;

    while (
      finalSection &&
      isSectionInEditMode(finalSection) &&
      retryCount < maxRetries
    ) {
      await sleepMs(200);
      finalSection = findTargetSection();
      retryCount++;
    }

    // Extra safety delay to ensure DOM is completely stable
    await sleepMs(100);
  } else {
    // If we're in edit mode but have no save button, that's an error
    if (isInEditMode && !currentSaveBtn) {
      return 'In edit mode but save button not found';
    }
  }

  return null;
}

export async function answerQuestions(answers) {
  var errors = [];

  // Begin overlay (ref-counted) for the duration of answering
  beginOverlay('Answering questions…', { title: 'ComprehendPT' });
  try {
    // Process questions sequentially (not in parallel) to avoid DOM race conditions.
    // When multiple answers run concurrently, one save can collapse a section while
    // another is still filling, causing some questions to be left unfilled.
    for (const answer of answers) {
      const replyType = answer['ReplyType'] || 'overwrite';
      var error = await answerQuestion(
        answer['Question'],
        answer['Answer'],
        replyType
      );
      if (error) {
        errors.push({
          Question: answer['Question'],
          Answer: answer['Answer'],
          Error: error
        });
      }
      // Delay between questions so Fusion's DOM can settle after save
      await sleep(300);
    }

    console.error('Errors:', errors);
    return errors;
  } finally {
    endOverlay();
  }
}

// Simple async mutex to serialize question answering operations reliably
class AsyncMutex {
  private chain: Promise<void>;

  constructor() {
    this.chain = Promise.resolve();
  }

  async runExclusive<T>(task: () => Promise<T> | T): Promise<T> {
    let release!: () => void;
    const willLock: Promise<void> = new Promise<void>((resolve) => {
      release = resolve;
    });
    const previous = this.chain;
    this.chain = previous.then(() => willLock);
    await previous;
    try {
      return await task();
    } finally {
      // @ts-ignore release is always assigned above
      release();
    }
  }
}

const questionMutex = new AsyncMutex();

/** Click the flow tab for this question so its section is visible before filling. */
async function ensureFlowTabSelected(questionTitle: string): Promise<void> {
  const norm = (s: string) =>
    (s || '').replace(/\s+/g, ' ').trim().toLowerCase();
  const qNorm = norm(questionTitle);
  const tabs = Array.from(
    document.querySelectorAll('.flow-control .flow-tab')
  ) as HTMLElement[];
  const tab = tabs.find((t) => {
    const text = t.textContent?.trim() || '';
    return (
      norm(text) === qNorm ||
      norm(text).includes(qNorm) ||
      qNorm.includes(norm(text))
    );
  });
  if (tab && tab.offsetParent !== null) {
    const isActive =
      tab.classList.contains('active') ||
      tab.classList.contains('selected') ||
      tab.getAttribute('aria-selected') === 'true';
    if (!isActive) {
      tab.click();
      await sleep(250);
    }
  }
}

// Section-level mutex to prevent concurrent edits of the same section
const sectionMutexes = new Map<string, AsyncMutex>();

const getSectionMutex = (sectionKey: string): AsyncMutex => {
  if (!sectionMutexes.has(sectionKey)) {
    sectionMutexes.set(sectionKey, new AsyncMutex());
  }
  return sectionMutexes.get(sectionKey)!;
};

export async function answerQuestion(
  question,
  answer,
  replyType = 'overwrite',
  totalWaitTime = 0
) {
  if (!questionInteractions[question]) {
    console.error(`Question not found: "${question}"`);
    return 'Question not found';
  }
  // function answerQuestion(question, answer) {
  /*
      Description: This function will take the answer and populate the question
      
      Args: 
          question: The question in question
          answer: The answer to the question
     
      Return: null if success otherwise a string with the error message
      */

  // console.log("Answering Question: '" + question + "' with '" + answer+"'")

  // Make sure the question exists
  if (questionInteractions[question] == null) {
    console.error('Question not found');
    console.log(question);
    // console.log(questionInteractions)
    // console.log(Object.keys(questionInteractions))

    return 'Question not found';
  }

  // Begin overlay ref-count and queue with this specific question
  beginOverlay('Answering questions…', { title: 'ComprehendPT' });
  pushQuestionToQueue(String(question));

  // Delegate flex questions to the flex handler (it manages its own mutex)
  if (questionInteractions[question]['Type'] === 'Flex') {
    try {
      await answerFlexQuestion(question, answer, replyType);
      return true;
    } finally {
      removeQuestionFromQueue(String(question));
      endOverlay();
    }
  }

  return questionMutex.runExclusive(async () => {
    // Ensure the flow tab for this question is selected so the section is visible
    await ensureFlowTabSelected(String(question));
    await sleep(100);

    // If it is a short answer or long answer, just populate the input
    if (questionInteractions[question]['Input']) {
      try {
        await questionInteractions[question]['Input'](answer, replyType);
        return true;
      } finally {
        removeQuestionFromQueue(String(question));
        endOverlay();
      }
    }

    // Check if the answer is a list of answers or just a single answer
    if (Array.isArray(answer)) {
      // Choose All
      var notFoundAnswers = [];
      for (var i = 0; i < answer.length; i++) {
        if (answer[i] in questionInteractions[question]['Choices']) {
          console.log('Answer found ' + answer[i]);
          await questionInteractions[question]['Choices'][answer[i]](true);
        } else {
          notFoundAnswers.push(answer[i]);
        }
      }
      try {
        if (notFoundAnswers.length > 0) {
          console.error(
            'The following answers were not found: ' + notFoundAnswers
          );
          return 'The following answers were not found: ' + notFoundAnswers;
        } else {
          return true;
        }
      } finally {
        removeQuestionFromQueue(String(question));
        endOverlay();
      }
    } else {
      // Multiple Choice
      try {
        if (answer in questionInteractions[question]['Choices']) {
          await questionInteractions[question]['Choices'][answer]();
          return true;
        } else {
          console.error('Answer not found');
          return 'Answer not found';
        }
      } finally {
        removeQuestionFromQueue(String(question));
        endOverlay();
      }
    }
  });
}

async function answerFlexQuestion(
  question,
  answer,
  replyType = 'overwrite',
  totalWaitTime = 0
) {
  return questionMutex.runExclusive(async () => {
    try {
      // Ensure the flow tab for this question is selected so the section is visible
      await ensureFlowTabSelected(String(question));
      await sleep(100);

      const errors = [];

      // Special-case handler for the "Tests & Measures" flex question.
      // The structure for this section is very dynamic, so we drive it
      // entirely from the provided answer object and the live DOM.
      if (question === 'Tests & Measures') {
        try {
          const testsAnswer =
            (answer as any)?.answer ?? (answer as any)?.Answer ?? answer;
          if (!testsAnswer || typeof testsAnswer !== 'object') {
            return null;
          }

          const unwrapVal = (v: any): string => {
            if (v == null) return '';
            if (typeof v === 'string' || typeof v === 'number')
              return String(v);
            if (typeof v === 'object' && !Array.isArray(v) && !('Input' in v)) {
              const o = v as Record<string, unknown>;
              const x =
                o['Existing Value'] ??
                o['ExistingValue'] ??
                o['Answer'] ??
                o['answer'];
              if (
                x !== undefined &&
                (typeof x === 'string' || typeof x === 'number')
              )
                return String(x);
            }
            return String(v);
          };

          const processTestTable = async (
            testName: string,
            testValue: Record<string, any>
          ) => {
            const perRowColumns: Record<string, Record<string, string>> = {};

            for (const [subKey, rawVal] of Object.entries(testValue)) {
              const val = unwrapVal(rawVal);
              if (isEmptyOrNull(val)) continue;
              if (typeof subKey !== 'string' || !subKey.includes(' - '))
                continue;
              const [rowName, colLabel] = subKey.split(' - ');
              if (!rowName || !colLabel) continue;
              if (!perRowColumns[rowName]) {
                perRowColumns[rowName] = {};
              }
              perRowColumns[rowName][colLabel] = val;
            }

            if (Object.keys(perRowColumns).length === 0) return;

            await fillNamedTextTableOnce({
              questionTitle: question,
              sectionTitle: testName,
              columnAnswers: perRowColumns
            });
          };

          for (const [topKey, topVal] of Object.entries(
            testsAnswer as Record<string, any>
          )) {
            if (!topVal || typeof topVal !== 'object') continue;

            const hasRowColKeys = Object.keys(topVal).some(
              (k) => typeof k === 'string' && k.includes(' - ')
            );

            if (hasRowColKeys) {
              await processTestTable(topKey, topVal);
            } else {
              for (const [testName, testValue] of Object.entries(
                topVal as Record<string, any>
              )) {
                if (!testValue || typeof testValue !== 'object') continue;
                const innerHasRowCol = Object.keys(testValue).some(
                  (k) => typeof k === 'string' && k.includes(' - ')
                );
                if (innerHasRowCol) {
                  await processTestTable(testName, testValue);
                }
              }
            }
          }
        } catch (e) {
          console.error('[FusionInject] Tests & Measures handler error:', e);
        }

        return null;
      }

      // Special-case: Therapy Activities / Exercises (same table pattern as Home Activities)
      const flexPayloadForTA =
        (answer as any)?.answer ?? (answer as any)?.Answer ?? answer;
      const therapyActivitiesData =
        flexPayloadForTA &&
        typeof flexPayloadForTA === 'object' &&
        ((flexPayloadForTA as Record<string, unknown>)[
          'Therapy Activities / Exercises'
        ] ??
          (flexPayloadForTA as Record<string, unknown>)['Therapy Activities']);
      if (therapyActivitiesData && typeof therapyActivitiesData === 'object') {
        const taObj = therapyActivitiesData as Record<string, unknown>;
        const hasActivityKeys = Object.keys(taObj).some(
          (k) => typeof k === 'string' && k.includes(' - ')
        );
        if (hasActivityKeys) {
          try {
            const columnAnswersTA: Record<string, Record<string, string>> = {};
            for (const [subKey, rawVal] of Object.entries(taObj)) {
              const val =
                rawVal == null
                  ? ''
                  : typeof rawVal === 'string' || typeof rawVal === 'number'
                    ? String(rawVal)
                    : '';
              if (!val) continue;
              if (typeof subKey === 'string' && subKey.includes(' - ')) {
                const [namePart, colLabel] = subKey.split(' - ');
                if (namePart && colLabel) {
                  if (!columnAnswersTA[namePart])
                    columnAnswersTA[namePart] = {};
                  columnAnswersTA[namePart][colLabel] = val;
                }
              }
            }
            if (Object.keys(columnAnswersTA).length > 0) {
              await fillNamedTextTableOnce({
                questionTitle: String(question),
                sectionTitle: 'Therapy Activities / Exercises',
                columnAnswers: columnAnswersTA
              });
            }
          } catch (e) {
            console.error(
              '[FusionInject] Therapy Activities handler error:',
              e
            );
          }
        }
      }

      // Special-case: Included Goals & Notes (evaluation - Description, Visibility, Notes table)
      const flexPayloadForGoals =
        (answer as any)?.answer ?? (answer as any)?.Answer ?? answer;
      const includedGoalsData =
        flexPayloadForGoals &&
        typeof flexPayloadForGoals === 'object' &&
        (flexPayloadForGoals as Record<string, unknown>)[
          'Included Goals & Notes'
        ];
      if (includedGoalsData && typeof includedGoalsData === 'object') {
        const igObj = includedGoalsData as Record<string, unknown>;
        const hasGoalKeys = Object.keys(igObj).some(
          (k) => typeof k === 'string' && k.includes(' - ')
        );
        if (hasGoalKeys) {
          try {
            const columnAnswersIG: Record<string, Record<string, string>> = {};
            for (const [subKey, rawVal] of Object.entries(igObj)) {
              const val =
                rawVal == null
                  ? ''
                  : typeof rawVal === 'string' || typeof rawVal === 'number'
                    ? String(rawVal)
                    : '';
              if (!val) continue;
              if (typeof subKey === 'string' && subKey.includes(' - ')) {
                const [namePart, colLabel] = subKey.split(' - ');
                if (namePart && colLabel) {
                  if (!columnAnswersIG[namePart])
                    columnAnswersIG[namePart] = {};
                  columnAnswersIG[namePart][colLabel] = val;
                }
              }
            }
            if (Object.keys(columnAnswersIG).length > 0) {
              await fillNamedTextTableOnce({
                questionTitle: String(question),
                sectionTitle: 'Included Goals & Notes',
                columnAnswers: columnAnswersIG
              });
            }
          } catch (e) {
            console.error(
              '[FusionInject] Included Goals & Notes handler error:',
              e
            );
          }
        }
      }

      const questionObject = questionInteractions[question];
      const structure = questionObject?.Structure || {};

      // Special-case: Goal / Objective Data can be answered either:
      //  - nested by category: { Balance: { "Goal - Value": "50" } }
      //  - flat: { "Goal - Value": "50" }
      // The generic flex handler expects the nested form, so normalize here.
      if (String(question) === 'Goal / Objective Data (for this session)') {
        const ansObj = answer as any;
        const payload =
          ansObj && typeof ansObj === 'object'
            ? ansObj.answer ?? ansObj.Answer ?? ansObj
            : ansObj;
        if (!payload || typeof payload !== 'object') {
          return null;
        }

        const categoryKeys = Object.keys(structure || {});
        const topLevelKeys = Object.keys(payload);
        const looksNested =
          topLevelKeys.length > 0 &&
          topLevelKeys.some((k) => categoryKeys.includes(k));

        const applyLeaf = async (
          catKey: string,
          leafKey: string,
          leafVal: any
        ) => {
          const sub = structure?.[catKey]?.[leafKey];
          if (!sub?.Input) {
            errors.push({
              Field: `${catKey}.${leafKey}`,
              Error: 'Input function not found'
            });
            return;
          }

          const values =
            typeof leafVal === 'string' ? leafVal.split('$') : [leafVal];
          for (const v of values) {
            if (isEmptyOrNull(v)) continue;
            try {
              await sub.Input(v, replyType);
            } catch (error) {
              errors.push({
                Field: `${catKey}.${leafKey}`,
                Error: `Error applying value: ${error.message}`
              });
            }
          }
        };

        if (looksNested) {
          for (const [catKey, catVal] of Object.entries(payload)) {
            if (!structure?.[catKey]) continue;
            if (!catVal || typeof catVal !== 'object') continue;
            for (const [leafKey, leafVal] of Object.entries(catVal as any)) {
              await applyLeaf(String(catKey), String(leafKey), leafVal);
              await sleep(30);
            }
          }
        } else {
          for (const [leafKey, leafVal] of Object.entries(payload)) {
            const foundCat = categoryKeys.find(
              (cat) => structure?.[cat]?.[leafKey]?.Input
            );
            if (!foundCat) {
              console.warn(
                `⚠️ Objectives leaf "${leafKey}" not found under any category.`
              );
              errors.push({
                Field: String(leafKey),
                Error: 'Leaf not found in any Objectives category'
              });
              continue;
            }
            await applyLeaf(foundCat, String(leafKey), leafVal);
            await sleep(30);
          }
        }

        // Close the section after filling
        await closeGoalObjectiveDataSection();

        return errors.length > 0 ? errors : null;
      }

      // Unwrap answer if backend sends { answer: {...} } or { Answer: {...} }
      const flexPayload =
        (answer as any)?.answer ?? (answer as any)?.Answer ?? answer;

      for (const [fieldKey, fieldValue] of Object.entries(
        flexPayload && typeof flexPayload === 'object' ? flexPayload : {}
      )) {
        const field = structure[fieldKey];

        // Handle fields not in structure when we have dedicated fill handlers
        if (!field) {
          const fieldKeyNorm = String(fieldKey).toLowerCase();

          // Assigned Home Activities / Therapy Activities / Included Goals & Notes: fill table even when not in scraped structure
          if (
            (matchesAny(
              fieldKeyNorm,
              TABLE_FILL_CONFIG.questionTypes.homeActivities
            ) ||
              matchesAny(
                fieldKeyNorm,
                TABLE_FILL_CONFIG.questionTypes.therapyActivities
              ) ||
              matchesAny(
                fieldKeyNorm,
                TABLE_FILL_CONFIG.questionTypes.includedGoals
              )) &&
            fieldValue &&
            typeof fieldValue === 'object' &&
            !Array.isArray(fieldValue) &&
            !('Input' in (fieldValue as object))
          ) {
            const groupObj = fieldValue as Record<string, unknown>;
            const columnAnswersForActivities: Record<
              string,
              Record<string, string>
            > = {};
            for (const [subKey, val] of Object.entries(groupObj)) {
              if (isEmptyOrNull(val)) continue;
              if (typeof subKey === 'string' && subKey.includes(' - ')) {
                const [namePart, colLabel] = subKey.split(' - ');
                if (namePart && colLabel) {
                  if (!columnAnswersForActivities[namePart])
                    columnAnswersForActivities[namePart] = {};
                  columnAnswersForActivities[namePart][colLabel] = String(val);
                }
              } else if (
                typeof subKey === 'string' &&
                val &&
                typeof val === 'object' &&
                !Array.isArray(val) &&
                !('Input' in (val as object))
              ) {
                for (const [col, v] of Object.entries(
                  val as Record<string, unknown>
                )) {
                  if (isEmptyOrNull(v)) continue;
                  if (!columnAnswersForActivities[subKey])
                    columnAnswersForActivities[subKey] = {};
                  columnAnswersForActivities[subKey][col] = String(v);
                }
              }
            }
            if (Object.keys(columnAnswersForActivities).length > 0) {
              let sectionTitleForFill = 'Assigned Home Activities';
              if (
                matchesAny(
                  fieldKeyNorm,
                  TABLE_FILL_CONFIG.questionTypes.therapyActivities
                )
              ) {
                sectionTitleForFill = 'Therapy Activities / Exercises';
              } else if (
                matchesAny(
                  fieldKeyNorm,
                  TABLE_FILL_CONFIG.questionTypes.includedGoals
                )
              ) {
                sectionTitleForFill = 'Included Goals & Notes';
              }
              try {
                await fillNamedTextTableOnce({
                  questionTitle: question,
                  sectionTitle: sectionTitleForFill,
                  columnAnswers: columnAnswersForActivities
                });
                await sleep(150);
              } catch (e) {
                console.error(
                  `[FusionInject] ${sectionTitleForFill} fill error:`,
                  e
                );
                errors.push({
                  Field: fieldKey,
                  Error: `Failed to fill: ${(e as Error).message}`
                });
              }
            }
            continue;
          }

          // Plan of Treatment: fill section textarea when not in structure
          if (fieldKeyNorm.includes('plan of treatment')) {
            const obj = fieldValue as Record<string, unknown>;
            const val =
              typeof fieldValue === 'string'
                ? fieldValue
                : String(
                    obj?.['Existing Value'] ??
                      obj?.['ExistingValue'] ??
                      obj?.['Answer'] ??
                      obj?.['answer'] ??
                      ''
                  );
            if (!isEmptyOrNull(val)) {
              try {
                await fillSectionTextareaByTitle('Plan of Treatment', val);
                await sleep(100);
              } catch (e) {
                console.error(
                  '[FusionInject] Plan of Treatment fill error:',
                  e
                );
                errors.push({
                  Field: fieldKey,
                  Error: `Failed to fill: ${(e as Error).message}`
                });
              }
            }
            continue;
          }

          // No fallback handler for this field

          console.warn(`⚠️ Field "${fieldKey}" not found in structure.`);
          errors.push({
            Field: fieldKey,
            Error: 'Field not found in Structure'
          });
          continue;
        }

        // Backend sometimes wraps values as { "Existing Value": "x" }, { "Answer": "x" },
        // or { "FieldName": "value" } (key matches field). Unwrap so we treat them as flat values.
        let unwrappedValue = fieldValue;
        if (
          fieldValue &&
          typeof fieldValue === 'object' &&
          !Array.isArray(fieldValue) &&
          !('Input' in fieldValue)
        ) {
          const obj = fieldValue as Record<string, unknown>;
          const v =
            obj['Existing Value'] ??
            obj['ExistingValue'] ??
            obj['Answer'] ??
            obj['answer'] ??
            obj[fieldKey]; // e.g. { "Intervention Comments": "value" }
          if (
            v !== undefined &&
            (typeof v === 'string' || typeof v === 'number')
          ) {
            unwrappedValue = v;
          }
        }

        // Detect if this field is a group (nested) or single (flat)
        const isGroup =
          typeof unwrappedValue === 'object' && !('Input' in unwrappedValue);

        if (isGroup) {
          // Try to detect a "named text table" group (keys like "Medical History - Text" or "Berg Balance - Findings")
          const groupObj = unwrappedValue as any;

          // Dedicated handling for Assigned Home Activities / Therapy Activities / Included Goals & Notes
          if (
            matchesAny(
              String(fieldKey).toLowerCase(),
              TABLE_FILL_CONFIG.questionTypes.homeActivities
            ) ||
            matchesAny(
              String(fieldKey).toLowerCase(),
              TABLE_FILL_CONFIG.questionTypes.therapyActivities
            ) ||
            matchesAny(
              String(fieldKey).toLowerCase(),
              TABLE_FILL_CONFIG.questionTypes.includedGoals
            )
          ) {
            const columnAnswersForActivities: Record<
              string,
              Record<string, string>
            > = {};
            for (const [subKey, val] of Object.entries(groupObj)) {
              if (isEmptyOrNull(val)) continue;
              if (typeof subKey === 'string' && subKey.includes(' - ')) {
                const [namePart, colLabel] = subKey.split(' - ');
                if (namePart && colLabel) {
                  if (!columnAnswersForActivities[namePart])
                    columnAnswersForActivities[namePart] = {};
                  columnAnswersForActivities[namePart][colLabel] = String(val);
                }
              } else if (
                typeof subKey === 'string' &&
                val &&
                typeof val === 'object' &&
                !Array.isArray(val) &&
                !('Input' in (val as object))
              ) {
                // Nested format: { "Catching a Ball": { "Reps": "x", "Sets": "y", "Comments": "z" } }
                for (const [col, v] of Object.entries(
                  val as Record<string, unknown>
                )) {
                  if (isEmptyOrNull(v)) continue;
                  if (!columnAnswersForActivities[subKey])
                    columnAnswersForActivities[subKey] = {};
                  columnAnswersForActivities[subKey][col] = String(v);
                }
              }
            }
            if (Object.keys(columnAnswersForActivities).length > 0) {
              let sectionTitleForFill = 'Assigned Home Activities';
              if (
                matchesAny(
                  String(fieldKey).toLowerCase(),
                  TABLE_FILL_CONFIG.questionTypes.therapyActivities
                )
              ) {
                sectionTitleForFill = 'Therapy Activities / Exercises';
              } else if (
                matchesAny(
                  String(fieldKey).toLowerCase(),
                  TABLE_FILL_CONFIG.questionTypes.includedGoals
                )
              ) {
                sectionTitleForFill = 'Included Goals & Notes';
              }
              try {
                await fillNamedTextTableOnce({
                  questionTitle: question,
                  sectionTitle: sectionTitleForFill,
                  columnAnswers: columnAnswersForActivities
                });
                await sleep(150);
              } catch (e) {
                console.error(
                  `[FusionInject] ${sectionTitleForFill} fill error:`,
                  e
                );
                errors.push({
                  Field: fieldKey,
                  Error: `Failed to fill: ${(e as Error).message}`
                });
              }
            }
            continue;
          }

          const textAnswers: Record<string, string> = {};
          const scoreAnswers: Record<string, string> = {};
          const columnAnswers: Record<string, Record<string, string>> = {};
          let looksLikeNamedTextTable = false;

          // Track standalone comment fields (outside table structure)
          const standaloneComments: Record<string, string> = {};

          for (const [subFieldKey, value] of Object.entries(groupObj)) {
            if (isEmptyOrNull(value)) continue;
            if (
              typeof subFieldKey === 'string' &&
              subFieldKey.includes(' - ')
            ) {
              const [namePart, suffix] = subFieldKey.split(' - ');
              if (suffix) {
                const sufNorm = suffix.toLowerCase();
                // Any "Name - Suffix" pattern indicates a named table structure
                looksLikeNamedTextTable = true;

                // Track all suffix values per row for generic Tests & Measures grids
                if (!columnAnswers[namePart]) {
                  columnAnswers[namePart] = {};
                }
                columnAnswers[namePart][suffix] = String(value);

                // Treat "... - Text", "... - Findings", "... - Notes", and "... - Comments" as text-area columns
                if (
                  sufNorm.includes('text') ||
                  sufNorm.includes('finding') ||
                  sufNorm.includes('notes') ||
                  sufNorm.includes('comment')
                ) {
                  textAnswers[namePart] = String(value);
                } else if (sufNorm.includes('score')) {
                  // Keep scores alongside, so we can fill both columns in one pass
                  scoreAnswers[namePart] = String(value);
                }
              }
            } else if (typeof subFieldKey === 'string') {
              // Check if this is a standalone comment field (not part of table structure)
              const keyNorm = subFieldKey.toLowerCase().trim();
              if (
                keyNorm.includes('comment') ||
                keyNorm.includes('additional comment') ||
                keyNorm === 'comments' ||
                keyNorm === 'comment'
              ) {
                standaloneComments[subFieldKey] = String(value);
              }
            }
          }

          // If this looks like a named text table, first fill all text/findings cells in one pass
          // Also handle simple textarea fields (like "Feeding" with just "Notes / Observations")
          const hasTableStructure =
            looksLikeNamedTextTable &&
            (Object.keys(textAnswers).length > 0 ||
              Object.keys(scoreAnswers).length > 0 ||
              Object.keys(columnAnswers).length > 0);

          // Check if it's a simple textarea field (no " - " pattern, just direct key-value pairs)
          const isSimpleTextarea =
            !looksLikeNamedTextTable &&
            Object.keys(groupObj).length > 0 &&
            Object.values(groupObj).some(
              (v) => typeof v === 'string' && v.length > 0
            );

          // For Findings flex, Additional Comments are handled by direct Input
          // handlers in handleFindingsSection, not by fillNamedTextTableOnce.
          // If all sub-keys are "*Additional Comments", skip the table filler
          // and let the nested Input calls run instead.
          let shouldUseFillNamed = hasTableStructure || isSimpleTextarea;
          const qNorm = String(question).toLowerCase();
          if (matchesAny(qNorm, TABLE_FILL_CONFIG.questionTypes.findings)) {
            const allKeys = Object.keys(groupObj);
            const allAreAdditionalComments =
              allKeys.length > 0 &&
              allKeys.every(
                (k) =>
                  typeof k === 'string' &&
                  k.toLowerCase().includes('additional comments')
              );
            if (allAreAdditionalComments) {
              shouldUseFillNamed = false;
            }
          }

          // For Functional Measures, we also have comment/notes-only groups
          // (e.g. "Feeding - Notes / Observations", "Household - Comments").
          // Those should be handled by the direct Input handlers created in
          // handleFunctionalMeasuresSection, not by fillNamedTextTableOnce.
          if (
            matchesAny(
              qNorm,
              TABLE_FILL_CONFIG.questionTypes.functionalMeasures
            )
          ) {
            const allKeys = Object.keys(groupObj);
            const allAreFmComments =
              allKeys.length > 0 &&
              allKeys.every(
                (k) =>
                  typeof k === 'string' &&
                  (k.toLowerCase().includes('comments') ||
                    k.toLowerCase().includes('notes / observations') ||
                    k.toLowerCase().includes('notes/observations'))
              );
            if (allAreFmComments) {
              shouldUseFillNamed = false;
            }
          }

          // History Items: Fusion re-renders the section on Edit; fillNamedTextTableOnce
          // uses stale DOM refs and fails on 2nd pass. Use the placeholder Input handlers
          // instead (which re-find section/row/textarea each time).
          const fieldKeyNorm = String(fieldKey).toLowerCase();
          if (
            fieldKeyNorm.includes('history items') ||
            qNorm.includes('patient history')
          ) {
            shouldUseFillNamed = false;
          }

          if (shouldUseFillNamed) {
            // For Findings-style sections where we *do* use the table filler,
            // ensure sectionTitle matches the actual flow-section title
            // (strip " - Additional Comments" suffix when present).
            let sectionTitleForFill = String(fieldKey);
            if (
              matchesAny(qNorm, TABLE_FILL_CONFIG.questionTypes.findings) &&
              typeof fieldKey === 'string' &&
              fieldKey.toLowerCase().includes('additional comments')
            ) {
              const base = fieldKey.split(' - ')[0];
              if (base && base.trim().length > 0) {
                sectionTitleForFill = base.trim();
              }
            }

            await fillNamedTextTableOnce({
              questionTitle: question,
              sectionTitle: sectionTitleForFill,
              textAnswers: isSimpleTextarea
                ? (groupObj as Record<string, string>)
                : textAnswers,
              scoreAnswers,
              columnAnswers
            });

            // Longer delay to ensure DOM is completely stable after save before processing next field
            // This prevents race conditions when processing multiple sections
            await sleep(150);
          }

          // Always run the nested structure logic as well so that non-text fields
          // in the same group (e.g. "... - Score") still get filled via their Input.
          // For text/findings subfields, we skip calling Input again to avoid double-writing.
          for (const [subFieldKey, value] of Object.entries(fieldValue)) {
            const sub = field[subFieldKey];

            if (!sub?.Input) {
              errors.push({
                Field: `${fieldKey}.${subFieldKey}`,
                Error: 'Input function not found'
              });
              continue;
            }

            // If we already handled this subfield via fillNamedTextTableOnce, skip it here.
            // Only skip when we actually used the table filler (otherwise we'd skip Input for History Items).
            if (
              shouldUseFillNamed &&
              looksLikeNamedTextTable &&
              typeof subFieldKey === 'string' &&
              subFieldKey.includes(' - ')
            ) {
              const [, suffix] = subFieldKey.split(' - ');
              const sufNorm = (suffix || '').toLowerCase();
              if (sufNorm.includes('text') || sufNorm.includes('finding')) {
                continue;
              }
            }

            // Unwrap nested value objects like { "Existing Value": "x" } or { [subFieldKey]: "x" }
            let unwrappedSubVal = value;
            if (
              value &&
              typeof value === 'object' &&
              !Array.isArray(value) &&
              !('Input' in value)
            ) {
              const obj = value as Record<string, unknown>;
              const v =
                obj['Existing Value'] ??
                obj['ExistingValue'] ??
                obj['Answer'] ??
                obj['answer'] ??
                obj[subFieldKey];
              if (
                v !== undefined &&
                (typeof v === 'string' || typeof v === 'number')
              ) {
                unwrappedSubVal = v;
              }
            }
            const values =
              typeof unwrappedSubVal === 'string'
                ? unwrappedSubVal.split('$')
                : [unwrappedSubVal];
            for (const val of values) {
              try {
                if (/(Functional Goal).*(PLOF|CLOF|Goal)/.test(subFieldKey)) {
                  await sleep(200);
                }
                await sub.Input(val, replyType);
                if (String(fieldKey).toLowerCase().includes('history items')) {
                  await sleep(200);
                }
              } catch (error) {
                errors.push({
                  Field: `${fieldKey}.${subFieldKey}`,
                  Error: `Error applying value: ${(error as Error).message}`
                });
              }
            }
          }
          // History Items: click Save after all rows filled so Fusion persists
          if (String(fieldKey).toLowerCase().includes('history items')) {
            const sections = Array.from(
              document.querySelectorAll('.flow-section')
            ) as HTMLElement[];
            const hiSection = sections.find((sec) => {
              const title =
                sec.querySelector('.flow-section-title')?.textContent || '';
              return (
                sec.offsetParent &&
                title.toLowerCase().includes('history items')
              );
            });
            const saveBtn = hiSection?.querySelector(
              'button[data-fire="~save"]'
            ) as HTMLButtonElement | null;
            if (saveBtn && saveBtn.offsetParent) {
              saveBtn.click();
              await sleep(300);
            }
          }
        } else {
          // Handle flat structure: e.g. Flowsheet["Procedure"] = "Ball squeeze exercise"
          // Also handle nested structure like { "Intervention Comments": { "Intervention Comments": { Input } } }
          let inputFn = field?.Input;
          if (
            !inputFn &&
            field &&
            fieldValue &&
            typeof fieldValue === 'object' &&
            fieldKey in field
          ) {
            const nested = field[fieldKey];
            if (nested?.Input) inputFn = nested.Input;
          }
          if (!inputFn) {
            errors.push({
              Field: fieldKey,
              Error: 'Input function not found'
            });
            continue;
          }

          const values =
            typeof unwrappedValue === 'string'
              ? unwrappedValue.split('$')
              : [unwrappedValue];
          for (const val of values) {
            try {
              await inputFn(val, replyType);
            } catch (error) {
              errors.push({
                Field: fieldKey,
                Error: `Error applying value: ${error.message}`
              });
            }
          }
        }
      }

      return errors.length > 0 ? errors : null;
    } catch (error) {
      console.error('Unexpected error:', error);
      return {
        Question: question,
        Error: `Unexpected error: ${error.message}`
      };
    }
  });
}

export async function getLogFromInjector() {
  try {
    const htmlContent = document.documentElement.outerHTML;
    chrome.runtime.sendMessage({
      type: 'INJECTOR_ERROR',
      error: { htmlContent: htmlContent }
    });
    return { htmlContent: htmlContent };
  } catch (error) {
    // Return the full HTML content of the document
    console.error('Error occurred while fetching document:', error);
    getLogFromInjector();
  }
}

/* Start the Blur Overlay */
const OVERLAY_ID = 'BlurOverlay';
const ACTIVE_CLASS = 'blur-overlay-active';
const CSS_ID = 'BlurOverlayStyles';

let overlayEl, msgEl, closeBtn;
let usingBackdrop = true;
let lastFocused = null;

function injectStyles() {
  if (document.getElementById(CSS_ID)) return;
  const style = document.createElement('style');
  style.id = CSS_ID;
  style.textContent = `
    /* Root toggle classes */
    html.${ACTIVE_CLASS} { overflow: hidden; }
    body.${ACTIVE_CLASS} { overflow: hidden; }

    /* Fallback blur for browsers without backdrop-filter */
    html.${ACTIVE_CLASS} body > *:not(#${OVERLAY_ID}) {
      filter: blur(var(--blur-overlay-amount, 8px)) saturate(0.9);
      pointer-events: none;
      user-select: none;
    }

    /* Overlay container */
    #${OVERLAY_ID} {
      position: fixed;
      inset: 0;
      /* Brand color (tealish green) */
      --brand-color: rgb(107, 216, 136);
      z-index: 2147483647;
      display: none;
      align-items: center;
      justify-content: center;
      padding: 24px;
    }

    /* When visible */
    #${OVERLAY_ID}[data-open="true"] { display: flex; }

    /* Backdrop blur (preferred) */
    #${OVERLAY_ID}::before {
      content: "";
      position: absolute;
      inset: 0;
      background: rgba(0,0,0,0.25);
      backdrop-filter: blur(var(--blur-overlay-amount, 8px)) brightness(0.95);
      -webkit-backdrop-filter: blur(var(--blur-overlay-amount, 8px)) brightness(0.95);
    }

    /* Message card */
    #${OVERLAY_ID} .blur-msg {
      position: relative;
      max-width: min(720px, 90vw);
      width: max(320px, 40vw);
      background: rgba(255,255,255,0.9);
      border-radius: 16px;
      box-shadow: 0 10px 30px rgba(0,0,0,0.2);
      padding: 20px 24px;
      font-family: system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif;
      color: #111;
      backdrop-filter: blur(2px);
    }

    #${OVERLAY_ID} .blur-msg h2 {
      margin: 0 0 10px 0;
      font-size: 18px;
      font-weight: 600;
      color: var(--brand-color);
    }

    #${OVERLAY_ID} .blur-msg .blur-text {
      line-height: 1.5;
      font-size: 15px;
      white-space: pre-wrap;
    }

    /* Close (X) in top-right of viewport */
    #${OVERLAY_ID} .blur-close {
      position: fixed;
      top: 12px;
      right: 12px;
      appearance: none;
      border: none;
      background: var(--brand-color);
      color: #fff;
      width: 36px;
      height: 36px;
      border-radius: 10px;
      font-size: 20px;
      line-height: 36px;
      text-align: center;
      cursor: pointer;
      box-shadow: 0 4px 12px rgba(0,0,0,0.25);
    }
    #${OVERLAY_ID} .blur-close:hover { background: #00937f; }
    #${OVERLAY_ID} .blur-close:focus { outline: 2px solid #fff; outline-offset: 2px; }
  `;
  document.head.appendChild(style);
}

function createOverlay() {
  if (document.getElementById(OVERLAY_ID)) {
    overlayEl = document.getElementById(OVERLAY_ID);
    msgEl = overlayEl.querySelector('.blur-text');
    closeBtn = overlayEl.querySelector('.blur-close');
    return;
  }
  overlayEl = document.createElement('div');
  overlayEl.id = OVERLAY_ID;
  overlayEl.setAttribute('aria-hidden', 'true');

  const card = document.createElement('div');
  card.className = 'blur-msg';
  card.setAttribute('role', 'dialog');
  card.setAttribute('aria-modal', 'true');
  card.setAttribute('aria-live', 'polite');

  const title = document.createElement('h2');
  title.textContent = 'Notice';

  msgEl = document.createElement('div');
  msgEl.className = 'blur-text';
  msgEl.textContent = '';

  closeBtn = document.createElement('button');
  closeBtn.className = 'blur-close';
  closeBtn.setAttribute('type', 'button');
  closeBtn.setAttribute('aria-label', 'Close overlay');
  closeBtn.textContent = '×';

  card.appendChild(title);
  card.appendChild(msgEl);

  overlayEl.appendChild(card);
  overlayEl.appendChild(closeBtn);
  document.body.appendChild(overlayEl);

  closeBtn.addEventListener('click', hide);
  document.addEventListener('keydown', (e) => {
    if (overlayEl.dataset.open === 'true' && e.key === 'Escape') hide();
  });

  // If user clicks the dim area (not the card), do nothing (X/ESC only)
  overlayEl.addEventListener('click', (e) => {
    const clickedInsideCard = e.target === card || card.contains(e.target);
    const clickedClose = e.target === closeBtn;
    if (!clickedInsideCard && !clickedClose) {
      // no-op; keep modal open unless using X/ESC
    }
  });
}

function ensureSupport() {
  usingBackdrop =
    CSS.supports('backdrop-filter: blur(8px)') ||
    CSS.supports('-webkit-backdrop-filter: blur(8px)');
}

type BlurOverlayOptions = {
  title?: string;
  blurAmount?: number;
  textColor?: string;
  background?: string;
  width?: string; // e.g. '600px' or '50vw'
};

function show(message = '', opts: BlurOverlayOptions = {}) {
  injectStyles();
  createOverlay();
  ensureSupport();

  // Options
  const {
    title = 'Notice',
    blurAmount = 8,
    textColor,
    background,
    width // e.g. '600px' or '50vw'
  } = opts;

  // Apply styles via CSS vars and inline as needed
  overlayEl.style.setProperty('--blur-overlay-amount', String(blurAmount));
  const card = overlayEl.querySelector('.blur-msg');
  if (textColor) card.style.color = textColor;
  if (background) card.style.background = background;
  if (width) card.style.width = width;

  overlayEl.querySelector('h2').textContent = title;
  msgEl.textContent = message ?? '';

  // Toggle classes depending on support
  document.documentElement.classList.add(ACTIVE_CLASS);
  document.body.classList.add(ACTIVE_CLASS);

  if (!usingBackdrop) {
    // Fallback: blur page content via CSS filter on everything but the overlay
    // Already handled by the CSS rule when ACTIVE_CLASS is present.
  }

  // Control visibility of close button (always visible with current UX)
  closeBtn.style.display = '';

  // Open
  overlayEl.dataset.open = 'true';
  overlayEl.setAttribute('aria-hidden', 'false');

  // Focus management
  lastFocused =
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
  closeBtn.focus();

  return true;
}

function update(message = '') {
  if (!overlayEl) createOverlay();
  msgEl.textContent = message ?? '';
  return true;
}

function hide() {
  if (!overlayEl) return false;

  overlayEl.dataset.open = 'false';
  overlayEl.setAttribute('aria-hidden', 'true');
  document.documentElement.classList.remove(ACTIVE_CLASS);
  document.body.classList.remove(ACTIVE_CLASS);

  // Restore focus
  if (lastFocused && document.contains(lastFocused)) {
    try {
      lastFocused.focus();
    } catch {}
  }
  lastFocused = null;
  return true;
}

function isVisible() {
  return !!(overlayEl && overlayEl.dataset.open === 'true');
}

// // Public API -> Not used globally, only used in this file...
// window.BlurOverlay = { show, update, hide, isVisible };
/* End the Blur Overlay */

/* Overlay ref-count helpers to avoid cross-call hide races */
let __overlayRefCount = 0;

function beginOverlay(message, opts) {
  __overlayRefCount++;
  if (!isVisible()) {
    show(message, opts);
  } else {
    update(message);
  }
  return __overlayRefCount;
}

function endOverlay() {
  __overlayRefCount = Math.max(0, __overlayRefCount - 1);
  if (__overlayRefCount === 0 && isVisible()) {
    hide();
  }
}

// Queue tracking for per-question answering overlay messaging
let __overlayAnswerQueue = [];
function formatAnswerQueueMessage() {
  const total = __overlayAnswerQueue.length;
  const header = 'Finishing your note… This should only take a few seconds.';
  if (total === 0) return header;

  const current = __overlayAnswerQueue[0];
  const nextItems = __overlayAnswerQueue.slice(1);
  const nextPreview = nextItems.slice(0, 3);
  const remaining = Math.max(0, nextItems.length - nextPreview.length);

  let message = header + `\nCurrently working on: ${current}`;
  if (nextPreview.length > 0) {
    const list = nextPreview.join(', ');
    message +=
      `\nUp next: ${list}` + (remaining > 0 ? `, and ${remaining} more` : '');
  }
  return message;
}
function pushQuestionToQueue(questionLabel) {
  if (!questionLabel) return;
  if (!__overlayAnswerQueue.includes(questionLabel)) {
    __overlayAnswerQueue.push(questionLabel);
  }
  if (isVisible()) update(formatAnswerQueueMessage());
}
function removeQuestionFromQueue(questionLabel) {
  const idx = __overlayAnswerQueue.indexOf(questionLabel);
  if (idx !== -1) __overlayAnswerQueue.splice(idx, 1);
  if (isVisible()) update(formatAnswerQueueMessage());
}
